import React, { useState, useEffect, useCallback, useRef } from 'react'
import { signOut, getCurrentUser } from '../lib/supabase'
import SharePurchaseFlow from './SharePurchaseFlow'
import BirthdayPackageButtons from './BirthdayPackageButtons'
import { ProductionCapitalCountdown } from './ProductionCapitalCountdown'
import type { BirthdayPackageId, ProductionCapitalPackageId } from '../constants'
import { extractNumericId } from '../lib/userIdExtractor'
import { missingProfileDetailFields, profileDetailsAreComplete, profileEditAcceptsAllFields, selfServiceProfileEditOpen, splitStoredName, PROFILE_DETAILS_ADMIN_NOTE, PROFILE_DETAILS_MISSING_NOTE } from '../lib/profileDetailsEdit.js'
import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import { DividendProjections2026to2035 } from './dividends/DividendProjections2026to2035'
import { DIVIDEND_PROJECTIONS, CALCULATION_CONSTANTS, computeDynamicAnnualProjection } from '../lib/services/calculationService'
import { useLiveTotalSharesSold } from '../lib/hooks/useLiveTotalSharesSold'
import { createSafeHtml } from '../lib/sanitizeHtml'
import { readPersistedSection, persistSection } from '../lib/kycClientResilience'
import { DIVIDENDS_USER_VISIBLE } from '../lib/dividendsUserVisible'

// Note: Using main supabase client instead of creating additional instances
// to avoid "Multiple GoTrueClient instances" warning
import { NotificationCenter } from './user/NotificationCenter'
import { UpdatesPage } from './user/UpdatesPage'
import { NotificationBadge, NotificationDropdown } from './user/NotificationBadge'
import { ReferralCenter } from './referrals/ReferralCenter'
import { DividendsSection } from './dashboard/sections/DividendsSection'
import MaterialsLibrary from './materials/MaterialsLibrary'

import { ComprehensivePortfolio } from './portfolio/ComprehensivePortfolio'
// EmailPinService REMOVED — replaced by magic link verification (PinVerificationModal now uses magic links internally)
import { SmartKYCRouter } from './user/SmartKYCRouter'
import { useCompleteCountryList } from '../lib/hooks/useCompleteCountryList'
import { SearchableCountrySelector, CountryOption } from './SearchableCountrySelector'

import { DashboardSwitcher } from './DashboardSwitcher'
import TicketingSystem from './support/TicketingSystem'
import ConsultationBooking from './consultation/ConsultationBooking'
import { ProfilePictureUpload } from './ProfilePictureUpload'
import { ProfilePictureUploadEnhanced } from './ProfilePictureUploadEnhanced'
import MySharesPage from './MySharesPage'
import { ImpersonationIndicator } from './admin/ImpersonationIndicator'
import { validateImpersonationSession } from '../lib/adminImpersonation'
import { CommissionWithdrawal } from './user/CommissionWithdrawal'
import { SecurePasswordChangeForm } from './user/SecurePasswordChangeForm'
import { MasterAdminViewWrapper } from './admin/MasterAdminViewBanner'
import { isMasterAdminViewSession, getMasterAdminViewSession } from '../lib/masterAdminAccess'
import { KycRequiredNotice } from './kyc/KycRequiredNotice'
import { TermsAndConditions } from './TermsAndConditions'
import { ComprehensivePrivacyPolicy } from './ComprehensivePrivacyPolicy'
import { ComprehensiveDisclaimer } from './ComprehensiveDisclaimer'
import SharePurchaseAgreement from './SharePurchaseAgreement'
import { LegalDocumentService } from '../lib/legalDocumentService'
import { getFullVersion } from '../lib/version'
import { PinVerificationModal } from './PinVerificationModal'
import { TotpMfaPanel } from './TotpMfaPanel'
import { HowToStartVideos } from './HowToStartVideos'
import { FoundingShareholdersShowcase } from './founding-shareholders/FoundingShareholdersShowcase'
import { getPhoneCodeForCountry, formatPhoneWithCountryCode } from '../lib/data/countryPhoneCodes'
import { VisaCardPurchase } from './user/VisaCardPurchase'
import BeneficiaryManager from './user/BeneficiaryManager'
import { LiveActivityFeed } from './calculator/LiveActivityFeed'
import { CompanyDashboardSection } from './company'
import { TransactionHistory } from './TransactionHistory'
import ShareAuditPage from './ShareAuditPage'
import { GoldDiggersDashboard } from './GoldDiggersDashboard'
import { ZimbabweAwaitsPage } from './ZimbabweAwaitsPage'
import { ProductionVeinAccord } from './ProductionVeinAccord'
import { isGlobalTopAffiliatePoolTester } from '../lib/productionVeinAccord'
import { AcresUserDashboard } from './user/acres/AcresUserDashboard'
import { AcresPromoBanner } from './AcresPromoBanner'
import { AcresPromoModal } from './AcresPromoModal'

// API base URL for server-side endpoints
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || ''
import { PendingPaymentsList } from './user/PendingPaymentsList'
import { RejectedPaymentsList } from './user/RejectedPaymentsList'
import { UnderpaidPaymentsList } from './user/UnderpaidPaymentsList'
import { UserInvoices } from './user/UserInvoices'
import type { ResumePaymentData } from './SharePurchaseFlow'
import { RoadmapJournalDisplay } from './roadmap/RoadmapJournalDisplay'
import { MediaGallery } from './media/MediaGallery'
import { ensureSessionHeaders } from '../lib/serverSession'
import { acceptDisplayedSharePrice } from '../lib/displayedSharePrice'
import LanguageFlagSelector from './LanguageFlagSelector'
import { isSupported, setStoredLanguage } from '../lib/languageResolver'
import { useTranslation } from 'react-i18next'

// Countries where bank transfer is available (primarily African countries with ZAR-based banking)
// Includes both full names and ISO 3166-1 alpha-3 codes for matching
const __BANK_TRANSFER_COUNTRIES = [
  'South Africa', 'ZAF',
  'Namibia', 'NAM',
  'Botswana', 'BWA',
  'Zimbabwe', 'ZWE',
  'Zambia', 'ZMB',
  'Mozambique', 'MOZ',
  'Eswatini', 'SWZ',
  'Lesotho', 'LSO',
  'Malawi', 'MWI'
]

/**
 * CRITICAL: Safely extract numeric user ID from user object
 * Handles various user ID formats including "db_XXX" strings
 * Prevents integer constraint violations in database operations
 */
const extractNumericUserId = (user: any): number => {
  // Helper function to safely parse any ID format to a number
  const parseId = (id: any): number | null => {
    if (!id) return null;

    // If already a number, validate it
    if (typeof id === 'number') {
      return Number.isInteger(id) && id > 0 ? id : null;
    }

    // Convert to string for processing
    const idStr = String(id);

    // Handle "master_assist_XXX" format (admin assistance mode)
    if (idStr.startsWith('master_assist_')) {
      const numericPart = idStr.replace('master_assist_', '');
      const parsed = parseInt(numericPart, 10);
      return !isNaN(parsed) && parsed > 0 ? parsed : null;
    }

    // Handle "db_XXX" format (including malformed "db_db_db_XXX")
    if (idStr.includes('db_')) {
      const numericPart = idStr.replace(/db_/g, '');
      const parsed = parseInt(numericPart, 10);
      return !isNaN(parsed) && parsed > 0 ? parsed : null;
    }

    // v4.0.57: Reject UUIDs and hex strings — parseInt('5e1dfdc0-...') silently returns 5
    if (idStr.includes('-') || idStr.length > 10 || !/^[0-9]+$/.test(idStr)) {
      return null;
    }

    // Try to parse as integer (only pure numeric strings reach here)
    const parsed = parseInt(idStr, 10);
    return !isNaN(parsed) && parsed > 0 ? parsed : null;
  };

  // Try database_user.id first - MUST parse it, don't return raw value
  const dbUserId = parseId(user?.database_user?.id);
  if (dbUserId) {
    return dbUserId;
  }

  // Try user_metadata.user_id
  const metadataUserId = parseId(user?.user_metadata?.user_id);
  if (metadataUserId) {
    return metadataUserId;
  }

  // Try user.id directly
  const directUserId = parseId(user?.id);
  if (directUserId) {
    return directUserId;
  }

  // Fallback to 0 (will cause validation errors but won't crash)
  console.error('⚠️ Could not extract numeric user ID from user object:', user);
  return 0;
};

interface UserDashboardProps {
  onLogout: () => void
  user?: any
  onNavigate?: (section: string) => void
  onSwitchDashboard?: (dashboard: 'shareholder' | 'affiliate') => void
}

// Enhanced Navigation Types
type DashboardSection =
  | 'overview'
  | 'purchase-shares'
  | 'portfolio'
  | 'referrals'
  | 'payments'
  | 'notifications'
  | 'messages'
  | 'dividends'
  | 'shares'
  | 'marketing-tools'
  | 'company-presentation'
  | 'company-accounts'
  | 'mining-operations'
  | 'roadmap-journal'
  | 'media-gallery'
  | 'community-relations'
  | 'founding-shareholders'
  | 'support-center'
  | 'support-tickets'
  | 'consultations'
  | 'settings'
  | 'beneficiaries'
  | 'kyc'
  | 'legal-documents'
  | 'invoices'
  | 'share-audit'
  | 'acres'
  | 'gold-diggers'
  | 'vein-accord'
  | 'zimbabwe-awaits'
  | 'updates'

// Modern Icons - Enhanced Set
const DashboardIcon = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
    <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z"/>
  </svg>
)

const SharesIcon = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
    <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2H4zm2 5a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h3a1 1 0 100-2H7z" clipRule="evenodd"/>
  </svg>
)

const PortfolioIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
  </svg>
)

const PaymentIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
  </svg>
)

const __NotificationIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-5 5v-5zM10.97 4.97a.235.235 0 0 0-.02 0 .327.327 0 0 0-.23.06.19.19 0 0 0-.06.1c0 .06.02.13.06.18l.02.02c.05.06.12.09.2.09s.15-.03.2-.09l.02-.02c.04-.05.06-.12.06-.18a.19.19 0 0 0-.06-.1.327.327 0 0 0-.23-.06.235.235 0 0 0-.02 0z" />
  </svg>
)

const PresentationIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 4V2a1 1 0 011-1h8a1 1 0 011 1v2h4a1 1 0 011 1v14a1 1 0 01-1 1H3a1 1 0 01-1-1V5a1 1 0 011-1h4z" />
  </svg>
)

const MiningIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
  </svg>
)

const SupportIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192L5.636 18.364M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
  </svg>
)

const SettingsIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
)

const KYCIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
  </svg>
)

const LegalIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
  </svg>
)

const BeneficiaryIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
  </svg>
)

const CompanyIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
  </svg>
)

// Navigation Menu Items Configuration - Shareholder Dashboard
// `labelKey`/`descKey` are i18next keys consumed by the sidebar render blocks; the
// `label`/`description` strings remain as English fallbacks if a translation is missing.
const shareholderNavigationItems = [
  {
    id: 'overview' as DashboardSection,
    label: 'Dashboard Overview',
    labelKey: 'sidebar.overview.label',
    icon: DashboardIcon,
    description: 'Your share portfolio summary and quick actions',
    descKey: 'sidebar.overview.desc'
  },
  {
    id: 'updates' as DashboardSection,
    label: 'Updates',
    icon: () => <span>📢</span>,
    description: 'Published updates for shareholders',
  },
  {
    id: 'purchase-shares' as DashboardSection,
    label: 'Purchase Shares',
    labelKey: 'sidebar.purchaseShares.label',
    icon: SharesIcon,
    description: 'Buy gold mining shares',
    descKey: 'sidebar.purchaseShares.desc',
    highlight: true
  },
  {
    id: 'portfolio' as DashboardSection,
    label: 'My Portfolio',
    labelKey: 'sidebar.portfolio.label',
    icon: PortfolioIcon,
    description: 'View your share purchases and performance',
    descKey: 'sidebar.portfolio.desc'
  },
  {
    id: 'shares' as DashboardSection,
    label: 'My Shares',
    labelKey: 'sidebar.shares.label',
    icon: () => <span>📊</span>,
    description: 'Detailed view of all your share holdings and transactions',
    descKey: 'sidebar.shares.desc'
  },
  {
    id: 'company-accounts' as DashboardSection,
    label: 'Company Accounts',
    labelKey: 'sidebar.companyAccounts.label',
    icon: CompanyIcon,
    description: 'Register and manage company share purchases',
    descKey: 'sidebar.companyAccounts.desc'
  },
  {
    id: 'dividends' as DashboardSection,
    label: 'Dividends',
    labelKey: 'sidebar.dividends.label',
    icon: PaymentIcon,
    description: 'View dividend payments and projections',
    descKey: 'sidebar.dividends.desc'
  },
  {
    id: 'gold-diggers' as DashboardSection,
    label: 'Gold Diggers Pool',
    labelKey: 'sidebar.goldDiggers.label',
    icon: () => <span>⛏️</span>,
    description: 'Daily Top 50 pool — 10% of share purchases',
    descKey: 'sidebar.goldDiggers.desc',
    isNew: true
  },
  {
    id: 'vein-accord' as DashboardSection,
    label: 'Global Top Affiliate Pool',
    icon: () => <span>🪙</span>,
    description: 'Production Capital paid-share pool on your Daily Performance Pool legs',
    isNew: true,
    highlight: true
  },
  {
    id: 'zimbabwe-awaits' as DashboardSection,
    label: 'Zimbabwe Awaits',
    icon: () => <span>🇿🇼</span>,
    description: 'Top 10 team leaders win a mine visit and 10% share bonus',
    isNew: true,
    highlight: true
  },
  {
    id: 'acres' as DashboardSection,
    label: 'ACRES Rewards',
    icon: () => <span>🎁</span>,
    description: 'Complete tasks, earn points, redeem for fractional shares & win monthly prizes',
    isNew: true,
    highlight: true
  },
  {
    id: 'messages' as DashboardSection,
    label: 'Messages',
    labelKey: 'sidebar.messages.label',
    icon: () => <span>📬</span>,
    description: 'Internal messaging system',
    descKey: 'sidebar.messages.desc',
    badge: 0 // Will be updated with unread count
  },
  {
    id: 'company-presentation' as DashboardSection,
    label: 'Company Presentation',
    labelKey: 'sidebar.companyPresentation.label',
    icon: PresentationIcon,
    description: 'Learn about Aureus Alliance Holdings',
    descKey: 'sidebar.companyPresentation.desc'
  },
  {
    id: 'mining-operations' as DashboardSection,
    label: 'Mining Operations',
    labelKey: 'sidebar.miningOperations.label',
    icon: MiningIcon,
    description: 'View mining progress and operations',
    descKey: 'sidebar.miningOperations.desc'
  },
  {
    id: 'roadmap-journal' as DashboardSection,
    label: 'Roadmap Journal',
    labelKey: 'sidebar.roadmap.label',
    icon: () => <span>📖</span>,
    description: 'Follow our mining progress and milestones',
    descKey: 'sidebar.roadmap.desc'
  },
  {
    id: 'media-gallery' as DashboardSection,
    label: 'Media Gallery',
    icon: () => <span>🖼️</span>,
    description: 'Photos and videos from the site gallery'
  },
  {
    id: 'founding-shareholders' as DashboardSection,
    label: 'Founding Shareholders',
    labelKey: 'sidebar.foundingShareholders.label',
    icon: () => <span>🏆</span>,
    description: 'Recognized founding shareholders',
    descKey: 'sidebar.foundingShareholders.desc'
  },
  {
    id: 'support-center' as DashboardSection,
    label: 'Support Center',
    labelKey: 'sidebar.supportCenter.label',
    icon: SupportIcon,
    description: 'Get help and contact support',
    descKey: 'sidebar.supportCenter.desc'
  },
  {
    id: 'settings' as DashboardSection,
    label: 'Settings',
    labelKey: 'sidebar.settings.label',
    icon: SettingsIcon,
    description: 'Manage your account preferences',
    descKey: 'sidebar.settings.desc'
  },
  {
    id: 'beneficiaries' as DashboardSection,
    label: 'Beneficiaries',
    labelKey: 'sidebar.beneficiaries.label',
    icon: BeneficiaryIcon,
    description: 'Manage your next of kin / beneficiaries',
    descKey: 'sidebar.beneficiaries.desc'
  },
  {
    id: 'kyc' as DashboardSection,
    label: 'KYC Verification',
    labelKey: 'sidebar.kyc.label',
    icon: KYCIcon,
    description: 'Complete identity verification for full access',
    descKey: 'sidebar.kyc.desc',
    conditional: true
  },
  {
    id: 'legal-documents' as DashboardSection,
    label: 'Legal Documents',
    labelKey: 'sidebar.legalDocuments.label',
    icon: LegalIcon,
    description: 'Terms, privacy policy, and legal information',
    descKey: 'sidebar.legalDocuments.desc'
  }
]

// TipTap WYSIWYG Editor Component for Profile Description
const ProfileDescriptionEditor: React.FC<{ value: string; onChange: (value: string) => void }> = ({ value, onChange }) => {
  const editor = useEditor({
    extensions: [StarterKit],
    content: value,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML())
    },
    editorProps: {
      attributes: {
        'data-placeholder': 'Click here to type your profile description...'
      }
    }
  })

  // Update editor content when value prop changes
  React.useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value || '')
    }
  }, [value, editor])

  if (!editor) {
    return null
  }

  return (
    <div style={{ backgroundColor: '#374151', borderRadius: '0.5rem', overflow: 'hidden' }}>
      {/* Toolbar */}
      <div style={{
        display: 'flex',
        gap: '4px',
        padding: '8px',
        backgroundColor: '#1F2937',
        borderBottom: '1px solid #4B5563',
        flexWrap: 'wrap'
      }}>
        <button
          onClick={() => editor.chain().focus().toggleBold().run()}
          disabled={!editor.can().chain().focus().toggleBold().run()}
          style={{
            padding: '6px 10px',
            backgroundColor: editor.isActive('bold') ? '#FBBF24' : '#4B5563',
            color: editor.isActive('bold') ? '#000' : '#FFF',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontWeight: 'bold'
          }}
        >
          B
        </button>
        <button
          onClick={() => editor.chain().focus().toggleItalic().run()}
          disabled={!editor.can().chain().focus().toggleItalic().run()}
          style={{
            padding: '6px 10px',
            backgroundColor: editor.isActive('italic') ? '#FBBF24' : '#4B5563',
            color: editor.isActive('italic') ? '#000' : '#FFF',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontStyle: 'italic'
          }}
        >
          I
        </button>
        <button
          onClick={() => editor.chain().focus().toggleStrike().run()}
          disabled={!editor.can().chain().focus().toggleStrike().run()}
          style={{
            padding: '6px 10px',
            backgroundColor: editor.isActive('strike') ? '#FBBF24' : '#4B5563',
            color: editor.isActive('strike') ? '#000' : '#FFF',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            textDecoration: 'line-through'
          }}
        >
          S
        </button>
        <div style={{ width: '1px', backgroundColor: '#4B5563', margin: '0 4px' }} />
        <button
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          style={{
            padding: '6px 10px',
            backgroundColor: editor.isActive('heading', { level: 1 }) ? '#FBBF24' : '#4B5563',
            color: editor.isActive('heading', { level: 1 }) ? '#000' : '#FFF',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: 'bold'
          }}
        >
          H1
        </button>
        <button
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          style={{
            padding: '6px 10px',
            backgroundColor: editor.isActive('heading', { level: 2 }) ? '#FBBF24' : '#4B5563',
            color: editor.isActive('heading', { level: 2 }) ? '#000' : '#FFF',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: 'bold'
          }}
        >
          H2
        </button>
        <div style={{ width: '1px', backgroundColor: '#4B5563', margin: '0 4px' }} />
        <button
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          style={{
            padding: '6px 10px',
            backgroundColor: editor.isActive('bulletList') ? '#FBBF24' : '#4B5563',
            color: editor.isActive('bulletList') ? '#000' : '#FFF',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          • List
        </button>
        <button
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          style={{
            padding: '6px 10px',
            backgroundColor: editor.isActive('orderedList') ? '#FBBF24' : '#4B5563',
            color: editor.isActive('orderedList') ? '#000' : '#FFF',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          1. List
        </button>
        <button
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          style={{
            padding: '6px 10px',
            backgroundColor: editor.isActive('blockquote') ? '#FBBF24' : '#4B5563',
            color: editor.isActive('blockquote') ? '#000' : '#FFF',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          " Quote
        </button>
        <button
          onClick={() => editor.chain().focus().clearNodes().run()}
          style={{
            padding: '6px 10px',
            backgroundColor: '#4B5563',
            color: '#FFF',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Clear
        </button>
      </div>
      {/* Editor */}
      <div
        onClick={() => editor.chain().focus().run()}
        style={{
          minHeight: '200px',
          backgroundColor: '#4B5563',
          padding: '12px',
          cursor: 'text'
        }}
      >
        <style>{`
          .ProseMirror {
            min-height: 176px;
            outline: none;
            color: #FFFFFF;
            font-size: 14px;
            line-height: 1.6;
          }
          .ProseMirror p {
            margin: 0 0 0.5em 0;
          }
          .ProseMirror:focus {
            outline: none;
          }
          .ProseMirror p.is-editor-empty:first-child::before {
            color: #9CA3AF;
            content: attr(data-placeholder);
            float: left;
            height: 0;
            pointer-events: none;
          }
        `}</style>
        <EditorContent
          editor={editor}
        />
      </div>
    </div>
  )
}

export const UserDashboard: React.FC<UserDashboardProps> = ({ onLogout, user: propUser, onSwitchDashboard }) => {
  // i18next translation hook (common namespace) for sidebar/header/logout copy
  const { t } = useTranslation('common')
  // Live total shares sold — dynamic denominator for Jul/Oct 2026 + Jan 2027
  // payouts. While shares are still selling the per-share dividend scales up
  // vs the 1,400,000-cap floor.
  const { totalSharesSold: liveSharesSold } = useLiveTotalSharesSold()
  // Enhanced State Management
  // CRITICAL: Initialize from propUser OR localStorage (for mobile magic link flow)
  const [user, setUser] = useState<any>(() => {
    // First try propUser
    if (propUser && propUser.database_user?.full_name) {
      console.log('📱 UserDashboard: Initializing from propUser');
      return propUser;
    }
    // Fallback: Try localStorage (critical for mobile magic link flow)
    try {
      const storedUser = localStorage.getItem('aureus_user');
      if (storedUser) {
        const parsed = JSON.parse(storedUser);
        if (parsed && parsed.database_user?.full_name) {
          console.log('📱 UserDashboard: Initializing from localStorage', {
            id: parsed.id,
            full_name: parsed.database_user?.full_name
          });
          return parsed;
        }
      }
    } catch (e) {
      console.warn('Failed to parse localStorage user:', e);
    }
    // Final fallback: use propUser as-is
    return propUser || null;
  })
  const [loading, setLoading] = useState(true)

  // CRITICAL: Sync propUser changes to internal user state
  // This ensures that when App.tsx updates the user prop (e.g., after magic link login),
  // UserDashboard uses the updated data instead of stale initial state
  useEffect(() => {
    if (propUser && propUser.database_user?.full_name) {
      console.log('📱 UserDashboard: Syncing propUser to internal state', {
        id: propUser.id,
        full_name: propUser.database_user?.full_name,
        phone: propUser.database_user?.phone,
        country: propUser.database_user?.country_of_residence
      });
      setUser(propUser);
    }
  }, [propUser]);

  const [activeSection, setActiveSection] = useState<DashboardSection>(() => {
    const allowedSections = ['overview', 'portfolio', 'shares', 'purchase-shares', 'kyc', ...(DIVIDENDS_USER_VISIBLE ? ['dividends'] : []), 'messages', 'legal-documents', 'settings', 'gold-diggers', 'vein-accord', 'zimbabwe-awaits'];
    // Check URL parameters for initial section
    const urlParams = new URLSearchParams(window.location.search);
    const sectionParam = urlParams.get('section');
    if (sectionParam && allowedSections.includes(sectionParam)) {
      return sectionParam as DashboardSection;
    }
    // v7.3.x FIX (lost place on reload): restore the section after a mobile page
    // reload (e.g. opening the camera/gallery during KYC discards the page).
    // sessionStorage is tab-scoped: survives reloads, clears on tab close.
    const saved = readPersistedSection(allowedSections);
    if (saved) {
      return saved as DashboardSection;
    }
    return 'overview';
  })

  // v7.3.x FIX (lost place on reload): persist the active section so a mobile page
  // reload returns the user to where they were (esp. mid-KYC).
  useEffect(() => {
    persistSection(activeSection);
  }, [activeSection])

  useEffect(() => {
    if (!DIVIDENDS_USER_VISIBLE && activeSection === 'dividends') {
      setActiveSection('overview')
    }
  }, [activeSection])
  const [showPurchaseFlow, setShowPurchaseFlow] = useState(false)
  const [birthdayPackage, setBirthdayPackage] = useState<BirthdayPackageId | undefined>(undefined)
  const [productionCapitalPackage, setProductionCapitalPackage] = useState<ProductionCapitalPackageId | undefined>(undefined)
  const [resumePaymentData, setResumePaymentData] = useState<ResumePaymentData | undefined>(undefined)
  const [showPaymentResult, setShowPaymentResult] = useState<{
    status: string;
    shares?: string;
    amount?: string;
    reference?: string;
    method?: string;
    reason?: string;
  } | null>(null)
  const [, setUserSharePurchases] = useState<any[]>([])
  const [, setUserData] = useState<any>(null)
  const [, setCommissionData] = useState<any>(null)
  const [_notificationCount] = useState(0)
  const [unreadMessageCount, setUnreadMessageCount] = useState(0)
  const [kycStatus, setKycStatus] = useState<'pending' | 'approved' | 'rejected' | 'not_started'>('not_started')
  const [kycLoaded, setKycLoaded] = useState(false) // Guard: don't show KYC banner until status is fetched
  const [kycId, setKycId] = useState<string | null>(null)
  const [kycNotice, setKycNotice] = useState<{
    notice_state: 'none' | 'dismissible' | 'wall' | 'pending_banner'
    rejection_reason: string | null
    lookup_failed: boolean
  } | null>(null)
  const [kycNoticeDismissed, setKycNoticeDismissed] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [hasInvoices, setHasInvoices] = useState(false)

  // Navigation Functions
  const handleSectionChange = (section: DashboardSection) => {
    if (!DIVIDENDS_USER_VISIBLE && section === 'dividends') {
      setActiveSection('overview')
      return
    }

    // Prevent navigation away from legal documents if compliance is required
    if (mustCompleteLegalDocuments && section !== 'legal-documents' && !isMasterAdminViewSession()) {
      setMessage({
        type: 'warning',
        text: 'Please complete all legal document agreements before accessing other sections.'
      })
      setTimeout(() => setMessage(null), 4000)
      return
    }

    // Prevent navigation away from Settings if profile completion is required
    if (mustCompleteSettings && section !== 'settings' && !isMasterAdminViewSession()) {
      setMessage({
        type: 'warning',
        text: 'Please complete your profile in Settings before accessing other sections.'
      })
      setTimeout(() => setMessage(null), 4000)
      return
    }

    if (kycNotice?.notice_state === 'wall' && section !== 'kyc' && section !== 'legal-documents' && !isMasterAdminViewSession()) {
      setMessage({
        type: 'warning',
        text: 'Complete KYC verification before using other sections.'
      })
      setTimeout(() => setMessage(null), 4000)
      setActiveSection('kyc')
      return
    }

    // Prevent navigation if sponsor assignment is required
    if (mustAddSponsor && !isMasterAdminViewSession()) {
      setMessage({
        type: 'warning',
        text: 'Please add a sponsor before accessing the dashboard.'
      })
      setTimeout(() => setMessage(null), 4000)
      return
    }
    // v4.0.57: Wallet registration enforcement DEFERRED — users can continue without locked wallet
    // Will be re-enforced at a later stage with better UX
    // if (mustRegisterWallet && !isMasterAdminViewSession()) {
    //   setMessage({
    //     type: 'warning',
    //     text: 'Please register a locked withdrawal wallet before accessing the dashboard.'
    //   })
    //   setTimeout(() => setMessage(null), 4000)
    //   return
    // }

    // Allow purchases in admin assistance mode, but not in old view-only mode
    if (isMasterAdminViewSession() && section === 'purchase-shares') {
      const session = getMasterAdminViewSession()
      const canPurchase = session?.permissions?.canPurchase ?? false

      if (!canPurchase) {
        setMessage({
          type: 'error',
          text: 'Purchase actions are disabled in view-only mode.'
        })
        setTimeout(() => setMessage(null), 4000)
        return
      }
      // If canPurchase is true, allow navigation to continue
    }

    // Mark isNew sections as visited (dismisses the "New" badge permanently)
    if (section === 'gold-diggers') {
      localStorage.setItem('aureus_visited_gold-diggers', 'true')
    }
    if (section === 'vein-accord') {
      localStorage.setItem('aureus_visited_vein-accord', 'true')
    }
    if (section === 'zimbabwe-awaits') {
      localStorage.setItem('aureus_visited_zimbabwe-awaits', 'true')
    }

    setActiveSection(section)
    setIsMobileMenuOpen(false)

    // NOTE: Removed auto-popup for purchase-shares - users should see the page first with legacy claim buttons
  }

  // Get filtered navigation items based on user type and status
  const getNavigationItems = () => {
    // IMPORTANT: UserDashboard component ALWAYS shows shareholder navigation
    // regardless of user's actual role, because this IS the shareholder dashboard
    // Users can switch between dashboards using the dashboard switcher

    console.log('🔍 UserDashboard - Always showing shareholder navigation items')

    // Always use shareholder navigation items for UserDashboard component
    let baseNavigationItems = [...shareholderNavigationItems]
    if (!DIVIDENDS_USER_VISIBLE) {
      baseNavigationItems = baseNavigationItems.filter((item) => item.id !== 'dividends')
    }

    // Conditionally add invoices navigation if user has invoices
    if (hasInvoices) {
      // Insert invoices after referrals/portfolio section
      const referralsIndex = baseNavigationItems.findIndex(item => item.id === 'referrals')
      const insertIndex = referralsIndex >= 0 ? referralsIndex + 1 : baseNavigationItems.length
      baseNavigationItems.splice(insertIndex, 0, {
        id: 'invoices' as DashboardSection,
        label: 'Invoices',
        labelKey: 'sidebar.invoices.label',
        icon: () => <span>📄</span>,
        description: 'View your invoices and request payments',
        descKey: 'sidebar.invoices.desc'
      })
    }

    // Conditionally add Share Audit navigation for user 106 only
    const currentNumericId = extractNumericId(user?.database_user?.id || user?.id)
    if (currentNumericId === 106) {
      baseNavigationItems.push({
        id: 'share-audit' as DashboardSection,
        label: 'Share Audit',
        labelKey: 'sidebar.shareAudit.label',
        icon: () => <span>🔍</span>,
        description: 'Comprehensive share calculation audit tool',
        descKey: 'sidebar.shareAudit.desc'
      })
    }

    // v4.7.78: ACRES Rewards is restricted to user 106 again while pre-rollout
    // testing continues. The v4.7.69 broad rollout has been rolled back to the
    // v4.7.68 user-106 gate. Server-side RLS on ACRES RPCs still binds every
    // read/write to auth.uid(); this client gate just hides the nav surface
    // from non-test users until the operator clears the live rollout.
    return baseNavigationItems.filter(item => {
      if (item.conditional && item.id === 'kyc') {
        return kycLoaded && kycStatus !== 'approved'
      }
      if (item.id === 'acres') {
        return currentNumericId === 106
      }
      if (item.id === 'vein-accord') {
        return isGlobalTopAffiliatePoolTester(user?.database_user?.username || user?.username)
      }
      return true
    }).map(item => ({
      ...item,
      badge: item.id === 'messages' ? unreadMessageCount : item.badge
    }))
  }

  // Dashboard data states
  const [dashboardData, setDashboardData] = useState({
    totalShares: 0,
    purchasedShares: 0,
    bonusShares: 0, // v4.8.250: free/gift shares (BOGO, Anniversary, PCAP buyer + upline, Consolidated/Legacy Bonus)
    convertedShares: 0,
    commissionShares: 0,
    earnedShares: 0, // Shares earned from referral commissions only
    legacyBonusShares: 0, // Legacy bonus shares from historical adjustments
    sharesSent: 0, // Total shares sent to others
    sharesReceived: 0, // Total shares received from others
    netTransferredShares: 0, // Net transfer balance (received - sent)
    shareValue: 0,
    futureDividends: 0,
    // Dividend eligibility — 3-month holding rule (July 2026 cutoff: April 6, 2026)
    dividendEligibility: {
      firstPurchaseDate: null as Date | null,
      purchasedBeforeCutoff: false,
      firstDividendDate: 'July 2026',
      firstDividendMonth: 'July' as 'July',
      firstDividendYear: 2026,
      eligibleFor2026Payouts: [] as ('July' | 'October')[]
    },
    // Separate commission types
    usdtCommissions: {
      totalEarned: 0,
      available: 0,
      escrowed: 0,
      converted: 0,
      displayedBalance: 0,
      computedWithdrawable: 0,
      withdrawalRequestMax: 0,
      withdrawalNetworkFee: 2,
      withdrawalBlocked: false,
      withdrawalBlockReasonCode: null as string | null,
      withdrawalBlockReasonMessage: null as string | null
    },
    shareCommissions: {
      totalShares: 0,
      currentValue: 0
    },
    accountBalance: 0,
    referralCount: 0,
    monthlyEarnings: 0,
    currentSharePrice: null as number | null
  })
  const [dataLoading, setDataLoading] = useState(true)
  const liveSharePriceRef = useRef<number | null>(null)
  const rememberSharePrice = (value: unknown): number | null => {
    const price = acceptDisplayedSharePrice(value)
    if (price != null) liveSharePriceRef.current = price
    return liveSharePriceRef.current
  }

  useEffect(() => {
    let cancelled = false
    const loadLivePhasePrice = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/investment-phases/current`)
        if (!response.ok) return
        const phase = await response.json()
        const price = acceptDisplayedSharePrice(phase?.price_per_share)
        if (cancelled || price == null) return
        liveSharePriceRef.current = price
        setDashboardData((prev) => ({
          ...prev,
          currentSharePrice: price,
          shareValue: prev.totalShares > 0 ? prev.totalShares * price : prev.shareValue,
          shareCommissions: {
            ...prev.shareCommissions,
            currentValue: (prev.shareCommissions?.totalShares || 0) * price,
          },
        }))
      } catch {
        // Leave the price unknown. Do not substitute $25 or any other hardcoded price.
      }
    }
    loadLivePhasePrice()
    return () => { cancelled = true }
  }, [])

  // Pending/rejected/flagged payments state
  const [pendingPayments, setPendingPayments] = useState<Array<{
    id: string;
    shares_purchased: number;
    total_amount: number;
    payment_method: string;
    created_at: string;
    transaction_reference: string | null;
    status: 'pending' | 'rejected' | 'flagged_duplicate' | 'review_requested';
    transaction_notes: string | null;
  }>>([])
  const [pendingPaymentsLoading, setPendingPaymentsLoading] = useState(false)
  const [deletingPaymentId, setDeletingPaymentId] = useState<string | null>(null)

  // Settings states
  const [settingsLoading, setSettingsLoading] = useState(false)
  const [settingsMessage, setSettingsMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null)

  // Language settings states
  const [selectedLanguage, setSelectedLanguage] = useState('')
  const [savedLanguage, setSavedLanguage] = useState('')
  const [languageOptions, setLanguageOptions] = useState<Array<{ code: string; name: string }>>([])
  const [languageChanged, setLanguageChanged] = useState(false)
  const [languageSaving, setLanguageSaving] = useState(false)

  // General message state for dashboard-wide messages
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'warning', text: string } | null>(null)
  const [isProfileEditing, setIsProfileEditing] = useState(false)
  const [profileEditorData, setProfileEditorData] = useState({
    full_name: '',
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    country_of_residence: '',
    profile_description: ''
  })
  const [profileValidation, setProfileValidation] = useState({
    full_name: { status: 'idle', message: '' }, // idle, checking, valid, invalid
    email: { status: 'idle', message: '' },
    phone: { status: 'idle', message: '' },
    country_of_residence: { status: 'idle', message: '' }
  })

  // Get complete country list with prioritized countries
  const { getCountryName } = useCompleteCountryList({
    prioritizeCountries: ['ZAF', 'NAM', 'SWZ'] // South Africa, Namibia, Eswatini at top
  })

  // Notification states
  const [showNotificationDropdown, setShowNotificationDropdown] = useState(false)

  // Password change modal state
  const [showPasswordChangeModal, setShowPasswordChangeModal] = useState(false)

  // 2FA PIN verification states
  const [show2FAPinModal, setShow2FAPinModal] = useState(false)
  const [pending2FAState, setPending2FAState] = useState<boolean | null>(null)

  // Email verification states
  const [showEmailVerificationPinModal, setShowEmailVerificationPinModal] = useState(false)

  // Notification preferences states
  const [notificationPreferences, setNotificationPreferences] = useState({
    payment_confirmations: true,
    security_alerts: true,
    commission_updates: true,
    marketing_emails: false,
    in_app_notifications: true,
    sound_notifications: false,
    notification_frequency: 'immediate'
  })
  const [notificationPreferencesLoading, setNotificationPreferencesLoading] = useState(false)
  const [notificationPreferencesMessage, setNotificationPreferencesMessage] = useState<{
    type: 'success' | 'error'
    text: string
  } | null>(null)

  // Legal documents modal states
  const [showTermsModal, setShowTermsModal] = useState(false)
  const [showPrivacyModal, setShowPrivacyModal] = useState(false)
  const [showShareAgreementModal, setShowShareAgreementModal] = useState(false)
  const [showDisclaimerModal, setShowDisclaimerModal] = useState(false)
  const [showSharePurchaseAgreementModal, setShowSharePurchaseAgreementModal] = useState(false)

  // Agreement tracking states
  const [agreementStatus, setAgreementStatus] = useState({
    terms_conditions: false,
    privacy_policy: false,
    disclaimer: false,
    share_purchase_agreement: false,
    all_agreed: false
  })
  const [agreementLoading, setAgreementLoading] = useState(false)
  const [isCheckingCompliance, setIsCheckingCompliance] = useState(true)
  const [mustCompleteLegalDocuments, setMustCompleteLegalDocuments] = useState(false)
  const [mustCompleteSettings, setMustCompleteSettings] = useState(false)
  const [mustAddSponsor, setMustAddSponsor] = useState(false)
  const [_mustRegisterWallet, setMustRegisterWallet] = useState(false)
  const [sponsorUsername, setSponsorUsername] = useState('')
  const [sponsorValidating, setSponsorValidating] = useState(false)
  const [sponsorSubmitting, setSponsorSubmitting] = useState(false)
  const [sponsorError, setSponsorError] = useState<string | null>(null)
  const [sponsorSuccess, setSponsorSuccess] = useState<string | null>(null)
  const [validatedSponsorId, setValidatedSponsorId] = useState<number | null>(null)
  const [validatedSponsorName, setValidatedSponsorName] = useState<string | null>(null)

  // Check agreement status on component mount and enforce compliance
  useEffect(() => {
    const checkAgreements = async () => {
      if (user) {
        // Skip compliance checks entirely for master admin sessions
        if (isMasterAdminViewSession()) {
          console.log('🔑 LEGAL DOCS: Skipping compliance check for master admin session')
          setMustCompleteLegalDocuments(false)
          setMustCompleteSettings(false)
          setMustAddSponsor(false)
          setIsCheckingCompliance(false)
          return
        }
        console.log('🔍 LEGAL DOCS: Starting compliance check for user:', user.id)
        console.log('🔍 LEGAL DOCS: User object structure:', {
          user_id: user.id,
          user_id_type: typeof user.id,
          database_user_id: user?.database_user?.id,
          database_user_id_type: typeof user?.database_user?.id,
          email: user?.email,
          database_user_email: user?.database_user?.email
        })

        setIsCheckingCompliance(true)
        try {
          // CRITICAL FIX: Use centralized numeric user ID extraction
          const userIdForCheck = extractNumericUserId(user)

          if (!userIdForCheck || userIdForCheck === 0) {
            console.error('❌ LEGAL DOCS: Could not determine valid user ID from user object:', {
              user_id: user?.id,
              database_user_id: user?.database_user?.id,
              user_structure: Object.keys(user || {})
            })
            // Force legal documents section for safety
            setMustCompleteLegalDocuments(true)
            setActiveSection('legal-documents')
            return
          }

          console.log('🔍 LEGAL DOCS: Final user ID for check:', userIdForCheck, 'Type:', typeof userIdForCheck)

          // Fire sponsor check in parallel with legal docs check (don't await yet)
          const sponsorCheckPromise = fetch(`${API_BASE_URL}/api/user/affiliate-transactions`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...await ensureSessionHeaders() },
            body: JSON.stringify({ operation: 'checkHasSponsor', data: { userId: userIdForCheck } })
          }).then(r => r.json()).catch(() => ({ success: false }))

          const status = await LegalDocumentService.checkAgreementStatus(userIdForCheck)
          console.log('📋 LEGAL DOCS: Service returned status:', status)
          console.log('📋 LEGAL DOCS: Individual document status:', {
            terms_conditions: status.terms_conditions,
            privacy_policy: status.privacy_policy,
            disclaimer: status.disclaimer,
            share_purchase_agreement: status.share_purchase_agreement,
            all_agreed: status.all_agreed
          })

          setAgreementStatus(status)

          // ENHANCED DECISION LOGIC: More defensive approach
          if (!status.all_agreed) {
            console.log('⚠️ LEGAL DOCS: Documents incomplete, forcing compliance mode')
            console.log('⚠️ LEGAL DOCS: Missing documents:', {
              terms_conditions: !status.terms_conditions,
              privacy_policy: !status.privacy_policy,
              disclaimer: !status.disclaimer,
              share_purchase_agreement: !status.share_purchase_agreement
            })
            setMustCompleteLegalDocuments(true)
            setActiveSection('legal-documents')
          } else {
            console.log('✅ LEGAL DOCS: All documents completed, checking profile completion...')
            setMustCompleteLegalDocuments(false)

            // CRITICAL FIX: Fetch fresh profile data from API instead of relying on stale localStorage
            let profileForCheck = user?.database_user;

            try {
              const profileResponse = await fetch(`${API_BASE_URL}/api/user/dashboard-data`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...await ensureSessionHeaders() },
                body: JSON.stringify({ userId: userIdForCheck, dataTypes: ['profile'] })
              });
              const profileResult = await profileResponse.json();
              if (profileResponse.ok && profileResult.data?.profile) {
                const freshProfile = profileResult.data.profile;
                console.log('📋 PROFILE CHECK: Fresh data from API:', freshProfile);
                profileForCheck = freshProfile;

                // Update user state and localStorage with fresh data
                setUser((prev: any) => ({
                  ...prev,
                  database_user: {
                    ...prev?.database_user,
                    ...freshProfile
                  }
                }));
                try {
                  const stored = localStorage.getItem('aureus_user');
                  if (stored) {
                    const parsed = JSON.parse(stored);
                    parsed.database_user = { ...parsed.database_user, ...freshProfile };
                    localStorage.setItem('aureus_user', JSON.stringify(parsed));
                  }
                } catch (e) { /* ignore localStorage errors */ }
              }
            } catch (profileFetchError) {
              console.warn('⚠️ PROFILE CHECK: Could not fetch fresh profile, using cached data:', profileFetchError);
            }

            console.log('📋 PROFILE CHECK: Field values:', {
              full_name: profileForCheck?.full_name,
              first_name: profileForCheck?.first_name,
              last_name: profileForCheck?.last_name,
              phone: profileForCheck?.phone,
              country_of_residence: profileForCheck?.country_of_residence
            });

            const isProfileComplete = profileDetailsAreComplete(profileForCheck);
            console.log('📋 PROFILE CHECK: isProfileComplete =', isProfileComplete);

            if (!isProfileComplete) {
              console.log('⚠️ PROFILE: Profile incomplete, forcing Settings completion. Missing:', missingProfileDetailFields(profileForCheck));
              setMustCompleteSettings(true)
              setActiveSection('settings')
            } else {
              console.log('✅ PROFILE: Profile complete, checking sponsor status...')
              setMustCompleteSettings(false)

              // Use the parallel sponsor check result (already fired earlier)
              const sponsorResult = await sponsorCheckPromise
              if (sponsorResult.success && !sponsorResult.hasSponsor) {
                console.log('⚠️ SPONSOR: User has no sponsor, requiring sponsor assignment')
                setMustAddSponsor(true)
              } else {
                console.log('✅ SPONSOR: User has a sponsor or check skipped')
                setMustAddSponsor(false)
                // Leave the onboarding screens once legal docs + profile are already complete.
                // Previously this path never changed section, so a restored/forced
                // legal-documents view stayed on screen at 100%.
                setActiveSection(prev => (
                  prev === 'legal-documents' || prev === 'settings' ? 'overview' : prev
                ))
              }
            }
          }

          // DISABLED (v4.0.57): Mandatory wallet check disabled — users can access dashboard without locked wallet
          // The wallet registration UI remains available in the dashboard for voluntary use
          console.log('ℹ️ WALLET: Mandatory wallet registration check is disabled')
          setMustRegisterWallet(false)
        } catch (error: any) {
          console.error('❌ LEGAL DOCS: Error checking legal compliance:', error)
          console.error('❌ LEGAL DOCS: Error details:', {
            message: error?.message,
            stack: error?.stack,
            user_id: user.id,
            user_id_type: typeof user.id,
            database_user_id: user?.database_user?.id
          })

          // ENHANCED ERROR HANDLING: Don't force legal documents on service errors
          // Instead, allow access but show a warning
          console.log('⚠️ LEGAL DOCS: Service error occurred, allowing dashboard access with warning')
          setMustCompleteLegalDocuments(false)
          setMessage({
            type: 'warning',
            text: 'Legal document status could not be verified. Please contact support if you experience issues.'
          })
          setTimeout(() => setMessage(null), 8000)
        } finally {
          setIsCheckingCompliance(false)
          console.log('🔍 LEGAL DOCS: Compliance check completed')
        }
      } else {
        console.log('⚠️ LEGAL DOCS: No user ID available for compliance check')
      }
    }

    // Add a small delay to ensure user data is fully loaded
    // Only run if we have a valid user with either user.id or database_user.id
    if (user?.id || user?.database_user?.id) {
      const timeoutId = setTimeout(checkAgreements, 300) // Increased delay for mobile
      return () => clearTimeout(timeoutId)
    }
  }, [user?.id, user?.email, user?.database_user?.id, user?.database_user?.full_name, loading]) // Re-run when user data changes

  // Handle legal document agreement
  const handleDocumentAgreement = async (
    documentType: 'terms_conditions' | 'privacy_policy' | 'disclaimer' | 'share_purchase_agreement',
    closeModal: () => void
  ) => {
    if (!user) {
      console.log('❌ LEGAL DOCS: No user available for recording agreement')
      setMessage({
        type: 'error',
        text: 'Unable to record agreement - user session invalid. Please log out and log in again.'
      })
      setTimeout(() => setMessage(null), 5000)
      return
    }

    // CRITICAL FIX: Use centralized numeric user ID extraction
    const userIdForRecord = extractNumericUserId(user)

    console.log('📝 LEGAL DOCS: Recording agreement for document type:', documentType)
    console.log('📝 LEGAL DOCS: Using user ID:', userIdForRecord, 'Type:', typeof userIdForRecord)
    console.log('📝 LEGAL DOCS: User object details:', {
      user_id: user.id,
      database_user_id: user?.database_user?.id,
      email: user?.email || user?.database_user?.email
    })

    // CRITICAL VALIDATION: Ensure we have a valid user ID before proceeding
    if (!userIdForRecord || userIdForRecord === 0) {
      console.error('❌ LEGAL DOCS: CRITICAL ERROR - No valid user ID found!')
      console.error('❌ LEGAL DOCS: User object:', JSON.stringify(user, null, 2))
      setMessage({
        type: 'error',
        text: 'Unable to record agreement - invalid user ID. Please log out and log in again.'
      })
      setTimeout(() => setMessage(null), 5000)
      return
    }

    console.log('✅ LEGAL DOCS: Final validated user ID:', userIdForRecord, 'Type:', typeof userIdForRecord)

    setAgreementLoading(true)

    // Create a safety timeout to prevent infinite loading
    const safetyTimeout = setTimeout(() => {
      console.warn('⚠️ LEGAL DOCS: Safety timeout triggered - forcing loading state to end')
      setAgreementLoading(false)
      setMessage({
        type: 'error',
        text: 'Request timed out. Please try again or refresh the page.'
      })
      setTimeout(() => setMessage(null), 8000)
    }, 35000) // 35 seconds safety timeout

    try {
      const result = await LegalDocumentService.recordAgreement(userIdForRecord, documentType)
      console.log('📋 LEGAL DOCS: Agreement recording result:', result)

      clearTimeout(safetyTimeout) // Clear safety timeout on success

      if (result.success) {
        console.log('✅ LEGAL DOCS: Agreement recorded successfully')

        // Update agreement status
        console.log('🔄 LEGAL DOCS: Checking updated agreement status...')
        const newStatus = await LegalDocumentService.checkAgreementStatus(userIdForRecord)
        console.log('📋 LEGAL DOCS: Updated agreement status:', newStatus)
        console.log('📋 LEGAL DOCS: Updated individual status:', {
          terms_conditions: newStatus.terms_conditions,
          privacy_policy: newStatus.privacy_policy,
          disclaimer: newStatus.disclaimer,
          share_purchase_agreement: newStatus.share_purchase_agreement,
          all_agreed: newStatus.all_agreed
        })

        setAgreementStatus(newStatus)

        // Check if all documents are now agreed to and user was in compliance mode
        if (newStatus.all_agreed && mustCompleteLegalDocuments) {
          console.log('🎉 All legal documents now completed! Redirecting to Settings to complete profile...')
          setMustCompleteLegalDocuments(false)

          // Check if profile is complete - fetch fresh data from API
          let isProfileComplete = profileDetailsAreComplete(user?.database_user);

          // Try to get fresh data if cached check fails
          if (!isProfileComplete) {
            try {
              const userId = extractNumericUserId(user);
              const profileResp = await fetch(`${API_BASE_URL}/api/user/dashboard-data`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...await ensureSessionHeaders() },
                body: JSON.stringify({ userId, dataTypes: ['profile'] })
              });
              const profileRes = await profileResp.json();
              if (profileResp.ok && profileRes.data?.profile) {
                const fp = profileRes.data.profile;
                isProfileComplete = profileDetailsAreComplete(fp);
                if (isProfileComplete) {
                  setUser((prev: any) => ({ ...prev, database_user: { ...prev?.database_user, ...fp } }));
                }
              }
            } catch (e) { /* use cached result */ }
          }

          if (!isProfileComplete) {
            // NEW FLOW: Redirect to Settings to complete profile
            setMustCompleteSettings(true)
            setActiveSection('settings')

            // Show success message explaining next step
            setMessage({
              type: 'success',
              text: '✅ Legal documents completed! Please complete your profile in Settings. You can then purchase shares immediately.'
            })
            setTimeout(() => setMessage(null), 8000)
          } else {
            // Profile already complete, check sponsor before allowing dashboard access
            try {
              const userId = extractNumericUserId(user)
              const sponsorResp = await fetch(`${API_BASE_URL}/api/user/affiliate-transactions`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...await ensureSessionHeaders() },
                body: JSON.stringify({ operation: 'checkHasSponsor', data: { userId } })
              })
              const sponsorRes = await sponsorResp.json()
              if (sponsorResp.ok && sponsorRes.success && !sponsorRes.hasSponsor) {
                setMustAddSponsor(true)
                setMessage({
                  type: 'success',
                  text: '✅ Legal documents completed! Please assign a sponsor to continue.'
                })
                setTimeout(() => setMessage(null), 8000)
              } else {
                setActiveSection('overview')
                setMessage({
                  type: 'success',
                  text: '✅ Legal documents completed! You can now purchase shares.'
                })
                setTimeout(() => setMessage(null), 8000)
              }
            } catch (e) {
              setActiveSection('overview')
              setMessage({
                type: 'success',
                text: '✅ Legal documents completed! You can now purchase shares.'
              })
              setTimeout(() => setMessage(null), 8000)
            }
          }
        } else if (newStatus.all_agreed) {
          console.log('✅ All documents agreed, but user was not in compliance mode')
          // Show individual agreement success message
          setSettingsMessage({
            type: 'success',
            text: 'Agreement recorded successfully!'
          })
          setTimeout(() => setSettingsMessage(null), 3000)
        } else {
          console.log('📝 Document agreement recorded, but not all documents completed yet. Status:', newStatus)
          // Show individual agreement success message
          setSettingsMessage({
            type: 'success',
            text: 'Agreement recorded successfully!'
          })
          setTimeout(() => setSettingsMessage(null), 3000)
        }

        // Close modal immediately on success
        closeModal()
      } else {
        clearTimeout(safetyTimeout) // Clear safety timeout on failure
        // Show error message - keep modal open so user can retry
        console.error('❌ LEGAL DOCS: Agreement recording failed:', result.error)
        setMessage({
          type: 'error',
          text: result.error || 'Failed to record agreement. Please try again.'
        })
        setTimeout(() => setMessage(null), 8000)
        // Do NOT close modal - let user retry
      }
    } catch (error: any) {
      clearTimeout(safetyTimeout) // Clear safety timeout on exception
      console.error('❌ LEGAL DOCS: Exception recording agreement:', error)

      // Check if it's a timeout error
      const isTimeout = error.message?.includes('timed out') || error.message?.includes('timeout')

      setMessage({
        type: 'error',
        text: isTimeout
          ? 'Request timed out. Please check your internet connection and try again.'
          : 'Failed to record agreement. Please try again or refresh the page.'
      })
      setTimeout(() => setMessage(null), 8000)
      // Do NOT close modal - let user retry
    } finally {
      setAgreementLoading(false)
    }
  }

  // Calculator state to track current gold price
  const [currentCalculatorData] = useState<any>(null)

  useEffect(() => {
    loadUser()
  }, [])

  // Check for payment result from Yoco redirect
  useEffect(() => {
    const paymentResultStr = localStorage.getItem('aureus_payment_result')
    if (paymentResultStr) {
      try {
        const paymentResult = JSON.parse(paymentResultStr)
        console.log('💳 Payment result found:', paymentResult)
        setShowPaymentResult(paymentResult)
        // Clear it so it doesn't show again on refresh
        localStorage.removeItem('aureus_payment_result')
      } catch (e) {
        console.error('Failed to parse payment result:', e)
        localStorage.removeItem('aureus_payment_result')
      }
    }
  }, [])

  // Load notification preferences when user data is available
  useEffect(() => {
    if (user?.database_user?.id) {
      loadNotificationPreferences()
    }
  }, [user?.database_user?.id])

  useEffect(() => {
    if (!user?.database_user?.id) return
    let cancelled = false
    const loadPreferredLanguage = async () => {
      try {
        const response = await fetch('/api/user/pref-language', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...await ensureSessionHeaders() },
          body: JSON.stringify({ operation: 'current' }),
        })
        if (!response.ok) return
        const data = await response.json()
        if (cancelled || data?.impersonated) return
        const options = Array.isArray(data?.languages)
          ? data.languages
              .filter((row: { code?: string; name?: string }) => row?.code && row?.name)
              .map((row: { code: string; name: string }) => ({ code: row.code, name: row.name }))
          : []
        const chosen = typeof data?.prefLanguage === 'string' ? data.prefLanguage : ''
        setLanguageOptions(options)
        setSelectedLanguage(chosen)
        setSavedLanguage(chosen)
      } catch {
        // Leave the selector empty. The person can open Settings again.
      }
    }
    loadPreferredLanguage()
    return () => { cancelled = true }
  }, [user?.database_user?.id])

  // Centralized profile picture update function
  const handleProfilePictureUpdate = async (newImageUrl: string) => {
    console.log('🖼️ Updating profile picture across all components:', newImageUrl);

    // Small delay to ensure database update has completed
    await new Promise(resolve => setTimeout(resolve, 100));

    // Update the user object immediately
    if (user?.database_user) {
      user.database_user.profile_image_url = newImageUrl;
      user.database_user.updated_at = new Date().toISOString();
    }

    // Update localStorage for persistence with timestamp
    const currentTimestamp = new Date().toISOString();
    const storedUser = localStorage.getItem('aureus_user');
    if (storedUser) {
      const userData = JSON.parse(storedUser);
      userData.profile_image_url = newImageUrl;
      userData.updated_at = currentTimestamp;
      localStorage.setItem('aureus_user', JSON.stringify(userData));
      console.log('✅ Profile picture URL updated in localStorage with timestamp');
    }

    // Force a re-render by updating state with the new URL
    const updatedUser = { ...user };
    if (updatedUser.database_user) {
      updatedUser.database_user.profile_image_url = newImageUrl;
      updatedUser.database_user.updated_at = currentTimestamp;
    }
    setUser(updatedUser);

    // Show success message
    setSettingsMessage({ type: 'success', text: 'Profile picture updated successfully!' });
    setTimeout(() => setSettingsMessage(null), 3000);

    console.log('✅ Profile picture update completed with timestamp:', currentTimestamp);
  };

  // Settings handlers
  const handle2FAToggle = async (enabled: boolean) => {
    if (!user?.database_user?.id) return;

    // Store the pending state and show PIN verification modal
    setPending2FAState(enabled);
    setShow2FAPinModal(true);
  };

  // Handle 2FA email-PIN verification success — calls toggle-2fa endpoint with verified token
  const handle2FAPinVerified = async (verifiedToken?: string) => {
    // CRITICAL: Must use parsed user ID to prevent "invalid input syntax for type integer" errors
    const userId = extractNumericUserId(user);
    if (!userId || userId === 0 || pending2FAState === null) return;

    // v4.6.3: PinVerificationModal no longer calls onClose on success (it would
    // clobber parent state transitions on TotpMfaPanel). Close it ourselves.
    setShow2FAPinModal(false);

    setSettingsLoading(true);
    setSettingsMessage(null);

    try {
      const response = await fetch(`${API_BASE_URL}/api/user/toggle-2fa`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...await ensureSessionHeaders() },
        body: JSON.stringify({ userId, enabled: pending2FAState, token: verifiedToken })
      });

      const result = await response.json();

      if (result.success) {
        // Reload security settings from database to get the updated two_factor_enabled status
        await loadUserSecuritySettings(userId);

        setSettingsMessage({
          type: 'success',
          text: `2FA ${pending2FAState ? 'enabled' : 'disabled'} successfully`
        });

        // Clear message after 3 seconds
        setTimeout(() => setSettingsMessage(null), 3000);
      } else {
        setSettingsMessage({
          type: 'error',
          text: result.error || 'Failed to update 2FA settings'
        });
      }
    } catch (error) {
      console.error('Error toggling 2FA:', error);
      setSettingsMessage({
        type: 'error',
        text: 'Failed to update 2FA settings'
      });
    } finally {
      setSettingsLoading(false);
      setPending2FAState(null);
    }
  };

  // Handle 2FA PIN modal close
  const handle2FAPinModalClose = () => {
    setShow2FAPinModal(false);
    setPending2FAState(null);
    setSettingsLoading(false);
  };

  // Refresh user profile data from database when Settings section is active
  useEffect(() => {
    const refreshProfileDisplay = async () => {
      if (activeSection === 'settings') {
        const userId = extractNumericId(user?.database_user?.id || user?.id);

        if (!userId) {
          console.error('❌ Cannot refresh profile display: Invalid user ID');
          return;
        }

        console.log('🔄 Refreshing profile display data from database for user:', userId);

        try {
          const response = await fetch(`${API_BASE_URL}/api/user/dashboard-data`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...await ensureSessionHeaders() },
            body: JSON.stringify({ userId, dataTypes: ['profile'] })
          })

          const result = await response.json()
          if (!response.ok) {
            console.error('❌ Error refreshing profile display:', result.error);
            return;
          }

          const freshUserData = result.data?.profile
          if (freshUserData) {
            console.log('✅ Profile display refreshed with latest data:', freshUserData);

            // Update user state with fresh data from database
            setUser((prev: any) => ({
              ...prev,
              database_user: {
                ...prev?.database_user,
                full_name: freshUserData.full_name,
                first_name: freshUserData.first_name,
                last_name: freshUserData.last_name,
                email: freshUserData.email,
                phone: freshUserData.phone,
                country_of_residence: freshUserData.country_of_residence,
                profile_description: freshUserData.profile_description,
                username: freshUserData.username,
                profile_details_edit_used: freshUserData.profile_details_edit_used !== false
              }
            }));

            // Also update localStorage to persist the fresh data
            const updatedUser = {
              ...user,
              database_user: {
                ...user?.database_user,
                ...freshUserData
              }
            };
            localStorage.setItem('aureus_user', JSON.stringify(updatedUser));
            console.log('💾 Updated user in localStorage with fresh profile data');
          }
        } catch (error) {
          console.error('❌ Error refreshing profile display:', error);
        }
      }
    };

    refreshProfileDisplay();
  }, [activeSection]); // Refresh when Settings section becomes active

  // Scroll to top whenever section changes (catches programmatic changes)
  useEffect(() => {
    // Use setTimeout to ensure content is rendered before scrolling
    setTimeout(() => {
      window.scrollTo(0, 0); // Instant scroll, no smooth behavior
    }, 100);
  }, [activeSection]);

  // Refresh profile display from database when Settings section becomes active - uses API endpoint
  useEffect(() => {
    const refreshProfileFromDatabase = async () => {
      if (activeSection === 'settings') {
        const userId = extractNumericId(user?.database_user?.id || user?.id);

        if (!userId) {
          console.error('❌ SETTINGS: Cannot refresh - Invalid user ID');
          return;
        }

        console.log('🔄 SETTINGS: Fetching fresh profile data from database for user:', userId);

        try {
          const response = await fetch(`${API_BASE_URL}/api/user/dashboard-data`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...await ensureSessionHeaders() },
            body: JSON.stringify({ userId, dataTypes: ['profile'] })
          })

          const result = await response.json()
          if (!response.ok) {
            console.error('❌ SETTINGS: Database fetch error:', result.error);
            return;
          }

          const freshData = result.data?.profile
          if (freshData) {
            console.log('✅ SETTINGS: Fresh data from database:', freshData);

            // Update user state with fresh database data
            setUser((prev: any) => ({
              ...prev,
              database_user: {
                ...prev?.database_user,
                ...freshData
              }
            }));
          }
        } catch (error) {
          console.error('❌ SETTINGS: Error refreshing profile:', error);
        }
      }
    };

    refreshProfileFromDatabase();
  }, [activeSection]);

  // Profile editor handlers - Load data when entering edit mode (AJAX) - uses API endpoint
  const loadProfileData = async () => {
    if (!selfServiceProfileEditOpen(user?.database_user)) {
      setSettingsMessage({ type: 'error', text: PROFILE_DETAILS_ADMIN_NOTE })
      setIsProfileEditing(false)
      return
    }
    try {
      // CRITICAL: Fetch fresh data from database instead of using stale user state
      const userId = extractNumericId(user?.database_user?.id || user?.id);

      if (!userId) {
        console.error('❌ Cannot load profile data: Invalid user ID');
        return;
      }

      console.log('🔄 Fetching fresh profile data from database for user:', userId);

      const response = await fetch(`${API_BASE_URL}/api/user/dashboard-data`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...await ensureSessionHeaders() },
        body: JSON.stringify({ userId, dataTypes: ['profile'] })
      })

      const result = await response.json()
      if (!response.ok) {
        console.error('❌ Error fetching fresh profile data:', result.error);
        // Fallback to current user state if fetch fails
        const countryCode = user?.database_user?.country_of_residence || '';
        const storedName = splitStoredName(user?.database_user);
        setProfileEditorData({
          full_name: [storedName.name, storedName.surname].filter(Boolean).join(' '),
          first_name: storedName.name,
          last_name: storedName.surname,
          email: user?.database_user?.email || user?.email || '',
          phone: user?.database_user?.phone || '',
          country_of_residence: countryCode,
          profile_description: user?.database_user?.profile_description || ''
        });
      } else {
        const freshUserData = result.data?.profile
        if (freshUserData) {
          console.log('✅ Fresh profile data loaded:', {
            full_name: freshUserData.full_name,
            email: freshUserData.email,
            phone: freshUserData.phone,
            country_of_residence: freshUserData.country_of_residence
          });

          const storedName = splitStoredName(freshUserData);
          setProfileEditorData({
            full_name: [storedName.name, storedName.surname].filter(Boolean).join(' '),
            first_name: storedName.name,
            last_name: storedName.surname,
            email: freshUserData.email || '',
            phone: freshUserData.phone || '',
            country_of_residence: freshUserData.country_of_residence || '',
            profile_description: freshUserData.profile_description || ''
          });
        }
      }

      setProfileValidation({
        full_name: { status: 'idle', message: '' },
        email: { status: 'idle', message: '' },
        phone: { status: 'idle', message: '' },
        country_of_residence: { status: 'idle', message: '' }
      });

      // Enter edit mode (no modal, inline editing)
      setIsProfileEditing(true);
    } catch (error) {
      console.error('❌ Error loading profile data:', error);
      setSettingsMessage({
        type: 'error',
        text: 'Failed to load profile data'
      });
    }
  };

  const profilePromptedRef = useRef(false)
  useEffect(() => {
    if (!mustCompleteSettings || activeSection !== 'settings' || isProfileEditing || profilePromptedRef.current) return
    profilePromptedRef.current = true
    loadProfileData()
  }, [mustCompleteSettings, activeSection, isProfileEditing])

  // Validation functions with debouncing
  const validateFullName = useCallback((name: string) => {
    if (!name.trim()) {
      return { status: 'invalid', message: '❌ Name is required' };
    }

    // Check for invalid characters (no periods, only letters, spaces, hyphens, apostrophes)
    if (!/^[a-zA-Z\s\-']+$/.test(name.trim())) {
      return { status: 'invalid', message: '❌ Name may only contain letters, spaces, hyphens, and apostrophes. No periods or special characters.' };
    }

    const nameParts = name.trim().split(/\s+/);
    if (nameParts.length < 2) {
      return { status: 'invalid', message: '⚠️ Please enter both first and last name' };
    }

    // Check each part is at least 2 characters (no single letters)
    for (const part of nameParts) {
      if (part.length < 2) {
        return { status: 'invalid', message: '❌ Each part of your name must be at least 2 characters. No single letters.' };
      }
    }

    return { status: 'valid', message: '✅ Name looks good' };
  }, []);

  const validateEmail = useCallback((email: string) => {
    if (!email.trim()) {
      return { status: 'invalid', message: '❌ Email is required' };
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return { status: 'invalid', message: '❌ Invalid email format' };
    }
    return { status: 'valid', message: '✅ Email looks good' };
  }, []);

  const validatePhone = useCallback((phone: string, country: string) => {
    if (!phone.trim()) {
      return { status: 'invalid', message: '❌ Phone number is required' };
    }
    if (!country) {
      return { status: 'invalid', message: '⚠️ Please select a country first' };
    }
    // Basic phone validation - should have at least 7 digits
    const digitsOnly = phone.replace(/\D/g, '');
    if (digitsOnly.length < 7) {
      return { status: 'invalid', message: '⚠️ Phone number too short' };
    }
    if (digitsOnly.length > 15) {
      return { status: 'invalid', message: '⚠️ Phone number too long' };
    }
    return { status: 'valid', message: '✅ Phone number valid' };
  }, []);

  const validateCountry = useCallback((country: string) => {
    if (!country) {
      return { status: 'invalid', message: '⚠️ Please select a country' };
    }
    return { status: 'valid', message: '✅ Country selected' };
  }, []);

  // Debounced validation handlers
  useEffect(() => {
    const timer = setTimeout(() => {
      if (profileEditorData.full_name) {
        setProfileValidation(prev => ({
          ...prev,
          full_name: validateFullName(profileEditorData.full_name)
        }));
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [profileEditorData.full_name, validateFullName]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (profileEditorData.email) {
        setProfileValidation(prev => ({
          ...prev,
          email: validateEmail(profileEditorData.email)
        }));
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [profileEditorData.email, validateEmail]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (profileEditorData.phone) {
        setProfileValidation(prev => ({
          ...prev,
          phone: validatePhone(profileEditorData.phone, profileEditorData.country_of_residence)
        }));
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [profileEditorData.phone, profileEditorData.country_of_residence, validatePhone]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (profileEditorData.country_of_residence) {
        setProfileValidation(prev => ({
          ...prev,
          country_of_residence: validateCountry(profileEditorData.country_of_residence)
        }));
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [profileEditorData.country_of_residence, validateCountry]);

  const handleProfileUpdate = async () => {
    // Use centralized user ID extraction
    const rawUserId = user?.database_user?.id ||
                     user?.user_metadata?.user_id ||
                     user?.id;

    const userId = extractNumericId(rawUserId);

    // Validate we have a valid user ID
    if (!userId) {
      console.error('❌ Cannot update profile: Invalid user ID', {
        raw_id: rawUserId,
        parsed_id: userId,
        user_structure: {
          has_database_user: !!user?.database_user,
          database_user_id: user?.database_user?.id,
          user_metadata_id: user?.user_metadata?.user_id,
          direct_id: user?.id
        }
      });
      setSettingsMessage({
        type: 'error',
        text: 'Unable to update profile: Invalid user ID'
      });
      return;
    }

    const givenName = profileEditorData.first_name.trim();
    const surname = profileEditorData.last_name.trim();
    if (!givenName || !surname) {
      setSettingsMessage({
        type: 'error',
        text: '❌ Please enter your name and surname'
      });
      return;
    }
    if (!profileEditorData.phone.trim()) {
      setSettingsMessage({
        type: 'error',
        text: '❌ Please enter your phone number'
      });
      return;
    }

    setSettingsLoading(true);
    setSettingsMessage(null);

    try {
      console.log('🔄 Updating profile for user:', userId, 'Original ID:', rawUserId);
      console.log('📝 Profile data:', profileEditorData);
      console.log('🌍 Country code:', profileEditorData.country_of_residence);

      // Use API endpoint to bypass RLS policies
      const response = await fetch(`${API_BASE_URL}/api/user/update-profile`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...await ensureSessionHeaders() },
        body: JSON.stringify({
          userId,
          operation: 'update_profile',
          data: {
            full_name: `${givenName} ${surname}`,
            first_name: givenName,
            last_name: surname,
            phone: profileEditorData.phone,
            country_of_residence: profileEditorData.country_of_residence,
            profile_description: profileEditorData.profile_description
          }
        })
      })

      const updateResult = await response.json()
      if (!response.ok) {
        // Display detailed validation message if available, with field prefix if specified
        const fieldPrefix = updateResult.field ? `${updateResult.field}: ` : ''
        const errorMessage = updateResult.message || updateResult.error || 'Failed to update profile'
        throw new Error(`${fieldPrefix}${errorMessage}`)
      }

      console.log('✅ Profile updated successfully via API');

      // --- Post-update steps (refresh + redirect) wrapped safely ---
      // The update succeeded, so even if these fail the user should see success
      try {
        // Get fresh user data from API
        const refreshResponse = await fetch(`${API_BASE_URL}/api/user/update-profile`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...await ensureSessionHeaders() },
          body: JSON.stringify({
            userId,
            operation: 'get_fresh_profile'
          })
        })

        const refreshResult = await refreshResponse.json()
        if (refreshResponse.ok && refreshResult.data?.user) {
          const refreshedUser = refreshResult.data.user
          console.log('✅ Refreshed user data from database:', refreshedUser);

          const isRefreshedProfileComplete = profileDetailsAreComplete(refreshedUser);

          setUser((prev: any) => ({
            ...prev,
            database_user: refreshedUser,
            needsProfileCompletion: !isRefreshedProfileComplete,
            user_metadata: {
              ...prev?.user_metadata,
              profile_completion_required: !isRefreshedProfileComplete
            }
          }));

          const finalProfileDescription = refreshedUser.profile_description
          setProfileEditorData((prev: any) => ({
            ...prev,
            profile_description: finalProfileDescription
          }))
        }
      } catch (refreshErr) {
        console.warn('⚠️ Post-update refresh failed (update still succeeded):', refreshErr);
      }

      // Check profile completion for redirect logic
      const isProfileComplete = profileDetailsAreComplete({
        full_name: `${profileEditorData.first_name} ${profileEditorData.last_name}`,
        first_name: profileEditorData.first_name,
        last_name: profileEditorData.last_name,
        phone: profileEditorData.phone,
        country_of_residence: profileEditorData.country_of_residence
      });

      if (isProfileComplete) {
        setSettingsMessage({
          type: 'success',
          text: '✅ Profile updated successfully! Redirecting to purchase shares...'
        });

        setIsProfileEditing(false);
        setMustCompleteSettings(false);

        // Check if sponsor is needed before redirecting
        try {
          const sponsorCheckUserId = extractNumericUserId(user);
          const sponsorResp = await fetch(`${API_BASE_URL}/api/user/affiliate-transactions`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...await ensureSessionHeaders() },
            body: JSON.stringify({ operation: 'checkHasSponsor', data: { userId: sponsorCheckUserId } })
          });
          const sponsorRes = await sponsorResp.json();
          if (sponsorResp.ok && sponsorRes.success && !sponsorRes.hasSponsor) {
            setMustAddSponsor(true);
            setSettingsMessage({
              type: 'success',
              text: '✅ Profile updated! Please assign a sponsor to continue.'
            });
            setTimeout(() => setSettingsMessage(null), 5000);
          } else {
            setTimeout(() => {
              setSettingsMessage(null);
              setActiveSection('purchase-shares');
            }, 2000);
          }
        } catch (e) {
          setTimeout(() => {
            setSettingsMessage(null);
            setActiveSection('purchase-shares');
          }, 2000);
        }
      } else {
        setSettingsMessage({
          type: 'success',
          text: 'Profile updated successfully!'
        });

        setIsProfileEditing(false);
        setTimeout(() => setSettingsMessage(null), 3000);
      }

    } catch (error: any) {
      console.error('❌ Error updating profile:', error);
      const errorMsg = error?.message || (typeof error === 'string' ? error : JSON.stringify(error) || 'Unknown error');
      setSettingsMessage({
        type: 'error',
        text: `Failed to update profile: ${errorMsg}`
      });
    } finally {
      setSettingsLoading(false);
    }
  };

  // Sponsor validation handler
  const handleSponsorValidate = async () => {
    if (!sponsorUsername.trim()) {
      setSponsorError('Please enter a sponsor username')
      return
    }
    setSponsorValidating(true)
    setSponsorError(null)
    setSponsorSuccess(null)
    setValidatedSponsorId(null)
    setValidatedSponsorName(null)
    try {
      const userId = extractNumericUserId(user)
      const response = await fetch(`${API_BASE_URL}/api/user/assign-sponsor`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...await ensureSessionHeaders() },
        body: JSON.stringify({ userId, operation: 'search_sponsor', data: { username: sponsorUsername.trim() } })
      })
      const result = await response.json()
      if (result.success && result.data?.sponsor) {
        const sponsor = result.data.sponsor
        setValidatedSponsorId(sponsor.id)
        setValidatedSponsorName(sponsor.displayName || sponsor.username)
        setSponsorSuccess(`Sponsor found: ${sponsor.displayName || sponsor.username} (@${sponsor.username}). Click "Confirm Sponsor" to assign.`)
        setSponsorError(null)
      } else {
        setSponsorError(result.error || 'No user found with that username')
        setSponsorSuccess(null)
      }
    } catch (err: any) {
      setSponsorError('Failed to validate sponsor. Please try again.')
    } finally {
      setSponsorValidating(false)
    }
  }

  // Sponsor submission handler
  const handleSponsorSubmit = async () => {
    if (!validatedSponsorId) {
      setSponsorError('Please search and verify a sponsor first')
      return
    }
    setSponsorSubmitting(true)
    setSponsorError(null)
    try {
      const userId = extractNumericUserId(user)
      const response = await fetch(`${API_BASE_URL}/api/user/assign-sponsor`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...await ensureSessionHeaders() },
        body: JSON.stringify({ userId, operation: 'assign_sponsor', data: { sponsorId: validatedSponsorId } })
      })
      const result = await response.json()
      if (response.ok && result.success) {
        setSponsorSuccess('Sponsor assigned successfully!')
        setSponsorError(null)
        setMustAddSponsor(false)
        setActiveSection('overview')
        setMessage({
          type: 'success',
          text: '✅ Sponsor assigned successfully! Welcome to Aureus Africa.'
        })
        setTimeout(() => setMessage(null), 5000)
      } else {
        // Clear the stale "Sponsor found… Click Confirm" success so the UI
        // never shows a green + red message at once. Keep validatedSponsorId
        // so the user can retry Confirm.
        setSponsorSuccess(null)
        setSponsorError(result.error || 'Failed to assign sponsor. Please try again.')
      }
    } catch (err: any) {
      setSponsorSuccess(null)
      setSponsorError('Failed to assign sponsor. Please try again.')
    } finally {
      setSponsorSubmitting(false)
    }
  }

  // Handle successful email verification via PIN - uses API endpoint
  const handleEmailVerificationPinSuccess = async () => {
    // CRITICAL: Must use parsed user ID to prevent "invalid input syntax for type integer" errors
    const userId = extractNumericUserId(user);
    if (!userId || userId === 0) return;

    try {
      // Update email_verified status via API endpoint
      const response = await fetch(`${API_BASE_URL}/api/user/update-profile`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...await ensureSessionHeaders() },
        body: JSON.stringify({
          userId,
          operation: 'verify_email'
        })
      })

      const result = await response.json()
      if (!response.ok) {
        console.error('❌ Error updating email verification status:', result.error);
        setSettingsMessage({
          type: 'error',
          text: 'Failed to update email verification status'
        });
        return;
      }

      // Reload security settings from database to get the updated email_verified status
      await loadUserSecuritySettings(userId);

      // Show success message
      setSettingsMessage({
        type: 'success',
        text: 'Email verified successfully! Your account is now fully verified.'
      });

      // Close modal
      setShowEmailVerificationPinModal(false);

      // Clear success message after 5 seconds
      setTimeout(() => setSettingsMessage(null), 5000);
    } catch (error) {
      console.error('❌ Error in email verification success handler:', error);
      setSettingsMessage({
        type: 'error',
        text: 'Failed to complete email verification'
      });
    }
  };

  // Debug function to refresh user data (for testing)
  const handleRefreshUserData = async () => {
    console.log('🔄 Refreshing user data...');
    // Clear localStorage cache
    localStorage.removeItem('aureus_user');
    localStorage.removeItem('aureus_test_user');
    // Reload user data
    await loadUser();
    console.log('✅ User data refreshed');
  };

  // Notification preferences handlers
  const handleNotificationPreferenceChange = (key: string, value: any) => {
    setNotificationPreferences(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleSaveNotificationPreferences = async () => {
    // CRITICAL: Must use parsed user ID to prevent "invalid input syntax for type integer" errors
    const userId = extractNumericUserId(user);
    if (!userId || userId === 0) return;

    setNotificationPreferencesLoading(true);
    setNotificationPreferencesMessage(null);

    try {
      console.log('🔔 Saving notification preferences:', notificationPreferences);

      // Use API endpoint to bypass RLS
      const response = await fetch(`${API_BASE_URL}/api/user/update-profile`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...await ensureSessionHeaders() },
        body: JSON.stringify({
          userId,
          operation: 'update_notification_preferences',
          data: notificationPreferences
        })
      })

      const result = await response.json()
      if (!response.ok) {
        throw new Error(result.error || 'Failed to save notification preferences')
      }

      setNotificationPreferencesMessage({
        type: 'success',
        text: 'Notification preferences saved successfully!'
      });

      // Clear message after 3 seconds
      setTimeout(() => setNotificationPreferencesMessage(null), 3000);

    } catch (error: any) {
      console.error('Error saving notification preferences:', error);
      setNotificationPreferencesMessage({
        type: 'error',
        text: error.message || 'Failed to save notification preferences'
      });
    } finally {
      setNotificationPreferencesLoading(false);
    }
  };

  // Handle language preference save
  const handleSaveLanguage = async () => {
    if (!selectedLanguage) {
      setSettingsMessage({ type: 'error', text: 'Choose a language from the list.' });
      return;
    }

    setLanguageSaving(true);
    setSettingsMessage(null);

    try {
      const response = await fetch('/api/user/pref-language', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...await ensureSessionHeaders() },
        body: JSON.stringify({ operation: 'save', language: selectedLanguage })
      })

      const result = await response.json()
      if (!response.ok || result?.success === false) {
        throw new Error(result.error || 'Failed to update language');
      }

      if (isSupported(selectedLanguage)) {
        setStoredLanguage(selectedLanguage, { explicit: true });
      }

      try {
        const storedUser = localStorage.getItem('aureus_user');
        if (storedUser) {
          const parsedUser = JSON.parse(storedUser);
          parsedUser.pref_language = selectedLanguage;
          if (isSupported(selectedLanguage)) parsedUser.language_preference = selectedLanguage;
          localStorage.setItem('aureus_user', JSON.stringify(parsedUser));
        }
      } catch {
        // The database save already succeeded.
      }

      setSavedLanguage(selectedLanguage);

      setSettingsMessage({
        type: 'success',
        text: '✅ Language saved! Reloading page to apply changes...'
      });

      setLanguageChanged(false);

      // Reload page after 1.5 seconds to apply translations
      setTimeout(() => {
        console.log('🔄 Reloading page to apply language...');
        window.location.reload();
      }, 1500);

    } catch (error: any) {
      console.error('❌ Error saving language:', error);
      setSettingsMessage({
        type: 'error',
        text: `Failed to save language: ${error.message}`
      });
      setLanguageSaving(false);
    }
  };

  // Load user security settings from database - uses API endpoint
  const loadUserSecuritySettings = async (userId: number) => {
    try {
      console.log('🔐 Loading security settings for user:', userId);

      const response = await fetch(`${API_BASE_URL}/api/user/dashboard-data`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...await ensureSessionHeaders() },
        body: JSON.stringify({ userId, dataTypes: ['security_settings'] })
      })

      const result = await response.json()
      if (!response.ok) {
        console.error('❌ Error loading security settings:', result.error);
        return;
      }

      const data = result.data?.security_settings
      if (data) {
        console.log('✅ Security settings loaded:', data);

        // Update user state with current database values
        setUser((prev: any) => ({
          ...prev,
          database_user: {
            ...prev?.database_user,
            two_factor_enabled: data.two_factor_enabled,
            email_verified: data.email_verified
          }
        }));
      }
    } catch (error) {
      console.error('❌ Error in loadUserSecuritySettings:', error);
    }
  };

  // Load notification preferences from database via API
  const loadNotificationPreferences = async () => {
    // CRITICAL: Must use parsed user ID to prevent "invalid input syntax for type integer" errors
    const userId = extractNumericUserId(user);
    if (!userId || userId === 0) return;

    try {
      console.log('🔔 Loading notification preferences for user:', userId);

      // SECURITY v2.9.14: Include session headers; server derives userId from session
      const response = await fetch(`/api/user-notifications?type=preferences`, {
        headers: { ...await ensureSessionHeaders() }
      });
      const result = await response.json();

      if (!result.success) {
        console.log('ℹ️ No existing notification preferences found, using defaults');
        return;
      }

      const data = result.data;
      if (data && data.user_id) {
        // Update state with loaded preferences
        setNotificationPreferences({
          payment_confirmations: data.payment_confirmations ?? true,
          security_alerts: data.security_alerts ?? true,
          commission_updates: data.commission_updates ?? true,
          marketing_emails: data.marketing_emails ?? false,
          in_app_notifications: data.in_app_notifications ?? true,
          sound_notifications: data.sound_notifications ?? false,
          notification_frequency: data.notification_frequency ?? 'immediate'
        });
        console.log('✅ Notification preferences loaded via API:', data);
      } else {
        console.log('ℹ️ No existing notification preferences found, using defaults');
      }

    } catch (error) {
      console.error('Error loading notification preferences:', error);
    }
  };

  const loadUser = async () => {
    try {
      console.log('🔄 Loading current user...')

      // AUTO-FIX: Validate and clean corrupted localStorage data BEFORE any queries
      // This prevents "invalid input syntax for type integer: 'db_XXX'" errors
      const storedUserCheck = localStorage.getItem('aureus_user');
      if (storedUserCheck) {
        try {
          const parsedCheck = JSON.parse(storedUserCheck);

          // v7.3.x: Resolve the numeric id from ALL known locations before
          // declaring corruption. Since v4.0.57 extractNumericId correctly
          // rejects UUIDs/long strings, so a top-level id that is an auth UUID
          // (stored by some login paths) must NOT trigger a hard logout while
          // a valid numeric id still lives in database_user.id / user_metadata.
          const numericId =
            extractNumericId(parsedCheck.id) ||
            extractNumericId(parsedCheck?.database_user?.id) ||
            extractNumericId(parsedCheck?.user_metadata?.user_id);

          if (!numericId || numericId <= 0) {
            console.error('🧹 AUTO-FIX: Detected corrupted localStorage data with invalid user ID:', parsedCheck.id);
            console.log('🧹 AUTO-FIX: Clearing corrupted cache and reloading...');

            // Clear all potentially corrupted localStorage
            localStorage.removeItem('aureus_user');
            localStorage.removeItem('aureus_session');
            localStorage.removeItem('aureus_email_user');

            // Show user-friendly message and reload
            alert('Your session data was corrupted. Please log in again.');
            window.location.href = '/';
            return;
          }
        } catch (parseError) {
          console.error('🧹 AUTO-FIX: Invalid JSON in localStorage, clearing...');
          localStorage.removeItem('aureus_user');
        }
      }

      // Check for admin impersonation session first
      const urlParams = new URLSearchParams(window.location.search)
      const impersonateUserId = urlParams.get('admin_impersonate')
      const sessionId = urlParams.get('session')
      if (impersonateUserId && sessionId) {
        console.log('🔍 Admin impersonation detected:', { impersonateUserId, sessionId })

        const validation = await validateImpersonationSession(sessionId, parseInt(impersonateUserId))
        if (validation.valid && validation.user) {
          console.log('✅ Impersonation session validated, loading impersonated user')
          console.log('🔍 Impersonated user data:', validation.user)

          // Set up user object for impersonated user
          const impersonatedUser = {
            id: validation.user.id,
            email: validation.user.email,
            database_user: validation.user,
            account_type: 'impersonated',
            user_metadata: {
              user_id: validation.user.id,
              username: validation.user.username,
              full_name: validation.user.full_name,
              phone: validation.user.phone,
              is_impersonated: true
            }
          }

          console.log('🎭 Setting impersonated user:', impersonatedUser)
          setUser(impersonatedUser)

          // Load dashboard data for the impersonated user
          const numericUserId = validation.user.id
          console.log('🚀 Loading dashboard data for impersonated user ID:', numericUserId)
          // Fire dashboard data + KYC in parallel to prevent KYC banner flash
          await Promise.allSettled([
            loadDashboardData(numericUserId),
            loadKYCStatus(numericUserId),
            loadKycCompliance(numericUserId)
          ])
          await loadUserSharePurchases(numericUserId)
          await loadPendingPayments(numericUserId)
          await loadUnreadMessageCount(numericUserId)
          await loadUserInvoices(numericUserId)

          setLoading(false)
          return
        } else {
          console.error('❌ Invalid impersonation session:', validation.error)
          alert('Invalid impersonation session. Redirecting to admin dashboard.')
          window.location.href = '/admin'
          return
        }
      }

      // PRIORITY: Use the user prop passed from login if available (but not during impersonation)
      let currentUser = propUser
      let userId = null

      if (currentUser) {
        console.log('✅ Using user prop from login:', currentUser)
        userId = currentUser?.database_user?.id || currentUser?.user_metadata?.user_id
        console.log('📊 User ID from prop:', userId)
        console.log('🔍 DEBUG: User prop structure:', {
          database_user_id: currentUser?.database_user?.id,
          user_metadata_user_id: currentUser?.user_metadata?.user_id,
          database_user: currentUser?.database_user,
          user_metadata: currentUser?.user_metadata
        })
      } else {
        // Fallback: Try to get user from localStorage
        try {
          const storedUser = localStorage.getItem('aureus_user')

        if (storedUser) {
          const parsedUser = JSON.parse(storedUser)

          // FIXED: Use numeric ID directly, never create malformed "db_XXX" strings.
          // v7.3.x: resolve from all known locations so a UUID top-level id does
          // not force a logout when a valid numeric id exists elsewhere.
          const numericUserId =
            extractNumericId(parsedUser.id) ||
            extractNumericId(parsedUser?.database_user?.id) ||
            extractNumericId(parsedUser?.user_metadata?.user_id);
          if (!numericUserId) {
            console.error('❌ Invalid user ID in localStorage:', parsedUser.id);
            localStorage.removeItem('aureus_user');
            alert('Your session data was corrupted. Please log in again.');
            window.location.href = '/';
            return;
          }

          // Create proper user object for email login
          currentUser = {
            id: parsedUser.auth_user_id || numericUserId, // Use numeric ID, not "db_XXX" string
            email: parsedUser.email,
            database_user: parsedUser,
            account_type: 'email',
            user_metadata: {
              username: parsedUser.username,
              full_name: parsedUser.full_name,
              phone: parsedUser.phone,
              country_of_residence: parsedUser.country_of_residence,
              user_id: numericUserId, // Always use validated numeric ID
              is_email_user: true
            }
          }
          userId = numericUserId
          console.log('✅ Email user loaded from localStorage:', currentUser, 'User ID:', userId, 'Type:', typeof userId)
          console.log('🔍 DEBUG: parsedUser structure:', {
            id: parsedUser.id,
            auth_user_id: parsedUser.auth_user_id,
            email: parsedUser.email
          })
        }
        } catch (storageError) {
          console.log('⚠️ Could not load from localStorage, trying getCurrentUser')
        }

        // Fallback to getCurrentUser if localStorage fails
        if (!currentUser) {
        currentUser = await getCurrentUser()
        // CRITICAL FIX: Extract the correct database user ID with priority order
        userId = currentUser?.database_user?.id ||
                 currentUser?.user_metadata?.user_id

        console.log('👤 Current user loaded from getCurrentUser:', currentUser, 'User ID:', userId)
        console.log('🔍 DEBUG: User ID extraction:', {
          database_user_id: currentUser?.database_user?.id,
          user_metadata_user_id: currentUser?.user_metadata?.user_id,
          final_userId: userId,
          userId_type: typeof userId
        })
        }
      } // Close the } else { block

      setUser(currentUser)

      // Ensure we have a valid user ID for data loading
      if (!userId && currentUser) {
        userId = currentUser?.database_user?.id ||
                 currentUser?.user_metadata?.user_id ||
                 currentUser?.id
        console.log('🔧 Extracted user ID for data loading:', userId)
      }

      // IMMEDIATE FIX: Set user data from authenticated session to avoid database queries
      console.log('🔍 DEBUG: Checking user data conditions:', {
        hasCurrentUser: !!currentUser,
        hasDatabaseUser: !!currentUser?.database_user,
        hasUserMetadata: !!currentUser?.user_metadata,
        userId: userId,
        userIdType: typeof userId
      })

      // FIXED: Check for currentUser directly since database_user and user_metadata might not exist
      if (currentUser) {
        const userData = {
          username: currentUser?.database_user?.username || currentUser?.user_metadata?.username || 'User',
          full_name: currentUser?.database_user?.full_name || currentUser?.user_metadata?.full_name || 'User'
        }
        console.log('✅ User data set from session:', userData)
        setUserData(userData)

        // Set default commission data to avoid 406 errors
        setCommissionData({
          usdt_balance: 0,
          share_balance: 0,
          total_earned_usdt: 0,
          total_earned_shares: 0,
          escrowed_amount: 0,
          total_withdrawn: 0
        })
        console.log('✅ Default commission data set')

        // Set default share purchases to avoid errors
        setUserSharePurchases([])
        console.log('✅ Default share purchases set')

        // CRITICAL FIX: Set user state and load dashboard data
        console.log('🔍 DEBUG: About to check userId and currentUser:', {
          hasUserId: !!userId,
          userId: userId,
          hasCurrentUser: !!currentUser,
          userIdType: typeof userId
        })

        if (userId && currentUser) {
          console.log('🔄 Setting user state and loading dashboard data for user ID:', userId, 'Type:', typeof userId)

          // CRITICAL: Ensure userId is numeric before making database calls
          let numericUserId = null
          if (typeof userId === 'number' && !isNaN(userId)) {
            numericUserId = userId
          } else if (typeof userId === 'string') {
            // Handle "db_549" format by removing "db_" prefix
            const cleanedId = userId.replace(/^db_/, '')
            const parsed = parseInt(cleanedId, 10)
            if (!isNaN(parsed)) {
              numericUserId = parsed
            }
          }

          if (!numericUserId) {
            console.error('❌ Cannot convert user ID to numeric:', userId)
            console.log('🔍 DEBUG: User object details:', {
              currentUser,
              userId,
              userIdType: typeof userId,
              hasDatabase: !!currentUser?.database_user,
              hasMetadata: !!currentUser?.user_metadata
            })

            // Set user state and default data even without numeric ID
            setUser(currentUser)
            setDataLoading(false)
            return
          }

          console.log('✅ Using numeric user ID for database operations:', numericUserId)
          setUser(currentUser) // Set the user state so it displays in the UI

          console.log('🚀 About to call data loading functions...')

          // CRITICAL FIX: Fire loadKYCStatus in parallel with loadDashboardData
          // Previously KYC was the 6th sequential call, causing the KYC banner to
          // flash "KYC Required" for several seconds on slow connections even for
          // approved users. Now both load simultaneously.
          const [dashboardResult, kycResult] = await Promise.allSettled([
            loadDashboardData(numericUserId),
            loadKYCStatus(numericUserId),
            loadKycCompliance(numericUserId)
          ])
          console.log('✅ loadDashboardData completed:', dashboardResult.status)
          console.log('✅ loadKYCStatus completed:', kycResult.status)

          await loadUserSecuritySettings(numericUserId)
          console.log('✅ loadUserSecuritySettings completed')

          await loadUserSharePurchases(numericUserId)
          console.log('✅ loadUserSharePurchases completed')

          await loadPendingPayments(numericUserId)
          console.log('✅ loadPendingPayments completed')

          await loadUnreadMessageCount(numericUserId)
          console.log('✅ loadUnreadMessageCount completed')

          await loadUserInvoices(numericUserId)
          console.log('✅ loadUserInvoices completed')
        } else {
          console.log('⚠️ No valid user ID found for data loading')
          console.log('🔍 DEBUG: Missing data:', { userId, currentUser: !!currentUser })

          // Set user state and default data even without user ID
          if (currentUser) {
            setUser(currentUser)

            // Set default user data to prevent UI errors
            const userData = {
              username: currentUser?.database_user?.username || currentUser?.user_metadata?.username || currentUser?.email?.split('@')[0] || 'User',
              full_name: currentUser?.database_user?.full_name || currentUser?.user_metadata?.full_name || 'User'
            }
            setUserData(userData)

            // Set default commission data
            setCommissionData({
              usdt_balance: 0,
              share_balance: 0,
              total_earned_usdt: 0,
              total_earned_shares: 0,
              escrowed_amount: 0,
              total_withdrawn: 0
            })

            // Set default dashboard data
            setDashboardData({
              totalShares: 0,
              purchasedShares: 0,
              convertedShares: 0,
              commissionShares: 0,
              earnedShares: 0,
              legacyBonusShares: 0,
              sharesSent: 0,
              sharesReceived: 0,
              netTransferredShares: 0,
              shareValue: 0,
              futureDividends: 0,
              dividendEligibility: {
                firstPurchaseDate: null,
                purchasedBeforeCutoff: false,
                firstDividendDate: 'July 2026',
                firstDividendMonth: 'July' as 'July',
                firstDividendYear: 2026,
                eligibleFor2026Payouts: ['July', 'October'] as ('July' | 'October')[]
              },
              usdtCommissions: {
                totalEarned: 0,
                available: 0,
                escrowed: 0,
                converted: 0,
                displayedBalance: 0,
                computedWithdrawable: 0,
                withdrawalRequestMax: 0,
                withdrawalNetworkFee: 2,
                withdrawalBlocked: false,
                withdrawalBlockReasonCode: null,
                withdrawalBlockReasonMessage: null
              },
              shareCommissions: {
                totalShares: 0,
                currentValue: 0
              },
              accountBalance: 0,
              referralCount: 0,
              monthlyEarnings: 0,
              currentSharePrice: rememberSharePrice(null)
            })
          }
          setDataLoading(false)
        }
      } // Close the if (currentUser?.database_user || currentUser?.user_metadata) block
    } catch (error) {
      console.error('❌ Error loading user:', error)
    } finally {
      setLoading(false)
    }
  }

  // Load unread message count - uses API endpoint to bypass RLS
  const loadUnreadMessageCount = async (userId: number) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/user/dashboard-data`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...await ensureSessionHeaders() },
        body: JSON.stringify({ userId, dataTypes: ['unread_messages'] })
      })

      const result = await response.json()
      if (!response.ok) {
        console.error('❌ Error loading unread message count:', result.error)
        return
      }

      setUnreadMessageCount(result.data?.unread_messages?.count || 0)
    } catch (error) {
      console.error('❌ Error loading unread message count:', error)
    }
  }

  // Load user share purchases - uses API endpoint to bypass RLS
  const loadUserSharePurchases = async (userId: number) => {
    try {
      console.log('🔄 Loading share purchases for user:', userId)

      const response = await fetch(`${API_BASE_URL}/api/user/dashboard-data`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...await ensureSessionHeaders() },
        body: JSON.stringify({ userId, dataTypes: ['share_purchases'] })
      })

      const result = await response.json()
      if (!response.ok) {
        console.error('❌ Error loading share purchases:', result.error)
        return
      }

      const purchases = result.data?.share_purchases || []
      console.log('✅ Share purchases loaded:', purchases)
      setUserSharePurchases(purchases)
    } catch (error) {
      console.error('❌ Failed to load share purchases:', error)
    }
  }

  // Load pending, rejected, and flagged payments via server-side API (bypasses RLS)
  const loadPendingPayments = async (userId: number) => {
    try {
      setPendingPaymentsLoading(true)
      console.log('🔄 Loading pending/rejected/flagged payments for user:', userId)

      const response = await fetch(`${API_BASE_URL}/api/user/dashboard-data`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...await ensureSessionHeaders() },
        body: JSON.stringify({ userId, dataTypes: ['pending_rejected_flagged_payments'] })
      })

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`)
      }

      const result = await response.json()
      if (!result.success) {
        throw new Error(result.error || 'Failed to load payments')
      }

      const paymentData = result.data?.pending_rejected_flagged_payments
      const sharePurchases = paymentData?.share_purchases || []
      const flaggedPayments = paymentData?.flagged_payments || []

      // Normalize share purchases
      const normalizedPurchases = sharePurchases.map((p: any) => ({
        id: p.id,
        shares_purchased: p.shares_purchased,
        total_amount: p.total_amount,
        payment_method: p.payment_method,
        created_at: p.created_at,
        transaction_reference: p.transaction_reference,
        status: p.status as 'pending' | 'rejected' | 'flagged_duplicate' | 'review_requested',
        transaction_notes: p.transaction_notes
      }))

      // Normalize flagged crypto payments
      const normalizedFlagged = flaggedPayments.map((p: any) => ({
        id: p.id,
        shares_purchased: p.shares_to_purchase,
        total_amount: p.amount,
        payment_method: p.network === 'BANK_TRANSFER' ? 'Bank Transfer' : `Crypto (${p.network})`,
        created_at: p.created_at,
        transaction_reference: p.transaction_hash,
        status: p.status as 'pending' | 'rejected' | 'flagged_duplicate' | 'review_requested',
        transaction_notes: p.transaction_notes
      }))

      const allPayments = [...normalizedPurchases, ...normalizedFlagged]
        .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())

      console.log('✅ Pending/rejected/flagged payments loaded:', allPayments.length)
      setPendingPayments(allPayments)
    } catch (error) {
      console.error('❌ Failed to load pending payments:', error)
      setPendingPayments([])
    } finally {
      setPendingPaymentsLoading(false)
    }
  }

  const loadDashboardData = async (userId: number) => {
    try {
      setDataLoading(true)
      console.log('🔄 Loading dashboard data for user:', userId, typeof userId)

      // Validate userId - handle both numeric and string IDs
      let validUserId = null
      if (typeof userId === 'number' && !isNaN(userId)) {
        validUserId = userId
      } else if (typeof userId === 'string') {
        const numericId = parseInt(userId, 10)
        if (!isNaN(numericId)) {
          validUserId = numericId
        }
      }

      const defaultData = {
        totalShares: 0,
        purchasedShares: 0,
        convertedShares: 0,
        commissionShares: 0,
        earnedShares: 0,
        legacyBonusShares: 0,
        sharesSent: 0,
        sharesReceived: 0,
        netTransferredShares: 0,
        shareValue: 0,
        futureDividends: 0,
        dividendEligibility: {
          firstPurchaseDate: null as Date | null,
          purchasedBeforeCutoff: false,
          firstDividendDate: 'July 2026',
          firstDividendMonth: 'July' as 'July',
          firstDividendYear: 2026,
          eligibleFor2026Payouts: ['July', 'October'] as ('July' | 'October')[]
        },
        usdtCommissions: {
          totalEarned: 0,
          available: 0,
          escrowed: 0,
          converted: 0,
          displayedBalance: 0,
          computedWithdrawable: 0,
          withdrawalRequestMax: 0,
          withdrawalNetworkFee: 2,
          withdrawalBlocked: false,
          withdrawalBlockReasonCode: null,
          withdrawalBlockReasonMessage: null
        },
        shareCommissions: { totalShares: 0, currentValue: 0 },
        accountBalance: 0,
        referralCount: 0,
        monthlyEarnings: 0,
        currentSharePrice: rememberSharePrice(null)
      }

      if (!validUserId) {
        console.error('❌ Invalid user ID:', userId, 'Type:', typeof userId)
        setCommissionData({ usdt_balance: 0, share_balance: 0, total_earned_usdt: 0, total_earned_shares: 0, escrowed_amount: 0, total_withdrawn: 0 })
        setUserSharePurchases([])
        setDashboardData(defaultData)
        setDataLoading(false)
        return
      }

      userId = validUserId
      console.log('✅ Using validated user ID:', userId, 'Type:', typeof userId)

      // ========================================
      // FETCH DASHBOARD DATA VIA API ENDPOINT
      // ========================================
      console.log('🚀 Fetching dashboard data via API...')
      const response = await fetch(`${API_BASE_URL}/api/user-dashboard-data`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...await ensureSessionHeaders() },
        body: JSON.stringify({ userId })
      })

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`)
      }

      const result = await response.json()

      if (!result.success || !result.data) {
        throw new Error(result.error || 'Failed to load dashboard data')
      }

      const apiData = result.data
      console.log('✅ API dashboard data received:', apiData)

      // Get current share price
      const currentSharePrice = rememberSharePrice(apiData.currentSharePrice)

      // Calculate share value only at the live phase price.
      const totalShares = apiData.totalShares || 0
      const shareValue = currentSharePrice == null ? 0 : totalShares * currentSharePrice
      const priceForValue = currentSharePrice ?? 0

      // Extract breakdown data
      const breakdown = apiData.breakdown || {}
      const purchasedShares = breakdown.purchasedShares || 0
      const bonusShares = breakdown.bonusShares || 0
      const commissionShares = breakdown.commissionShares || 0
      const convertedShares = breakdown.convertedShares || 0
      const netTransferredShares = breakdown.netTransferredShares || 0

      // Commission data
      const usdtCommissions = apiData.usdtCommissions || { available: 0, pending: 0, total: 0 }
      const availableUSDT = usdtCommissions.available || 0
      const escrowedAmount = usdtCommissions.pending || 0
      const totalEarnedUSDT = usdtCommissions.total || 0
      const computedWithdrawable = usdtCommissions.computed_withdrawable_balance ?? availableUSDT
	      const displayedBalance = usdtCommissions.displayed_balance ?? availableUSDT
      const withdrawalRequestMax = usdtCommissions.withdrawal_request_max_usdt ?? Math.max(0, computedWithdrawable - 2)
      const withdrawalNetworkFee = usdtCommissions.network_fee ?? 2
      const withdrawalBlocked = Boolean(usdtCommissions.withdrawal_blocked)
      const withdrawalBlockReasonCode = usdtCommissions.withdrawal_block_reason_code || null
      const withdrawalBlockReasonMessage = usdtCommissions.withdrawal_block_reason_message || null

      // Dividend eligibility — 3-month holding rule (July 2026 cutoff: April 6, 2026)
      const firstPurchaseDate = apiData.firstPurchaseDate ? new Date(apiData.firstPurchaseDate) : null
      // Compute payout eligibility per the 3-month holding rule
      const julyCutoff2026 = new Date(Date.UTC(2026, 3, 6))
      const octoberCutoff2026 = new Date(Date.UTC(2026, 6, 6))
      const eligibleForJuly2026 = firstPurchaseDate ? firstPurchaseDate.getTime() <= julyCutoff2026.getTime() + (24*60*60*1000 - 1) : false
      const eligibleForOctober2026 = firstPurchaseDate ? firstPurchaseDate.getTime() <= octoberCutoff2026.getTime() + (24*60*60*1000 - 1) : false
      const eligible2026Payouts: ('July' | 'October')[] = []
      if (eligibleForJuly2026) eligible2026Payouts.push('July')
      if (eligibleForOctober2026) eligible2026Payouts.push('October')
      const firstDividendDateLabel = eligibleForJuly2026 ? 'July 2026' : eligibleForOctober2026 ? 'October 2026' : 'January 2027'

      // Calculate first dividend amount from production-based projections (July 2026)
      const proj2026 = DIVIDEND_PROJECTIONS[2026];
      const payouts2026 = proj2026?.quarterlyPayouts || [];
      const firstPayoutPerShare = payouts2026[0]?.amount || 0; // July payout (first available)
      const futureDividends = Math.round(totalShares * firstPayoutPerShare * 100) / 100;

      // Set commission data for other components
      setCommissionData({
        usdt_balance: availableUSDT,
        share_balance: commissionShares,
        total_earned_usdt: totalEarnedUSDT,
        total_earned_shares: commissionShares,
        escrowed_amount: escrowedAmount,
        total_withdrawn: 0
      })

      console.log('📊 Setting dashboard data from API:', { totalShares, shareValue, currentSharePrice })

      setDashboardData({
        totalShares,
        purchasedShares,
        bonusShares,
        convertedShares,
        commissionShares,
        earnedShares: commissionShares,
        legacyBonusShares: 0,
        sharesSent: netTransferredShares < 0 ? Math.abs(netTransferredShares) : 0,
        sharesReceived: netTransferredShares > 0 ? netTransferredShares : 0,
        netTransferredShares,
        shareValue,
        futureDividends,
        dividendEligibility: {
          firstPurchaseDate,
          purchasedBeforeCutoff: eligibleForJuly2026, // 3-month holding rule: April 6, 2026 cutoff
          firstDividendDate: firstDividendDateLabel,
          firstDividendMonth: 'July',
          firstDividendYear: eligibleForJuly2026 || eligibleForOctober2026 ? 2026 : 2027,
          eligibleFor2026Payouts: eligible2026Payouts
        },
        usdtCommissions: {
          totalEarned: totalEarnedUSDT,
          available: availableUSDT,
          escrowed: escrowedAmount,
          converted: 0,
          displayedBalance,
          computedWithdrawable,
          withdrawalRequestMax,
          withdrawalNetworkFee,
          withdrawalBlocked,
          withdrawalBlockReasonCode,
          withdrawalBlockReasonMessage
        },
        shareCommissions: {
          totalShares: commissionShares,
          currentValue: commissionShares * priceForValue
        },
        accountBalance: availableUSDT,
        referralCount: 0, // Will be loaded separately if needed
        monthlyEarnings: (totalEarnedUSDT + (commissionShares * priceForValue)) / 12,
        currentSharePrice
      })

      console.log('✅ Dashboard data loaded from API:', { totalShares, shareValue, currentSharePrice })

    } catch (error: any) {
      console.error('❌ Error loading dashboard data:', error)
      // Show error to user instead of silently resetting to 0
      // Only reset data if dashboardData hasn't been loaded yet (first load)
      setMessage({
        type: 'error',
        text: 'Failed to load your dashboard data. Please refresh the page. If the issue persists, please contact support.'
      })
      setTimeout(() => setMessage(null), 10000)
    } finally {
      setDataLoading(false)
    }
  }

  const loadKycCompliance = async (userId: number) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/user/kyc-compliance`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...await ensureSessionHeaders() },
        body: JSON.stringify({})
      })
      const result = await response.json()
      if (!response.ok || result.lookup_failed || result.ok !== true) {
        setKycNotice({ notice_state: 'none', rejection_reason: null, lookup_failed: true })
        setKycNoticeDismissed(false)
        return
      }
      setKycNotice({
        notice_state: result.notice_state,
        rejection_reason: result.rejection_reason || null,
        lookup_failed: false
      })
      if (result.notice_state === 'dismissible' && localStorage.getItem(`aureus_kyc_notice_dismissed_${userId}`) === '1') {
        setKycNoticeDismissed(true)
      } else if (result.notice_state === 'pending_banner' && sessionStorage.getItem(`aureus_kyc_pending_banner_${userId}`) === '1') {
        setKycNoticeDismissed(true)
      } else {
        setKycNoticeDismissed(false)
      }
    } catch (error) {
      console.error('[USER-DASHBOARD] KYC compliance lookup failed open:', error)
      setKycNotice({ notice_state: 'none', rejection_reason: null, lookup_failed: true })
    }
  }

  const loadKYCStatus = async (userId: number) => {
    try {
      console.log('🔍 [USER-DASHBOARD] Loading KYC status for user:', userId)

      // CRITICAL FIX: Use server-side API endpoint with service_role client
      // instead of direct anon client query which can silently fail due to
      // RLS/session issues, causing completed KYC users to see "not_started"
      const response = await fetch(`${API_BASE_URL}/api/kyc-status?userId=${userId}`, {
        headers: { ...await ensureSessionHeaders() }
      })
      const result = await response.json()

      if (!response.ok) {
        console.error('❌ [USER-DASHBOARD] KYC API error:', result)
        setKycStatus('not_started')
        setKycId(null)
        setKycLoaded(true)
        return
      }

      if (result.success) {
        const finalStatus = result.kycStatus || 'not_started'
        console.log('✅ [USER-DASHBOARD] KYC status loaded via API:', {
          rawStatus: result.rawStatus,
          finalStatus,
          kycId: result.kycId
        })
        setKycStatus(finalStatus as 'pending' | 'approved' | 'rejected' | 'not_started')
        setKycId(result.kycId || null)
      } else {
        console.warn('⚠️ [USER-DASHBOARD] KYC API returned unsuccessful:', result)
        setKycStatus('not_started')
        setKycId(null)
      }
    } catch (error) {
      console.error('❌ [USER-DASHBOARD] Error loading KYC status:', error)
      setKycStatus('not_started')
      setKycId(null)
    } finally {
      setKycLoaded(true)
    }
  }

  // Check if user has invoices for conditional navigation rendering - uses API endpoint
  const loadUserInvoices = async (userId: number) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/user/dashboard-data`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...await ensureSessionHeaders() },
        body: JSON.stringify({ userId, dataTypes: ['invoices'] })
      })

      const result = await response.json()
      if (!response.ok) {
        console.error('❌ Error checking user invoices:', result.error)
        setHasInvoices(false)
        return
      }

      console.log('📄 User has invoices:', result.data?.invoices?.hasInvoices)
      setHasInvoices(result.data?.invoices?.hasInvoices === true)
    } catch (err) {
      console.error('❌ Error loading user invoices:', err)
      setHasInvoices(false)
    }
  }

  const handleLogout = async () => {
    await signOut()
    onLogout()
  }

  if ((loading || isCheckingCompliance) && !isMasterAdminViewSession()) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0f1419]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500 mx-auto mb-4"></div>
          <p className="text-white">
            {isCheckingCompliance ? 'Checking legal document compliance...' : 'Loading your dashboard...'}
          </p>
        </div>
      </div>
    )
  }

  // Sponsor assignment blocking overlay
  if (mustAddSponsor && !isMasterAdminViewSession()) {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#050505',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
      }}>
        <div style={{
          background: 'rgba(18, 18, 18, 0.95)',
          border: '1px solid rgba(255, 215, 0, 0.3)',
          borderRadius: '16px',
          padding: '40px',
          maxWidth: '480px',
          width: '100%',
          textAlign: 'center',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.6)'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>🤝</div>
          <h2 style={{ color: '#FFD700', fontSize: '24px', fontWeight: '700', marginBottom: '8px' }}>
            Add Your Sponsor
          </h2>
          <p style={{ color: '#B0B0B0', fontSize: '14px', marginBottom: '24px', lineHeight: '1.6' }}>
            Every Aureus Africa member needs a sponsor. Please enter the username of the person who referred you.
          </p>

          <div style={{ marginBottom: '16px' }}>
            <input
              type="text"
              value={sponsorUsername}
              onChange={(e) => {
                setSponsorUsername(e.target.value)
                setSponsorError(null)
                setSponsorSuccess(null)
              }}
              placeholder="Enter sponsor username"
              style={{
                width: '100%',
                padding: '12px 16px',
                backgroundColor: 'rgba(31, 41, 55, 0.8)',
                border: sponsorError ? '1px solid #EF4444' : sponsorSuccess ? '1px solid #22C55E' : '1px solid rgba(255, 215, 0, 0.2)',
                borderRadius: '8px',
                color: '#FFFFFF',
                fontSize: '16px',
                outline: 'none',
                boxSizing: 'border-box'
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSponsorValidate()
              }}
            />
          </div>

          {sponsorError && (
            <p style={{ color: '#EF4444', fontSize: '13px', marginBottom: '12px' }}>
              ❌ {sponsorError}
            </p>
          )}
          {sponsorSuccess && (
            <p style={{ color: '#22C55E', fontSize: '13px', marginBottom: '12px' }}>
              ✅ {sponsorSuccess}
            </p>
          )}

          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
            {!sponsorSuccess ? (
              <button
                onClick={handleSponsorValidate}
                disabled={sponsorValidating || !sponsorUsername.trim()}
                style={{
                  padding: '12px 32px',
                  backgroundColor: sponsorValidating || !sponsorUsername.trim() ? 'rgba(255, 215, 0, 0.3)' : '#FFD700',
                  color: '#000000',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: sponsorValidating || !sponsorUsername.trim() ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease'
                }}
              >
                {sponsorValidating ? 'Validating...' : 'Validate Sponsor'}
              </button>
            ) : (
              <button
                onClick={handleSponsorSubmit}
                disabled={sponsorSubmitting}
                style={{
                  padding: '12px 32px',
                  backgroundColor: sponsorSubmitting ? 'rgba(34, 197, 94, 0.3)' : '#22C55E',
                  color: '#FFFFFF',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: sponsorSubmitting ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease'
                }}
              >
                {sponsorSubmitting ? 'Assigning...' : 'Confirm Sponsor'}
              </button>
            )}
          </div>
        </div>
      </div>
    )
  }

  // v4.0.57: Locked wallet registration splash DEFERRED — users can continue without locked wallet
  // Will be re-enforced at a later stage with better UX
  // The blocking overlay has been removed. Users can still voluntarily register wallets
  // from the Settings section. Admin wallet verification badges remain active.

  return (
    <MasterAdminViewWrapper>
      <div style={{
        minHeight: '100vh',
        background: '#050505',
        color: '#FFFFFF',
        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
      }}>
        {/* Admin Impersonation Indicator */}
        <ImpersonationIndicator />

        {!isMasterAdminViewSession() && kycNotice && !kycNotice.lookup_failed && kycNotice.notice_state === 'wall' && activeSection !== 'kyc' && activeSection !== 'legal-documents' && (
          <KycRequiredNotice
            mode="wall"
            rejectionReason={kycNotice.rejection_reason}
            onContinueToKyc={() => setActiveSection('kyc')}
            onLogout={handleLogout}
          />
        )}
        {!isMasterAdminViewSession() && kycNotice && !kycNotice.lookup_failed && kycNotice.notice_state === 'dismissible' && !kycNoticeDismissed && (
          <KycRequiredNotice
            mode="dismissible"
            rejectionReason={kycNotice.rejection_reason}
            onClose={() => {
              const uid = extractNumericUserId(user)
              if (uid) localStorage.setItem(`aureus_kyc_notice_dismissed_${uid}`, '1')
              setKycNoticeDismissed(true)
            }}
          />
        )}
        {!isMasterAdminViewSession() && kycNotice && !kycNotice.lookup_failed && kycNotice.notice_state === 'pending_banner' && !kycNoticeDismissed && (
          <KycRequiredNotice
            mode="pending_banner"
            onClose={() => {
              const uid = extractNumericUserId(user)
              if (uid) sessionStorage.setItem(`aureus_kyc_pending_banner_${uid}`, '1')
              setKycNoticeDismissed(true)
            }}
          />
        )}
        {!isMasterAdminViewSession() && kycNotice?.notice_state === 'wall' && activeSection === 'kyc' && (
          <div
            role="status"
            style={{
              margin: '12px 16px 0',
              padding: '10px 14px',
              borderRadius: 8,
              background: 'rgba(202, 138, 4, 0.12)',
              border: '1px solid rgba(250, 204, 21, 0.35)',
              color: '#FEF3C7',
              fontSize: 14
            }}
          >
            Complete KYC verification to unlock purchases and withdrawals. Other sections stay locked until this is submitted.
          </div>
        )}

        {/* ACRES Promo — shown only on the shareholder dashboard, hidden while
            already on the ACRES section, gated server-side by segment + cooldown.
            v4.7.78: client-side user-106 gate restored while pre-rollout testing
            is in progress. The acres_promo_banner_config kill-switch still
            applies on top of this gate. */}
        {activeSection !== 'acres' && !mustCompleteLegalDocuments && !mustAddSponsor && extractNumericId(user?.database_user?.id || user?.id) === 106 && (
          <>
            <AcresPromoBanner onNavigateToAcres={() => handleSectionChange('acres' as DashboardSection)} />
            <AcresPromoModal onNavigateToAcres={() => handleSectionChange('acres' as DashboardSection)} />
          </>
        )}

      {/* Mobile Header - Visible only on mobile */}
      <div className="lg:hidden bg-gray-800 border-b border-gray-700 sticky top-0 z-50" style={{
        backgroundColor: 'rgba(18, 18, 18, 0.9)',
        backdropFilter: 'blur(16px)',
        borderBottom: '1px solid #1E1E1E'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <img
              src="https://fgubaqoftdeefcakejwu.supabase.co/storage/v1/object/public/assets/logo-s.png"
              alt="Aureus Alliance Holdings"
              style={{
                width: '32px',
                height: '32px'
              }}
            />
            <span style={{ fontWeight: 'bold', fontSize: '18px', color: '#FFFFFF' }}>Aureus Alliance</span>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            style={{
              padding: '8px',
              borderRadius: '8px',
              backgroundColor: '#121212',
              border: '1px solid #1E1E1E',
              color: '#FFFFFF',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            aria-label="Toggle menu"
            onMouseEnter={(e) => (e.target as HTMLElement).style.backgroundColor = '#1E1E1E'}
            onMouseLeave={(e) => (e.target as HTMLElement).style.backgroundColor = '#121212'}
          >
            <svg style={{ width: '24px', height: '24px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {isMobileMenuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>
      </div>

      {/* Mobile Navigation Menu */}
      {isMobileMenuOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setIsMobileMenuOpen(false)}
        >
          <div
            className="fixed inset-y-0 left-0 w-80 bg-gray-800 z-50 shadow-xl border-r border-gray-700"
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              {/* Mobile Menu Header with Close Button */}
              <div className="p-4 border-b border-gray-700 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <img
                    src="https://fgubaqoftdeefcakejwu.supabase.co/storage/v1/object/public/assets/logo-s.png"
                    alt="Aureus Alliance Holdings"
                    className="w-10 h-10"
                  />
                  <span className="text-xl font-bold text-white">Aureus Alliance</span>
                </div>
                <button
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="p-2 rounded-lg bg-gray-700 hover:bg-gray-600 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Mobile Dashboard Switcher */}
              {onSwitchDashboard && (
                <div className="p-4 border-b border-gray-700">
                  <DashboardSwitcher
                    currentDashboard="shareholder"
                    onSwitch={onSwitchDashboard}
                    user={user}
                  />
                </div>
              )}

              {/* Mobile Navigation */}
              <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
                {getNavigationItems().map((item) => {
                  const IconComponent = item.icon
                  const isActive = activeSection === item.id
                  const isLocked = (mustCompleteLegalDocuments && item.id !== 'legal-documents' && !isMasterAdminViewSession()) || (mustCompleteSettings && item.id !== 'settings' && !isMasterAdminViewSession()) || (mustAddSponsor && !isMasterAdminViewSession()) || (kycNotice?.notice_state === 'wall' && item.id !== 'kyc' && item.id !== 'legal-documents' && !isMasterAdminViewSession())

                  return (
                    <div
                      key={item.id}
                      onClick={() => handleSectionChange(item.id)}
                      className={`
                        group relative p-3 rounded-lg cursor-pointer transition-all duration-200
                        ${isActive
                          ? 'bg-blue-600/20 border border-blue-500/30 text-blue-400'
                          : isLocked
                          ? 'bg-gray-800/50 text-gray-500 cursor-not-allowed opacity-60'
                          : 'hover:bg-gray-700/50 text-gray-300 hover:text-white'
                        }
                        ${item.highlight ? 'ring-2 ring-yellow-500/30' : ''}
                      `}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`
                          p-2 rounded-lg transition-colors
                          ${isActive ? 'bg-blue-500/20' : 'bg-gray-700/50 group-hover:bg-gray-600/50'}
                        `}>
                          <IconComponent />
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <span className="font-medium text-sm truncate">{item.labelKey ? t(item.labelKey, item.label) : item.label}</span>
                            {isLocked && (
                              <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                              </svg>
                            )}
                            {!isLocked && item.badge !== undefined && item.badge > 0 && (
                              <span className="bg-red-500 text-white text-sm px-2 py-1 rounded-full ml-2">
                                {item.badge}
                              </span>
                            )}
                            {!isLocked && item.isNew && !localStorage.getItem(`aureus_visited_${item.id}`) && (
                              <span className="bg-green-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full ml-2 uppercase tracking-wide">
                                {t('sidebar.badge.new', 'New')}
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-white mt-1 truncate">
                            {isLocked ? t('sidebar.lockedDescription', 'Complete legal documents to access') : (item.descKey ? t(item.descKey, item.description) : item.description)}
                          </p>
                        </div>
                      </div>

                      {item.highlight && (
                        <div className="absolute -top-1 -right-1 w-3 h-3 bg-yellow-500 rounded-full animate-pulse"></div>
                      )}
                    </div>
                  )
                })}
              </nav>

              {/* Mobile User Info */}
              <div className="p-4 border-t border-gray-700">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <ProfilePictureUpload
                      currentImageUrl={user?.database_user?.profile_image_url}
                      userId={(() => {
                        let userId = user?.database_user?.id || user?.id;
                        if (typeof userId === 'string' && userId.startsWith('db_')) {
                          userId = parseInt(userId.replace('db_', ''));
                        }
                        return userId;
                      })()}
                      onImageUpdate={handleProfilePictureUpdate}
                      size="small"
                      editable={false}
                      userInitials={(user?.username || user?.email)?.charAt(0).toUpperCase() || 'U'}
                      userName={user?.username || user?.email?.split('@')[0] || 'User'}
                    />
                    <div>
                      <p className="text-white text-sm font-medium">
                        {user?.username || user?.email?.split('@')[0]}
                      </p>
                      <p className="text-white text-sm">{t('dashboard.userStatus.active', 'Active')}</p>
                    </div>
                  </div>
                  <button
                    onClick={onLogout}
                    className="p-2 rounded-lg bg-gray-700 hover:bg-gray-600 transition-colors"
                    title={t('dashboard.header.logout', 'Logout')}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', minHeight: '100vh', maxWidth: '100vw', overflowX: 'hidden' }} className="mobile-container">
        {/* Desktop Sidebar - Hidden on mobile */}
        <div style={{
          display: window.innerWidth >= 1024 ? 'flex' : 'none',
          width: '320px',
          backgroundColor: '#121212',
          borderRight: '1px solid #1E1E1E',
          flexDirection: 'column',
          backdropFilter: 'blur(16px)',
          boxShadow: '0px 0px 24px rgba(255, 215, 0, 0.15)'
        }}>
          {/* Desktop Logo */}
          <div style={{ padding: '24px', borderBottom: '1px solid #1E1E1E' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <img
                src="https://fgubaqoftdeefcakejwu.supabase.co/storage/v1/object/public/assets/logo-s.png"
                alt="Aureus Alliance Holdings"
                style={{
                  width: '40px',
                  height: '40px'
                }}
              />
              <span style={{ fontSize: '20px', fontWeight: 'bold', color: '#FFFFFF' }}>Aureus Alliance</span>
            </div>
          </div>

          {/* Dashboard Switcher */}
          {onSwitchDashboard && (
            <div style={{ padding: '16px', borderBottom: '1px solid #1E1E1E' }}>
              <DashboardSwitcher
                currentDashboard="shareholder"
                onSwitch={onSwitchDashboard}
                user={user}
              />
            </div>
          )}



        {/* Enhanced Navigation */}
        <nav style={{
          flex: 1,
          padding: '16px',
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: '8px'
        }}>
          {getNavigationItems().map((item) => {
            const IconComponent = item.icon
            const isActive = activeSection === item.id
            const isLocked = (mustCompleteLegalDocuments && item.id !== 'legal-documents' && !isMasterAdminViewSession()) || (mustCompleteSettings && item.id !== 'settings' && !isMasterAdminViewSession()) || (mustAddSponsor && !isMasterAdminViewSession()) || (kycNotice?.notice_state === 'wall' && item.id !== 'kyc' && item.id !== 'legal-documents' && !isMasterAdminViewSession())

            return (
              <div
                key={item.id}
                onClick={() => handleSectionChange(item.id)}
                style={{
                  position: 'relative',
                  padding: '12px',
                  borderRadius: '8px',
                  cursor: isLocked ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease',
                  backgroundColor: isActive ? 'rgba(255, 215, 0, 0.1)' : 'transparent',
                  border: isActive ? '1px solid rgba(255, 215, 0, 0.3)' : '1px solid transparent',
                  color: isActive ? '#FFD700' : isLocked ? '#666666' : '#B0B0B0',
                  opacity: isLocked ? 0.6 : 1,
                  boxShadow: item.highlight ? '0 0 0 2px rgba(255, 215, 0, 0.3)' : 'none'
                }}
                onMouseEnter={(e) => {
                  if (!isActive && !isLocked) {
                    const target = e.target as HTMLElement;
                    target.style.backgroundColor = 'rgba(30, 30, 30, 0.5)'
                    target.style.color = '#FFFFFF'
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActive && !isLocked) {
                    const target = e.target as HTMLElement;
                    target.style.backgroundColor = 'transparent'
                    target.style.color = '#B0B0B0'
                  }
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{
                    padding: '8px',
                    borderRadius: '8px',
                    backgroundColor: isActive ? 'rgba(255, 215, 0, 0.2)' : 'rgba(30, 30, 30, 0.5)',
                    transition: 'all 0.2s ease'
                  }}>
                    <IconComponent />
                  </div>

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span style={{ fontWeight: '500', fontSize: '14px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.labelKey ? t(item.labelKey, item.label) : item.label}</span>
                      {item.badge !== undefined && item.badge > 0 && (
                        <span style={{
                          backgroundColor: '#dc2626',
                          color: 'white',
                          fontSize: '14px',
                          padding: '4px 8px',
                          borderRadius: '20px',
                          marginLeft: '8px'
                        }}>
                          {item.badge}
                        </span>
                      )}
                      {item.isNew && !localStorage.getItem(`aureus_visited_${item.id}`) && (
                        <span style={{
                          backgroundColor: '#16a34a',
                          color: 'white',
                          fontSize: '10px',
                          fontWeight: '700',
                          padding: '2px 6px',
                          borderRadius: '10px',
                          marginLeft: '8px',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                          {t('sidebar.badge.new', 'New')}
                        </span>
                      )}
                    </div>
                    <p style={{
                      fontSize: '14px',
                      color: '#ffffff',
                      marginTop: '4px',
                      margin: 0,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>{item.descKey ? t(item.descKey, item.description) : item.description}</p>
                  </div>
                </div>

                {item.highlight && (
                  <div style={{
                    position: 'absolute',
                    top: '-4px',
                    right: '-4px',
                    width: '12px',
                    height: '12px',
                    backgroundColor: '#FFD700',
                    borderRadius: '50%',
                    animation: 'pulse 2s infinite'
                  }}></div>
                )}
              </div>
            )
          })}
        </nav>

        {/* User Info at Bottom */}
        <div style={{ padding: '16px', borderTop: '1px solid #1E1E1E' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <ProfilePictureUpload
                currentImageUrl={user?.database_user?.profile_image_url}
                userId={user?.database_user?.id || user?.id}
                onImageUpdate={handleProfilePictureUpdate}
                size="small"
                editable={false}
                userInitials={(user?.username || user?.email)?.charAt(0).toUpperCase() || 'U'}
                userName={user?.username || user?.email?.split('@')[0] || 'User'}
              />
              <div>
                <p style={{ color: '#FFFFFF', fontSize: '14px', fontWeight: '500', margin: 0 }}>
                  {user?.username || user?.email?.split('@')[0]}
                </p>
                <p style={{ color: '#B0B0B0', fontSize: '14px', margin: 0 }}>{t('dashboard.userStatus.active', 'Active')}</p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              style={{
                background: 'none',
                border: 'none',
                color: '#B0B0B0',
                cursor: 'pointer',
                padding: '4px',
                transition: 'color 0.2s ease'
              }}
              title={t('dashboard.header.logout', 'Logout')}
              onMouseEnter={(e) => (e.target as HTMLElement).style.color = '#FFD700'}
              onMouseLeave={(e) => (e.target as HTMLElement).style.color = '#B0B0B0'}
            >
              <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div style={{ flex: 1, padding: '32px', backgroundColor: '#050505', maxWidth: '100%', overflowX: 'hidden', boxSizing: 'border-box' }} className="mobile-dashboard-content">
        {/* General Messages */}
        {message && (
          <div className={`rounded-lg p-4 mb-6 ${
            message.type === 'success'
              ? 'bg-green-900/20 border border-green-500/30 text-green-300'
              : message.type === 'warning'
              ? 'bg-yellow-900/20 border border-yellow-500/30 text-yellow-300'
              : 'bg-red-900/20 border border-red-500/30 text-red-300'
          }`}>
            <p className="text-sm">
              {message.type === 'success' ? '✅' : message.type === 'warning' ? '⚠️' : '❌'} {message.text}
            </p>
          </div>
        )}

        {/* Header */}
        <div className="user-dashboard-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px', position: 'relative', flexWrap: 'nowrap' }}>
          <div className="user-dashboard-header-left" style={{ display: 'flex', alignItems: 'center', gap: '20px', flex: '0 0 auto', position: 'relative', zIndex: 2 }}>
            {/* Profile Picture */}
            <ProfilePictureUploadEnhanced
              currentImageUrl={user?.database_user?.profile_image_url}
              userId={(() => {
                let userId = user?.database_user?.id || user?.id;
                if (typeof userId === 'string' && userId.includes('db_')) {
                  // Remove ALL occurrences of "db_" prefix (handles malformed IDs)
                  userId = parseInt(userId.replace(/db_/g, ''), 10);
                }
                return userId;
              })()}
              onImageUpdate={handleProfilePictureUpdate}
              size="large"
              autoSave={false}
              showSaveButton={true}
              editable={true}
              userInitials={(
                user?.database_user?.full_name ||
                user?.user_metadata?.full_name ||
                user?.database_user?.first_name ||
                user?.username ||
                user?.email?.split('@')[0] ||
                'User'
              ).charAt(0).toUpperCase()}
              userName={
                user?.database_user?.full_name ||
                user?.user_metadata?.full_name ||
                user?.database_user?.first_name ||
                user?.username ||
                user?.email?.split('@')[0] ||
                'User'
              }
            />

            {/* Welcome Text */}
            <div className="user-dashboard-header-text">
              <h1 style={{ fontSize: '32px', fontWeight: 'bold', color: '#FFFFFF', margin: 0 }}>{t('dashboard.header.title', 'Dashboard')}</h1>
              <p style={{ color: '#B0B0B0', marginTop: '8px', margin: 0 }}>
                {t('dashboard.header.welcome', 'Welcome back, {{name}}!', {
                  name: user?.database_user?.full_name ||
                    user?.user_metadata?.full_name ||
                    user?.database_user?.first_name ||
                    user?.username ||
                    user?.email?.split('@')[0] ||
                    'User'
                })}
              </p>
              <p style={{ color: '#ffffff', fontSize: '14px', marginTop: '4px', margin: 0 }}>
                {t('dashboard.header.profileHint', 'Click your profile picture to update it')}
              </p>
            </div>
          </div>
          <div className="user-dashboard-header-pcap-slot" aria-hidden={false}>
            <div className="user-dashboard-header-pcap-abs">
              <ProductionCapitalCountdown compact header />
            </div>
          </div>
          <div className="user-dashboard-header-right" style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'nowrap', flex: '0 0 auto', justifyContent: 'flex-end', position: 'relative', zIndex: 2 }}>
            {/* Language Flag Selector (authenticated) — persists to DB via update-profile */}
            <LanguageFlagSelector variant="header" authenticated={true} />
            {/* Notification Badge with Dropdown */}
            <div style={{ position: 'relative' }}>
              <NotificationBadge
                userId={(() => {
                  let userId = user?.database_user?.id || user?.id;
                  if (typeof userId === 'string' && userId.includes('db_')) {
                    // Remove ALL occurrences of "db_" prefix (handles malformed IDs)
                    userId = parseInt(userId.replace(/db_/g, ''), 10);
                  }
                  return userId;
                })()}
                onClick={() => setShowNotificationDropdown(!showNotificationDropdown)}
                className="p-2 bg-gray-800 rounded-lg border border-gray-700 hover:bg-gray-700 transition-colors"
              />

              <NotificationDropdown
                userId={(() => {
                  let userId = user?.database_user?.id || user?.id;
                  if (typeof userId === 'string' && userId.startsWith('db_')) {
                    userId = parseInt(userId.replace('db_', ''));
                  }
                  return userId;
                })()}
                isOpen={showNotificationDropdown}
                onClose={() => setShowNotificationDropdown(false)}
                onViewAll={() => {
                  setActiveSection('notifications')
                  setShowNotificationDropdown(false)
                }}
              />
            </div>

            {/* Premium Logout Button */}
            <button
              onClick={handleLogout}
              style={{
                backgroundColor: '#121212',
                border: '1px solid #FFD700',
                color: '#FFD700',
                padding: '12px 20px',
                borderRadius: '12px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'all 0.3s ease',
                boxShadow: '0 0 18px #FFD70066'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#FFD700';
                e.currentTarget.style.color = '#000000';
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 0 28px #FFD70088';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#121212';
                e.currentTarget.style.color = '#FFD700';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 0 18px #FFD70066';
              }}
              title={t('dashboard.header.logoutTitle', 'Sign out of your account')}
            >
              <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              {t('dashboard.header.logout', 'Logout')}
            </button>
          </div>
        </div>



        {/* Sponsor Assignment Blocking Screen */}
        {mustAddSponsor && !isMasterAdminViewSession() ? (
          <div className="flex items-center justify-center min-h-[60vh]">
            <div className="bg-gray-800 rounded-2xl border border-yellow-500/30 p-8 max-w-lg w-full shadow-lg" style={{ boxShadow: '0 0 30px rgba(255, 215, 0, 0.1)' }}>
              {/* Header */}
              <div className="text-center mb-8">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-yellow-500/10 flex items-center justify-center">
                  <span className="text-3xl">👤</span>
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">Who Referred You?</h2>
                <p className="text-white text-sm">
                  Every Aureus Africa member must have a sponsor. Please enter the username of the person who referred you.
                </p>
              </div>

              {/* Search Input */}
              <div className="mb-4">
                <label className="block text-gray-300 text-sm font-medium mb-2">Sponsor Username</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={sponsorUsername}
                    onChange={(e) => {
                      setSponsorUsername(e.target.value)
                      // Reset validation when username changes
                      if (validatedSponsorId) {
                        setValidatedSponsorId(null)
                        setValidatedSponsorName(null)
                        setSponsorSuccess(null)
                      }
                    }}
                    onKeyDown={(e) => { if (e.key === 'Enter' && !sponsorValidating) handleSponsorValidate() }}
                    placeholder="Enter sponsor's username"
                    className="flex-1 px-4 py-3 bg-gray-900 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500/50 transition-colors"
                    disabled={sponsorValidating || sponsorSubmitting}
                  />
                  <button
                    onClick={handleSponsorValidate}
                    disabled={sponsorValidating || !sponsorUsername.trim() || sponsorSubmitting}
                    className="px-5 py-3 bg-yellow-600 hover:bg-yellow-500 disabled:bg-gray-600 disabled:cursor-not-allowed text-black font-semibold rounded-lg transition-colors whitespace-nowrap"
                  >
                    {sponsorValidating ? '🔍 Searching...' : '🔍 Search'}
                  </button>
                </div>
              </div>

              {/* Error Message */}
              {sponsorError && (
                <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm flex items-center gap-2">
                  <span>❌</span> {sponsorError}
                </div>
              )}

              {/* Validated Sponsor Card */}
              {validatedSponsorId && validatedSponsorName && sponsorSuccess && (
                <div className="mb-6">
                  <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                        <span className="text-lg">✅</span>
                      </div>
                      <div>
                        <p className="text-green-400 font-semibold">{validatedSponsorName}</p>
                        <p className="text-green-400/70 text-sm">@{sponsorUsername.trim()}</p>
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={handleSponsorSubmit}
                    disabled={sponsorSubmitting}
                    className="w-full py-3 bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-400 hover:to-yellow-500 disabled:from-gray-600 disabled:to-gray-600 disabled:cursor-not-allowed text-black font-bold rounded-lg transition-all text-lg"
                    style={{ boxShadow: sponsorSubmitting ? 'none' : '0 0 20px rgba(255, 215, 0, 0.3)' }}
                  >
                    {sponsorSubmitting ? '⏳ Assigning Sponsor...' : '✅ Confirm Sponsor'}
                  </button>
                </div>
              )}

              {/* Success Message (brief flash before redirect) */}
              {sponsorSuccess && !validatedSponsorId && (
                <div className="mb-4 p-3 bg-green-500/10 border border-green-500/30 rounded-lg text-green-400 text-sm flex items-center gap-2">
                  <span>✅</span> {sponsorSuccess}
                </div>
              )}

              {/* Help Text */}
              <div className="mt-6 pt-4 border-t border-gray-700">
                <p className="text-gray-500 text-sm text-center">
                  💡 Don't know your sponsor's username? Contact the person who invited you to Aureus Africa and ask for their username.
                </p>
              </div>
            </div>
          </div>
        ) : activeSection === 'notifications' ? (
          <NotificationCenter
            userId={(() => {
              let userId = user?.database_user?.id || user?.id;
              if (typeof userId === 'string' && userId.startsWith('db_')) {
                userId = parseInt(userId.replace('db_', ''));
              }
              return userId;
            })()}
            className="bg-gray-800 rounded-lg border border-gray-700 p-6"
          />
        ) : activeSection === 'messages' ? (
          <div className="bg-gray-800 rounded-lg border border-gray-700 p-6 text-center">
            <div className="text-6xl mb-4">💬</div>
            <h2 className="text-2xl font-bold text-white mb-4">Messages</h2>
            <p className="text-white mb-6">
              Your messages are available in the Affiliate Dashboard where you can communicate with your team members and sponsors.
            </p>
            <button
              onClick={() => {
                window.history.pushState({}, '', '/dashboard?section=messages');
                if (onSwitchDashboard) onSwitchDashboard('affiliate');
              }}
              className="px-6 py-3 bg-gradient-to-r from-yellow-500 to-yellow-600 text-black font-semibold rounded-lg hover:from-yellow-400 hover:to-yellow-500 transition-all"
            >
              📬 Open Messages in Affiliate Dashboard
            </button>
          </div>

        ) : activeSection === 'updates' ? (
          <UpdatesPage audience="shareholder" />
        ) : activeSection === 'overview' ? (
          <>
            {/* KYC Requirement Banner — only show after KYC status has been fetched from API */}
            {kycLoaded && kycStatus !== 'approved' && (
              <div style={{
                backgroundColor: kycStatus === 'pending' ? 'rgba(245, 158, 11, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                border: `2px solid ${kycStatus === 'pending' ? '#f59e0b' : '#ef4444'}`,
                borderRadius: '12px',
                padding: '20px',
                marginBottom: '30px',
                display: 'flex',
                alignItems: 'center',
                gap: '16px'
              }}>
                <div style={{
                  fontSize: '40px',
                  flexShrink: 0
                }}>
                  {kycStatus === 'pending' ? '⏳' : kycStatus === 'rejected' ? '❌' : '🔒'}
                </div>
                <div style={{ flex: 1 }}>
                  <h3 style={{
                    color: kycStatus === 'pending' ? '#f59e0b' : '#ef4444',
                    fontSize: '18px',
                    fontWeight: 'bold',
                    marginBottom: '8px'
                  }}>
                    {kycStatus === 'pending' ? 'KYC Verification Pending' : kycStatus === 'rejected' ? 'KYC Verification Rejected' : 'KYC Verification Required'}
                  </h3>
                  <p style={{
                    color: '#ffffff',
                    fontSize: '14px',
                    marginBottom: '12px',
                    lineHeight: '1.5'
                  }}>
                    {kycStatus === 'pending'
                      ? 'Your KYC submission is being reviewed by our admin team. You can purchase shares while we review your documents. This usually takes 24-48 hours.'
                      : kycStatus === 'rejected'
                      ? 'Your KYC submission was rejected. Please review the feedback and resubmit your documents. You can still purchase shares while updating your KYC.'
                      : 'Complete KYC verification to make withdrawals. You can purchase shares without KYC verification.'}
                  </p>
                  {kycStatus !== 'pending' && (
                    <button
                      onClick={() => setActiveSection('kyc')}
                      style={{
                        backgroundColor: '#3b82f6',
                        color: 'white',
                        border: 'none',
                        padding: '10px 20px',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        transition: 'background-color 0.2s'
                      }}
                      onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#2563eb'}
                      onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#3b82f6'}
                    >
                      {kycStatus === 'rejected' ? 'Resubmit KYC Documents' : 'Complete KYC Verification'}
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Migration Status Banner removed - migration system simplified */}

            {/* Shareholder Dashboard Content */}
            {(() => {
              // IMPORTANT: UserDashboard component ALWAYS shows shareholder content
              // regardless of user's actual role, because this IS the shareholder dashboard

              console.log('🔍 UserDashboard overview - Always showing shareholder content')

              // Always show shareholder content - no role-based switching in UserDashboard
              // Users who want affiliate features should switch to AffiliateDashboard

              return (
                <>
                    {/* 2026–2027 GROWTH YEARS NOTICE - Prominent Banner */}
                    {DIVIDENDS_USER_VISIBLE && (
                    <div style={{
                      background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.15) 0%, rgba(16, 185, 129, 0.10) 100%)',
                      border: '2px solid #f59e0b',
                      borderRadius: '12px',
                      padding: '16px 20px',
                      marginBottom: '20px',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '14px',
                    }}>
                      <div style={{ fontSize: '28px', flexShrink: 0, lineHeight: 1 }}>📈</div>
                      <div>
                        <p style={{ fontSize: '15px', fontWeight: '700', color: '#fbbf24', margin: '0 0 6px 0', letterSpacing: '0.5px', textTransform: 'uppercase' }}>
                          2026–2027 Growth Years
                        </p>
                        <p style={{ fontSize: '14px', color: '#e5e7eb', margin: 0, lineHeight: '1.5' }}>
                          <strong style={{ color: '#fbbf24' }}>2026 and 2027 are designated growth years</strong> as we scale wash plant capacity toward the 200+ plant roadmap. Dividend projections shown reflect annual figures across this scaling phase and are subject to actual production and sales. Thank you for being part of this growth journey.
                        </p>
                      </div>
                    </div>
                    )}

                    {/* Shareholder Dashboard Cards - Separated Share Categories */}
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                      gap: '20px',
                      marginBottom: '30px'
                    }}>


                      {/* Total Shares */}
                      <div style={{
                        backgroundColor: 'rgba(31, 41, 55, 0.9)',
                        borderRadius: '12px',
                        padding: '20px',
                        textAlign: 'center',
                        border: '2px solid #f59e0b',
                        position: 'relative'
                      }}>
                        <div style={{ fontSize: '20px', marginBottom: '8px' }}>🏆</div>
                        <p style={{ fontSize: '28px', fontWeight: 'bold', color: '#f59e0b', margin: '0 0 5px 0' }}>
                          {dataLoading ? '...' : Math.floor(dashboardData.totalShares).toLocaleString()}
                        </p>
                        <p style={{ fontSize: '14px', color: '#e5e7eb', margin: 0 }}>Whole Shares</p>
                        <p style={{ fontSize: '13px', color: '#60a5fa', margin: '4px 0 0 0' }}>
                          + {dataLoading ? '...' : (dashboardData.totalShares % 1).toFixed(6)} fractional
                        </p>
                        {/* v4.8.250: free/gift shares earned from packages — BOGO, Anniversary,
                            PCAP (buyer and referrer/upline gifts), plus Consolidated/Legacy Bonus.
                            Already part of Whole Shares above; shown so the member can see how much
                            of their holding was earned rather than bought. */}
                        {!dataLoading && dashboardData.bonusShares > 0 && (
                          <p style={{ fontSize: '13px', color: '#f59e0b', margin: '2px 0 0 0' }}>
                            + {dashboardData.bonusShares.toLocaleString(undefined, { maximumFractionDigits: 2 })} bonus shares
                          </p>
                        )}
                        <button
                          onClick={() => handleSectionChange('shares')}
                          style={{
                            marginTop: '12px',
                            backgroundColor: '#f59e0b',
                            color: '#ffffff',
                            border: 'none',
                            borderRadius: '8px',
                            padding: '8px 16px',
                            fontSize: '14px',
                            fontWeight: 'bold',
                            cursor: 'pointer',
                            width: '100%'
                          }}
                        >
                          📊 View My Shares
                        </button>
                      </div>

                      {/* Portfolio Value */}
                      <div style={{
                        backgroundColor: 'rgba(31, 41, 55, 0.9)',
                        borderRadius: '12px',
                        padding: '20px',
                        textAlign: 'center',
                        border: '1px solid #374151'
                      }}>
                        <div style={{ fontSize: '20px', marginBottom: '8px' }}>📊</div>
                        <p style={{ fontSize: '28px', fontWeight: 'bold', color: '#10b981', margin: '0 0 5px 0' }}>
                          {"$" + (dataLoading || dashboardData.currentSharePrice == null ? '...' : dashboardData.shareValue.toLocaleString())}
                        </p>
                        <p style={{ fontSize: '14px', color: '#e5e7eb', margin: 0 }}>Portfolio Value</p>
                        <p style={{ fontSize: '13px', color: '#ffffff', margin: '4px 0 0 0' }}>Current Market Value</p>
                      </div>

                      {/* Estimated Dividends - hidden while DIVIDENDS_USER_VISIBLE is false */}
                      {DIVIDENDS_USER_VISIBLE && (() => {
                        // Overlay the live-denominator dynamic projection so the
                        // displayed 2026 figure reflects actual shares sold (not
                        // the 1.4M cap floor). The dashboardData.futureDividends
                        // (July payout) is kept as a fallback when liveShares=0.
                        const staticProj = DIVIDEND_PROJECTIONS[2026];
                        const dyn = computeDynamicAnnualProjection(2026, liveSharesSold);
                        const annualPerShare = dyn?.estimatedDividendPerShare ?? 0;
                        const goldKgYear = staticProj?.goldProductionKgPerYear || 0;
                        const goldKgMonth = staticProj?.goldProductionKgPerMonth || 0;
                        const monthsOp = staticProj?.monthsOperating || 12;
                        const isRamp = staticProj?.isRampYear ?? false;
                        const liveAnnualDividend = annualPerShare * dashboardData.totalShares;
                        const denominator = dyn ? Math.floor(dyn.denominatorUsed) : 0;
                        return (
                      <div style={{
                        backgroundColor: 'rgba(31, 41, 55, 0.9)',
                        borderRadius: '12px',
                        padding: '20px',
                        textAlign: 'center',
                        border: '1px solid #374151'
                      }}>
                        <div style={{ fontSize: '20px', marginBottom: '8px' }}>⛏️</div>
                        <p style={{ fontSize: '28px', fontWeight: 'bold', color: '#f59e0b', margin: '0 0 5px 0' }}>
                          {"$" + (dataLoading ? '...' : liveAnnualDividend.toLocaleString(undefined, { maximumFractionDigits: 0 }))}
                        </p>
                        <p style={{ fontSize: '14px', color: '#e5e7eb', margin: 0 }}>
                          {dataLoading ? 'Loading...' : 'Est. 2026 Dividend (annual)'}
                        </p>
                        <p style={{ fontSize: '13px', color: '#ffffff', margin: '4px 0 0 0' }}>
                          Based on projected mining yield
                        </p>

                        {/* Calculation breakdown */}
                        {!dataLoading && dashboardData.totalShares > 0 && (
                            <div style={{
                              marginTop: '10px',
                              padding: '10px',
                              backgroundColor: 'rgba(245, 158, 11, 0.08)',
                              borderRadius: '8px',
                              border: '1px solid rgba(245, 158, 11, 0.2)',
                              textAlign: 'left',
                              fontSize: '12px',
                              lineHeight: '1.6',
                              color: '#d1d5db'
                            }}>
                              <p style={{ margin: '0 0 4px 0', fontWeight: '600', color: '#fbbf24', fontSize: '12px' }}>📐 How this is calculated:</p>
                              <p style={{ margin: '0 0 2px 0' }}>Gold production: <span style={{ color: '#fff' }}>{goldKgYear.toLocaleString()} kg{isRamp ? ` in 2026 (${goldKgMonth} kg/month × ${monthsOp} operating months)` : '/year'}</span></p>
                              <p style={{ margin: '0 0 2px 0' }}>Gold price: <span style={{ color: '#fff' }}>${CALCULATION_CONSTANTS.GOLD_PRICE_USD_PER_KG.toLocaleString()}/kg</span></p>
                              <p style={{ margin: '0 0 2px 0' }}>Net margin (after {CALCULATION_CONSTANTS.REFINING_PERCENT}% refining + {CALCULATION_CONSTANTS.OPEX_PERCENT}% opex): <span style={{ color: '#fff' }}>50%</span></p>
                              {dyn ? (
                                <p style={{ margin: '0 0 2px 0' }}>Shares used in calculation: <span style={{ color: '#10b981', fontWeight: 600 }}>{denominator.toLocaleString()} shares sold so far</span></p>
                              ) : (
                                <p style={{ margin: '0 0 2px 0' }}>Shares used in calculation: <span style={{ color: '#fff' }}>Waiting for the live share count</span></p>
                              )}
                              <p style={{ margin: '0 0 2px 0' }}>Annual dividend per share: <span style={{ color: '#10b981', fontWeight: '600' }}>${annualPerShare.toLocaleString(undefined, { maximumFractionDigits: 2 })}</span></p>
                              <p style={{ margin: '6px 0 0 0', borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '6px' }}>
                                Your shares: <span style={{ color: '#fff' }}>{dashboardData.totalShares.toLocaleString(undefined, { maximumFractionDigits: 6 })}</span> × ${annualPerShare.toLocaleString(undefined, { maximumFractionDigits: 2 })} = <span style={{ color: '#f59e0b', fontWeight: '700' }}>${liveAnnualDividend.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>/year
                              </p>
                            </div>
                        )}

                        <p style={{ fontSize: '11px', color: '#9ca3af', margin: '8px 0 0 0', fontStyle: 'italic' }}>
                          Subject to actual gold production &amp; market conditions
                        </p>
                      </div>
                        );
                      })()}

                      {/* Current Share Price */}
                      <div style={{
                        backgroundColor: 'rgba(31, 41, 55, 0.9)',
                        borderRadius: '12px',
                        padding: '20px',
                        textAlign: 'center',
                        border: '1px solid #374151'
                      }}>
                        <div style={{ fontSize: '20px', marginBottom: '8px' }}>💲</div>
                        <p style={{ fontSize: '28px', fontWeight: 'bold', color: '#8b5cf6', margin: '0 0 5px 0' }}>
                          {dashboardData.currentSharePrice == null ? '...' : `$${dashboardData.currentSharePrice.toFixed(2)}`}
                        </p>
                        <p style={{ fontSize: '14px', color: '#e5e7eb', margin: 0 }}>Current Share Price</p>
                        <p style={{ fontSize: '13px', color: '#ffffff', margin: '4px 0 0 0' }}>Per Share Value</p>
                      </div>
                    </div>

                    {/* Commission Balances Section - Only show if balance > 0 */}
                    {((dashboardData.usdtCommissions?.available || 0) > 0 || (dashboardData.usdtCommissions?.totalEarned || 0) > 0) && (
                    <div style={{
                      backgroundColor: 'rgba(17, 24, 39, 0.8)',
                      borderRadius: '12px',
                      padding: '20px',
                      marginBottom: '30px',
                      border: '1px solid #374151'
                    }}>
                      <h3 style={{ color: '#ffffff', fontSize: '18px', fontWeight: 'bold', marginBottom: '15px', textAlign: 'center' }}>
                        💰 Commission Balances
                      </h3>

                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                        gap: '15px',
                        marginBottom: '15px'
                      }}>
                        {/* USDT Commission */}
                        <div style={{
                          backgroundColor: 'rgba(31, 41, 55, 0.6)',
                          borderRadius: '8px',
                          padding: '16px',
                          border: '1px solid #374151',
                          textAlign: 'center'
                        }}>
                          <div style={{ fontSize: '24px', marginBottom: '8px' }}>💵</div>
                          <p style={{ fontSize: '24px', fontWeight: 'bold', color: '#10b981', margin: '0 0 5px 0' }}>
	                        ${dataLoading ? '...' : (dashboardData.usdtCommissions?.computedWithdrawable ?? dashboardData.usdtCommissions?.available ?? 0).toFixed(2)}
                          </p>
                          <p style={{ fontSize: '14px', color: '#e5e7eb', margin: 0 }}>Available Balance</p>
                          <p style={{ fontSize: '13px', color: '#ffffff', margin: '4px 0 0 0' }}>Commission Balance: ${dataLoading ? '...' : (dashboardData.usdtCommissions?.displayedBalance ?? dashboardData.usdtCommissions?.available ?? 0).toFixed(2)}</p>
                          {!!dashboardData.usdtCommissions?.withdrawalBlocked && (
                            <p style={{ fontSize: '11px', color: '#fbbf24', margin: '6px 0 0 0' }}>
                              ⚠ {dashboardData.usdtCommissions.withdrawalBlockReasonCode === 'WITHDRAWAL_ESCROW_ACTIVE'
                                ? 'Withdrawal in progress'
                                : dashboardData.usdtCommissions.withdrawalBlockReasonCode === 'KYC_REQUIRED'
                                  ? 'KYC required'
                                  : dashboardData.usdtCommissions.withdrawalBlockReasonCode === 'WALLET_REQUIRED'
                                    ? 'Wallet setup required'
                                    : 'Withdrawal temporarily restricted'}
                            </p>
                          )}
                        </div>

                        {/* Total Earned */}
                        <div style={{
                          backgroundColor: 'rgba(31, 41, 55, 0.6)',
                          borderRadius: '8px',
                          padding: '16px',
                          border: '1px solid #374151',
                          textAlign: 'center'
                        }}>
                          <div style={{ fontSize: '24px', marginBottom: '8px' }}>📊</div>
                          <p style={{ fontSize: '24px', fontWeight: 'bold', color: '#3b82f6', margin: '0 0 5px 0' }}>
                            ${dataLoading ? '...' : (dashboardData.usdtCommissions?.totalEarned || 0).toFixed(2)}
                          </p>
                          <p style={{ fontSize: '14px', color: '#e5e7eb', margin: 0 }}>Total Earned</p>
                          <p style={{ fontSize: '13px', color: '#ffffff', margin: '4px 0 0 0' }}>Lifetime USDT</p>
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          window.history.pushState({}, '', '/dashboard?section=commissions');
                          if (onSwitchDashboard) onSwitchDashboard('affiliate');
                        }}
                        style={{
                          backgroundColor: '#10b981',
                          color: '#ffffff',
                          border: 'none',
                          borderRadius: '8px',
                          padding: '10px 20px',
                          fontSize: '14px',
                          fontWeight: 'bold',
                          cursor: 'pointer',
                          width: '100%'
                        }}
                      >
                        💼 Go to Commissions in Affiliate Dashboard
                      </button>
                    </div>
                    )}

                    {/* Conservative Dividend Projections - NEW */}
                    {DIVIDENDS_USER_VISIBLE && (
                    <DividendProjections2026to2035
                      userShares={dashboardData.totalShares}
                      variant="full"
                    />
                    )}

                    {/* How To Start Training Videos */}
                    <HowToStartVideos
                      pageType="shareholder-dashboard"
                      title="Training Videos"
                      className="mt-8"
                    />
                  </>
                )
            })()}

        {/* Shareholder Additional Content */}
        {(() => {
          // IMPORTANT: UserDashboard component ALWAYS shows shareholder content
          // No role-based switching - this IS the shareholder dashboard

          console.log('🔍 UserDashboard additional content - Always showing shareholder content')

          // Always show shareholder additional content (none currently needed)
          return null; // No additional content needed for shareholders currently

        })()}
          </>
        ) : activeSection === 'referrals' ? (
          <div className="space-y-6">
            <ReferralCenter
              userId={(() => {
                let userId = user?.database_user?.id || user?.id;
                if (typeof userId === 'string' && userId.includes('db_')) {
                  userId = parseInt(userId.replace(/db_/g, ''), 10);
                }
                console.log('🔗 ReferralCenter userId:', userId, 'type:', typeof userId);
                return typeof userId === 'number' && !isNaN(userId) ? userId : 0;
              })()}
              className="bg-gray-800 rounded-lg border border-gray-700 p-6"
            />
            {isMasterAdminViewSession() ? (
              <div className="bg-gray-800 border border-yellow-500 rounded-lg p-6 text-center">
                <div className="text-yellow-500 text-4xl mb-4">🔒</div>
                <h3 className="text-xl font-bold text-white mb-2">
                  Commission Withdrawal - Read-Only Mode
                </h3>
                <p className="text-white">
                  Withdrawal actions are disabled in admin view mode.
                  <br />
	                Commission Balance: ${(dashboardData.usdtCommissions?.displayedBalance ?? dashboardData.usdtCommissions?.available ?? 0).toFixed(2)}
	                <br />
	                Available Balance: ${(dashboardData.usdtCommissions?.computedWithdrawable ?? dashboardData.usdtCommissions?.available ?? 0).toFixed(2)}
	                <br />
	                Max Withdrawal Now: ${(dashboardData.usdtCommissions?.withdrawalRequestMax ?? dashboardData.usdtCommissions?.computedWithdrawable ?? 0).toFixed(2)}
                  <br />
                  Available Shares: {(dashboardData.commissionShares || 0).toLocaleString()}
                </p>
              </div>
            ) : (
              <CommissionWithdrawal
                userId={(() => {
                  let userId = user?.database_user?.id || user?.id;
                  if (typeof userId === 'string' && userId.includes('db_')) {
                    userId = parseInt(userId.replace(/db_/g, ''), 10);
                  }
                  return typeof userId === 'number' && !isNaN(userId) ? userId : 0;
                })()}
                availableUSDT={dashboardData.usdtCommissions?.available || 0}
                displayedBalanceUSDT={dashboardData.usdtCommissions?.displayedBalance ?? dashboardData.usdtCommissions?.available ?? 0}
                computedWithdrawableUSDT={dashboardData.usdtCommissions?.computedWithdrawable ?? dashboardData.usdtCommissions?.available ?? 0}
                withdrawalRequestMaxUSDT={dashboardData.usdtCommissions?.withdrawalRequestMax ?? undefined}
                networkFeeUSDT={dashboardData.usdtCommissions?.withdrawalNetworkFee ?? undefined}
                withdrawalBlocked={dashboardData.usdtCommissions?.withdrawalBlocked || false}
                withdrawalBlockReasonCode={dashboardData.usdtCommissions?.withdrawalBlockReasonCode || null}
                withdrawalBlockReasonMessage={dashboardData.usdtCommissions?.withdrawalBlockReasonMessage || null}
                availableShares={dashboardData.commissionShares || 0}
                currentSharePrice={dashboardData.currentSharePrice ?? 0}
                onWithdrawalRequest={() => {
                  // Refresh dashboard data after withdrawal request
                  const userId = extractNumericUserId(user);
                  if (userId) {
                    loadDashboardData(userId);
                  }
                }}
                userEmail={user?.database_user?.email || user?.email}
                onNavigateToSupport={() => {
                  // Navigate to support center in shareholder dashboard
                  handleSectionChange('support-center' as DashboardSection);
                }}
              />
            )}
          </div>
        ) : activeSection === 'dividends' && DIVIDENDS_USER_VISIBLE ? (
          <DividendsSection
            user={user}
            dashboardData={dashboardData as any}
          />
        ) : activeSection === 'portfolio' ? (
          <div>
            {/* Pending and Rejected Payments Sections */}
            <PendingPaymentsList
              userId={extractNumericUserId(user)}
              onResume={(payment) => {
                setResumePaymentData({
                  paymentId: payment.id,
                  amount: payment.amount,
                  shares: payment.shares_to_purchase,
                  network: payment.network,
                  isResubmit: false
                });
                setShowPurchaseFlow(true);
              }}
            />
            <RejectedPaymentsList
              userId={extractNumericUserId(user)}
              onResubmit={(payment) => {
                setResumePaymentData({
                  paymentId: payment.id,
                  amount: payment.amount,
                  shares: payment.shares_to_purchase,
                  network: payment.network,
                  isResubmit: true
                });
                setShowPurchaseFlow(true);
              }}
            />
            <UnderpaidPaymentsList
              userId={extractNumericUserId(user)}
              onSubmitBalancePayment={(payment, underpaymentDetails) => {
                // Set up resume payment data with balance payment flag
                setResumePaymentData({
                  paymentId: payment.id, // This will be the original underpaid payment ID
                  amount: underpaymentDetails.balance_owed, // Only the balance amount
                  shares: payment.shares_to_purchase,
                  network: payment.network,
                  isResubmit: false,
                  isBalancePayment: true,
                  originalPaymentId: payment.id,
                  balanceOwed: underpaymentDetails.balance_owed
                });
                setShowPurchaseFlow(true);
              }}
            />
            {/* Main Portfolio Content */}
            <ComprehensivePortfolio
              user={user}
              currentPhase={{ price_per_share: dashboardData.currentSharePrice }}
              calculatorData={currentCalculatorData}
              totalShares={dashboardData.totalShares}
            />
            {/* Gold Diggers Pool CTA */}
            <div
              onClick={() => handleSectionChange('gold-diggers')}
              style={{
                marginTop: '16px',
                padding: '16px 20px',
                background: 'linear-gradient(135deg, rgba(120,80,0,0.3), rgba(60,40,0,0.2))',
                border: '1px solid rgba(255,215,0,0.25)',
                borderRadius: '12px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                transition: 'border-color 0.2s'
              }}
              onMouseEnter={e => (e.currentTarget.style.borderColor = 'rgba(255,215,0,0.5)')}
              onMouseLeave={e => (e.currentTarget.style.borderColor = 'rgba(255,215,0,0.25)')}
            >
              <span style={{ fontSize: '28px' }}>⛏️</span>
              <div style={{ flex: 1 }}>
                <div style={{ color: '#FFD700', fontWeight: '600', fontSize: '15px' }}>Gold Diggers Performance Pool</div>
                <div style={{ color: '#aaa', fontSize: '13px', marginTop: '2px' }}>Compete daily — Top 50 teams share 10% of all share purchases →</div>
              </div>
            </div>
            <div
              onClick={() => handleSectionChange('zimbabwe-awaits')}
              style={{
                marginTop: '12px',
                padding: '16px 20px',
                background: 'linear-gradient(135deg, rgba(120,80,0,0.35), rgba(40,20,0,0.25))',
                border: '1px solid rgba(255,215,0,0.35)',
                borderRadius: '12px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                transition: 'border-color 0.2s'
              }}
              onMouseEnter={e => (e.currentTarget.style.borderColor = 'rgba(255,215,0,0.6)')}
              onMouseLeave={e => (e.currentTarget.style.borderColor = 'rgba(255,215,0,0.35)')}
            >
              <span style={{ fontSize: '28px' }}>🇿🇼</span>
              <div style={{ flex: 1 }}>
                <div style={{ color: '#FFD700', fontWeight: '600', fontSize: '15px' }}>Zimbabwe Awaits</div>
                <div style={{ color: '#aaa', fontSize: '13px', marginTop: '2px' }}>Top 10 team leaders: mine visit + 10% share bonus →</div>
              </div>
            </div>
          </div>
        ) : activeSection === 'shares' ? (
          <MySharesPage user={user} />
        ) : activeSection === 'purchase-shares' ? (
          <div className="space-y-6">
            {/* Purchase Button Section */}
            <div className="bg-gray-800 rounded-lg border border-gray-700 p-6">
              <h2 className="text-2xl font-bold text-white mb-4">🛒 Purchase Gold Shares</h2>
              <p className="text-white mb-6">
                Purchase Aureus Alliance Holdings gold mining shares. Choose your share purchase amount and payment method.
              </p>
              <button
                onClick={() => {
                  setBirthdayPackage(undefined);
                  setProductionCapitalPackage(undefined);
                  setShowPurchaseFlow(true);
                }}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium transition-colors"
              >
                Start Purchase Process
              </button>
              {/* Production Capital P1–P5 cards — auto-hidden outside campaign window */}
              <div className="mt-6">
                <BirthdayPackageButtons
                  onLaunchPackage={(packageId) => {
                    setBirthdayPackage(undefined);
                    setProductionCapitalPackage(packageId);
                    setShowPurchaseFlow(true);
                  }}
                  onLaunchStandard={() => {
                    setBirthdayPackage(undefined);
                    setProductionCapitalPackage(undefined);
                    setShowPurchaseFlow(true);
                  }}
                />
              </div>
            </div>

            {/* Commission Top-Up has moved to the USDT-to-Shares converter on the affiliate dashboard */}

            {/* VISA Card Purchase Section */}
            <VisaCardPurchase
              userId={extractNumericUserId(user)}
              onSuccess={() => {
                // Optionally reload data after successful order
              }}
            />

            {/* Payment Status Section - Pending, Rejected & Flagged */}
            <div className="bg-gray-800 rounded-lg border border-amber-600/50 p-6">
              <div className="flex items-center gap-3 mb-4">
                <span className="text-2xl">📋</span>
                <h3 className="text-xl font-bold text-amber-400">Payment Status</h3>
                {pendingPayments.length > 0 && (
                  <span className="bg-amber-600/20 text-amber-400 text-sm px-2 py-0.5 rounded-full">
                    {pendingPayments.filter(p => p.status === 'pending').length} pending,
                    {pendingPayments.filter(p => p.status === 'rejected').length} rejected,
                    {pendingPayments.filter(p => p.status === 'flagged_duplicate' || p.status === 'review_requested').length} flagged
                  </span>
                )}
              </div>

              {pendingPaymentsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-400"></div>
                  <span className="ml-3 text-white">Loading payment status...</span>
                </div>
              ) : pendingPayments.length === 0 ? (
                <div className="text-center py-8">
                  <div className="text-4xl mb-3">✅</div>
                  <p className="text-white">No pending, rejected, or flagged payments</p>
                  <p className="text-gray-500 text-sm mt-1">All your payments have been processed successfully</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {pendingPayments.map((payment) => (
                    <div
                      key={payment.id}
                      className={`bg-gray-700/50 rounded-lg p-4 border transition-colors ${
                        payment.status === 'rejected'
                          ? 'border-red-600/50 hover:border-red-500'
                          : payment.status === 'flagged_duplicate' || payment.status === 'review_requested'
                            ? 'border-orange-600/50 hover:border-orange-500'
                            : 'border-gray-600 hover:border-amber-500/50'
                      }`}
                    >
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <div className="flex items-center gap-4">
                          {/* Payment Method Icon */}
                          <div className="text-2xl">
                            {payment.status === 'rejected' ? '❌' :
                             payment.status === 'flagged_duplicate' ? '🚩' :
                             payment.status === 'review_requested' ? '🔍' :
                             payment.payment_method === 'crypto' || payment.payment_method?.includes('Crypto') ? '₿' :
                             payment.payment_method === 'bank' || payment.payment_method === 'eft' || payment.payment_method === 'Bank Transfer' ? '🏦' :
                             payment.payment_method === 'yoco' ? '💳' : '💰'}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className={`font-semibold ${
                                payment.status === 'rejected' ? 'text-red-400' :
                                payment.status === 'flagged_duplicate' || payment.status === 'review_requested' ? 'text-orange-400' :
                                'text-white'
                              }`}>
                                ${parseFloat(String(payment.total_amount)).toFixed(2)} USDT
                              </span>
                              <span className="text-white">•</span>
                              <span className="text-gray-300">
                                {parseFloat(String(payment.shares_purchased)).toFixed(2)} shares
                              </span>
                            </div>
                            <div className="text-sm text-white mt-1">
                              {payment.payment_method === 'crypto' || payment.payment_method?.includes('Crypto') ? 'Cryptocurrency' :
                               payment.payment_method === 'bank' || payment.payment_method === 'Bank Transfer' ? 'Bank Transfer' :
                               payment.payment_method === 'eft' ? 'EFT Payment' :
                               payment.payment_method === 'yoco' ? 'Yoco Card Payment' :
                               payment.payment_method}
                              {payment.transaction_reference && (
                                <span className="ml-2 text-gray-500">
                                  Ref: {payment.transaction_reference}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          {payment.status === 'rejected' ? (
                            <div className="inline-flex items-center gap-1.5 bg-red-600/20 text-red-400 text-sm px-3 py-1 rounded-full">
                              <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                              Rejected
                            </div>
                          ) : payment.status === 'flagged_duplicate' ? (
                            <div className="inline-flex items-center gap-1.5 bg-orange-600/20 text-orange-400 text-sm px-3 py-1 rounded-full">
                              <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                              Flagged as Duplicate
                            </div>
                          ) : payment.status === 'review_requested' ? (
                            <div className="inline-flex items-center gap-1.5 bg-blue-600/20 text-blue-400 text-sm px-3 py-1 rounded-full">
                              <span className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></span>
                              Review Requested
                            </div>
                          ) : (
                            <div className="inline-flex items-center gap-1.5 bg-amber-600/20 text-amber-400 text-sm px-3 py-1 rounded-full">
                              <span className="w-2 h-2 bg-amber-400 rounded-full animate-pulse"></span>
                              Awaiting Admin Approval
                            </div>
                          )}
                          <div className="text-sm text-gray-500 mt-1">
                            Submitted {new Date(payment.created_at).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </div>
                        </div>
                      </div>
                      {/* Rejection Reason */}
                      {payment.status === 'rejected' && payment.transaction_notes && (
                        <div className="mt-3 p-3 bg-red-900/20 border border-red-700/30 rounded-lg">
                          <p className="text-red-300 text-sm">
                            <span className="font-semibold">❌ Rejection Reason:</span> {payment.transaction_notes.replace(/^Rejected:\s*/i, '')}
                          </p>
                        </div>
                      )}
                      {/* Delete button for pending and rejected payments */}
                      {(payment.status === 'pending' || payment.status === 'rejected') && (
                        <div className="mt-3 flex justify-end">
                          <button
                            disabled={deletingPaymentId === payment.id}
                            onClick={async () => {
                              const statusLabel = payment.status === 'rejected' ? 'rejected' : 'pending'
                              const confirmed = window.confirm(
                                `Are you sure you want to permanently delete this ${statusLabel} payment?\n\n` +
                                `Amount: $${parseFloat(String(payment.total_amount)).toFixed(2)}\n` +
                                `Shares: ${parseFloat(String(payment.shares_purchased)).toFixed(2)}\n\n` +
                                `This action cannot be undone.`
                              )
                              if (!confirmed) return

                              try {
                                setDeletingPaymentId(payment.id)
                                const validUserId = extractNumericUserId(user)
                                if (!validUserId) {
                                  alert('Invalid user ID. Please refresh the page.')
                                  return
                                }
                                const response = await fetch(`${API_BASE_URL}/api/user/update-profile`, {
                                  method: 'POST',
                                  headers: { 'Content-Type': 'application/json', ...await ensureSessionHeaders() },
                                  body: JSON.stringify({
                                    userId: validUserId,
                                    operation: 'delete_purchase_payment',
                                    data: { paymentId: payment.id }
                                  })
                                })
                                const result = await response.json()
                                if (!response.ok) throw new Error(result.error)

                                // Reload payments after deletion
                                if (validUserId) {
                                  await loadPendingPayments(validUserId)
                                }
                              } catch (err: any) {
                                console.error('Error deleting payment:', err)
                                alert('Failed to delete payment: ' + (err.message || 'Unknown error'))
                              } finally {
                                setDeletingPaymentId(null)
                              }
                            }}
                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                              deletingPaymentId === payment.id
                                ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                                : 'bg-red-600/20 text-red-400 hover:bg-red-600/40 border border-red-600/30'
                            }`}
                          >
                            {deletingPaymentId === payment.id ? '⏳ Deleting...' : '🗑️ Delete'}
                          </button>
                        </div>
                      )}
                      {/* Flagged Reason and Request Review Button */}
                      {(payment.status === 'flagged_duplicate' || payment.status === 'review_requested') && (
                        <div className="mt-3 p-3 bg-orange-900/20 border border-orange-700/30 rounded-lg">
                          {payment.transaction_notes && (() => {
                            try {
                              const notes = typeof payment.transaction_notes === 'string'
                                ? JSON.parse(payment.transaction_notes)
                                : payment.transaction_notes
                              return notes?.flagged_reason ? (
                                <p className="text-orange-300 text-sm mb-3">
                                  <span className="font-semibold">🚩 Flag Reason:</span> {notes.flagged_reason}
                                </p>
                              ) : null
                            } catch {
                              return null
                            }
                          })()}
                          {payment.status === 'flagged_duplicate' && (
                            <button
                              onClick={async () => {
                                try {
                                  const validUserId = extractNumericUserId(user)
                                  if (!validUserId) {
                                    alert('Invalid user ID. Please refresh the page.')
                                    return
                                  }
                                  const response = await fetch(`${API_BASE_URL}/api/user/update-profile`, {
                                    method: 'POST',
                                    headers: { 'Content-Type': 'application/json', ...await ensureSessionHeaders() },
                                    body: JSON.stringify({
                                      userId: validUserId,
                                      operation: 'request_payment_review',
                                      data: { paymentId: payment.id }
                                    })
                                  })
                                  const result = await response.json()
                                  if (!response.ok) throw new Error(result.error)

                                  await loadPendingPayments(validUserId)
                                } catch (err) {
                                  console.error('Error requesting review:', err)
                                  alert('Failed to request review. Please try again.')
                                }
                              }}
                              className="w-full py-2 px-4 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium text-sm"
                            >
                              🔍 Request Review
                            </button>
                          )}
                          {payment.status === 'review_requested' && (
                            <p className="text-blue-300 text-sm">
                              <span className="font-semibold">ℹ️</span> Your review request has been submitted. An admin will review your payment shortly.
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  ))}

                  {pendingPayments.some(p => p.status === 'pending') && (
                    <div className="mt-4 p-3 bg-blue-900/20 border border-blue-700/30 rounded-lg">
                      <p className="text-blue-300 text-sm">
                        <span className="font-semibold">ℹ️ Note:</span> Payments are typically reviewed within 24-48 hours.
                        You will receive a notification once your payment is approved and shares are allocated.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Transaction History */}
            <TransactionHistory userId={extractNumericUserId(user)} />

          </div>
        ) : activeSection === 'marketing-tools' ? (
          <MaterialsLibrary
            userId={(() => {
              let userId = user?.database_user?.id || user?.id;
              if (typeof userId === 'string' && userId.includes('db_')) {
                userId = parseInt(userId.replace(/db_/g, ''), 10);
              }
              return typeof userId === 'number' && !isNaN(userId) ? userId : 0;
            })()}
          />
        ) : activeSection === 'company-presentation' ? (
          <div className="bg-gray-800 rounded-lg border border-gray-700 p-6">
            <h2 className="text-2xl font-bold text-white mb-4">📋 Company Presentation</h2>
            <p className="text-white mb-6">
              Learn about Aureus Alliance Holdings, our mining operations, and share purchase opportunities.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-gray-700 rounded-lg p-6 hover:bg-gray-600 transition-colors">
                <div className="text-center">
                  <div className="text-4xl mb-4">🇬🇧</div>
                  <h3 className="text-lg font-semibold text-white mb-3">English Presentation</h3>
                  <p className="text-gray-300 text-sm mb-4">Complete company overview and mining operations details</p>
                  <a
                    href="https://fgubaqoftdeefcakejwu.supabase.co/storage/v1/object/public/marketing-materials/Aureus%20Presentation%20Plan%20-%20Eng.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                  >
                    📄 Download PDF
                  </a>
                </div>
              </div>
              <div className="bg-gray-700 rounded-lg p-6 hover:bg-gray-600 transition-colors">
                <div className="text-center">
                  <div className="text-4xl mb-4">🇮🇳</div>
                  <h3 className="text-lg font-semibold text-white mb-3">Hindi Presentation</h3>
                  <p className="text-gray-300 text-sm mb-4">हिंदी में कंपनी की पूरी जानकारी और खनन संचालन विवरण</p>
                  <a
                    href="https://fgubaqoftdeefcakejwu.supabase.co/storage/v1/object/public/marketing-materials/Aureus%20Presentation%20Plan%20-%20Hindi.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                  >
                    📄 Download PDF
                  </a>
                </div>
              </div>
              <div className="bg-gray-700 rounded-lg p-6 hover:bg-gray-600 transition-colors">
                <div className="text-center">
                  <div className="text-4xl mb-4">🇮🇩</div>
                  <h3 className="text-lg font-semibold text-white mb-3">Indonesian Presentation</h3>
                  <p className="text-gray-300 text-sm mb-4">Gambaran lengkap perusahaan dan detail operasi pertambangan</p>
                  <a
                    href="https://fgubaqoftdeefcakejwu.supabase.co/storage/v1/object/public/marketing-materials/Aureus%20Presentation%20Plan%20-%20Indonesian.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                  >
                    📄 Download PDF
                  </a>
                </div>
              </div>
            </div>
          </div>
        ) : activeSection === 'mining-operations' ? (
          <div className="bg-gray-800 rounded-lg border border-gray-700 p-6">
            <h2 className="text-2xl font-bold text-white mb-6">⛏️ Mining Operations</h2>

            {/* Main Operations Overview */}
            <div className="bg-gradient-to-r from-gray-700 to-gray-600 rounded-lg p-6 mb-8">
              <div className="text-center mb-6">
                <h3 className="text-xl font-bold text-white mb-4">🌍 Network of Eco-Friendly Wash Plants</h3>
                <p className="text-white text-lg leading-relaxed">
                  We are building a network of over <span className="text-yellow-400 font-bold">200 eco-friendly wash plants across Africa</span>.
                  These plants process mine tailings to extract fine traces of <span className="text-yellow-400 font-bold">gold, dust, and nuggets</span> —
                  producing an estimated <span className="text-yellow-400 font-bold">100 tons per year</span>.
                </p>
              </div>
              <div className="text-center">
                <p className="text-gray-300 text-base">
                  Our operations are driven by <span className="text-green-400 font-semibold">environmental sustainability</span> and
                  <span className="text-blue-400 font-semibold"> social responsibility</span>, ensuring long-term growth while uplifting the communities around us.
                </p>
              </div>
            </div>

            {/* Timeline and Expansion */}
            <div className="mb-8">
              <h3 className="text-xl font-bold text-white mb-6 text-center">📅 Timeline and Expansion</h3>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                <div className="bg-gray-700 rounded-lg p-6">
                  <h4 className="text-lg font-semibold text-white mb-4">🚀 Operational Timeline</h4>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <span className="text-yellow-400 font-bold">January 2026:</span>
                      <span className="text-gray-300">Two wash plants operational</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-yellow-400 font-bold">June 2026:</span>
                      <span className="text-gray-300">Expanding to 10 plants</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-yellow-400 font-bold">2030:</span>
                      <span className="text-gray-300">200+ plants across 5,000 hectares</span>
                    </div>
                  </div>
                </div>

                {DIVIDENDS_USER_VISIBLE && (
                <div className="bg-gray-700 rounded-lg p-6">
                  <h4 className="text-lg font-semibold text-white mb-4">💰 Dividend Schedule</h4>
                  <div className="space-y-2 text-sm">
                    <p className="text-gray-300">
                      <span className="text-blue-400 font-semibold">Years 1-2:</span> Quarterly payments
                    </p>
                    <p className="text-gray-300">
                      <span className="text-blue-400 font-semibold">Years 3-4:</span> Biannual payments
                    </p>
                    <p className="text-gray-300">
                      <span className="text-blue-400 font-semibold">Year 6+:</span> Annual payments
                    </p>
                    <p className="text-sm text-white mt-2">
                      Aligned with our long-term growth strategy
                    </p>
                  </div>
                </div>
                )}
              </div>
            </div>

            {/* Multi-Resource Operations */}
            <div className="mb-8">
              <h3 className="text-xl font-bold text-white mb-6 text-center">💎 Multi-Resource Operations</h3>
              <div className="bg-gray-700 rounded-lg p-6">
                <p className="text-gray-300 text-center mb-4">
                  These results are powered not only by <span className="text-yellow-400 font-bold">gold</span>, but also by:
                </p>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
                  <div className="bg-gray-600 rounded-lg p-3 text-center">
                    <div className="text-orange-400 text-lg">🥉</div>
                    <div className="text-white text-sm font-semibold">Copper</div>
                  </div>
                  <div className="bg-gray-600 rounded-lg p-3 text-center">
                    <div className="text-white text-lg">⚙️</div>
                    <div className="text-white text-sm font-semibold">Iron</div>
                  </div>
                  <div className="bg-gray-600 rounded-lg p-3 text-center">
                    <div className="text-gray-300 text-lg">🥈</div>
                    <div className="text-white text-sm font-semibold">Platinum</div>
                  </div>
                  <div className="bg-gray-600 rounded-lg p-3 text-center">
                    <div className="text-white text-lg">🥈</div>
                    <div className="text-white text-sm font-semibold">Silver</div>
                  </div>
                  <div className="bg-gray-600 rounded-lg p-3 text-center">
                    <div className="text-blue-400 text-lg">🔷</div>
                    <div className="text-white text-sm font-semibold">Cobalt</div>
                  </div>
                  <div className="bg-gray-600 rounded-lg p-3 text-center">
                    <div className="text-blue-200 text-lg">💎</div>
                    <div className="text-white text-sm font-semibold">Diamonds</div>
                  </div>
                  <div className="bg-gray-600 rounded-lg p-3 text-center">
                    <div className="text-yellow-400 text-lg">🥇</div>
                    <div className="text-white text-sm font-semibold">Gold</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Diversified Business */}
            <div className="bg-gradient-to-r from-blue-800 to-purple-800 rounded-lg p-6">
              <h3 className="text-xl font-bold text-white mb-4 text-center">🚀 Beyond Mining</h3>
              <p className="text-white text-center mb-4">
                Aureus is also expanding into <span className="text-blue-300 font-semibold">property development</span>,
                <span className="text-purple-300 font-semibold"> technology</span>, and
                <span className="text-green-300 font-semibold"> artificial intelligence</span> through partnerships with
                <span className="text-yellow-300 font-bold"> SUN (Smart United Network)</span> and other collaborators.
              </p>
              <div className="text-center">
                <span className="inline-block bg-yellow-600 text-white px-4 py-2 rounded-full text-sm font-semibold">
                  🎯 Multiple Streams of Profit for All Shareholders
                </span>
              </div>
            </div>
          </div>
        ) : activeSection === 'roadmap-journal' ? (
          <div className="bg-gray-800 rounded-lg border border-gray-700 p-6">
            <RoadmapJournalDisplay maxEntries={20} />
          </div>
        ) : activeSection === 'media-gallery' ? (
          <div className="bg-gray-800 rounded-lg border border-gray-700 p-6">
            <MediaGallery mode="showcase" />
          </div>
        ) : activeSection === 'community-relations' ? (
          <div className="bg-gray-800 rounded-lg border border-gray-700 p-6">
            <h2 className="text-2xl font-bold text-white mb-4">🏘️ Community Relations</h2>
            <p className="text-white mb-6">
              Our commitment to community development and stakeholder engagement.
            </p>
            <div className="space-y-4">
              <div className="bg-gray-700 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-white mb-2">🤝 Community Meetings</h3>
                <p className="text-gray-300 text-sm">Regular engagement with local communities</p>
              </div>
              <div className="bg-gray-700 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-white mb-2">🏗️ Development Plans</h3>
                <p className="text-gray-300 text-sm">Infrastructure and community development initiatives</p>
              </div>
            </div>
          </div>
        ) : activeSection === 'founding-shareholders' ? (
          <div className="bg-gray-800 rounded-lg border border-gray-700 p-6">
            <FoundingShareholdersShowcase />
          </div>
        ) : activeSection === 'support-center' ? (
          <div className="space-y-6">
            {/* Support Center Header */}
            <div className="bg-gray-800 rounded-lg border border-gray-700 p-6">
              <h2 className="text-2xl font-bold text-white mb-4">🆘 Support Center</h2>
              <p className="text-white mb-6">
                Get comprehensive support through multiple channels - tickets and private consultations.
              </p>
            </div>

            {/* Support Options Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-gray-800 rounded-lg border border-gray-700 p-4 hover:border-yellow-500 transition-colors cursor-pointer"
                   onClick={() => setActiveSection('support-tickets')}>
                <div className="text-2xl mb-2">🎫</div>
                <h3 className="text-lg font-semibold text-white mb-2">Support Tickets</h3>
                <p className="text-white text-sm">Create and track support requests</p>
              </div>

              <div className="bg-gray-800 rounded-lg border border-gray-700 p-4 hover:border-yellow-500 transition-colors cursor-pointer"
                   onClick={() => setActiveSection('consultations')}>
                <div className="text-2xl mb-2">📅</div>
                <h3 className="text-lg font-semibold text-white mb-2">Private Consultations</h3>
                <p className="text-white text-sm">Book one-on-one expert sessions</p>
              </div>
            </div>
          </div>
        ) : activeSection === 'support-tickets' ? (
          <div className="bg-gray-800 rounded-lg border border-gray-700 p-6">
            <div className="text-white">
              <TicketingSystem />
            </div>
          </div>

        ) : activeSection === 'consultations' ? (
          <div className="bg-gray-800 rounded-lg border border-gray-700 p-6">
            <div className="text-white">
              <ConsultationBooking />
            </div>
          </div>
        ) : activeSection === 'settings' ? (
          <div className="bg-gray-800 rounded-lg border border-gray-700 p-6">
            <h2 className="text-2xl font-bold text-white mb-4">⚙️ Settings</h2>
            <p className="text-white mb-6">
              Manage your account preferences and security settings.
            </p>
            {/* Settings Messages */}
            {settingsMessage && (
              <div className={`rounded-lg p-4 mb-4 ${
                settingsMessage.type === 'success'
                  ? 'bg-green-900/20 border border-green-500/30 text-green-300'
                  : 'bg-red-900/20 border border-red-500/30 text-red-300'
              }`}>
                <p className="text-sm">
                  {settingsMessage.type === 'success' ? '✅' : '❌'} {settingsMessage.text}
                </p>
              </div>
            )}

            <div className="space-y-4">
              {/* Profile Picture Section */}
              <div className="bg-gray-700 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-white mb-2">📷 Profile Picture</h3>
                <p className="text-gray-300 text-sm mb-4">Upload a professional profile picture</p>

                <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                  <ProfilePictureUpload
                    currentImageUrl={user?.database_user?.profile_image_url}
                    userId={(() => {
                      let userId = user?.database_user?.id || user?.id;
                      if (typeof userId === 'string' && userId.startsWith('db_')) {
                        userId = parseInt(userId.replace('db_', ''));
                      }
                      return userId;
                    })()}
                    onImageUpdate={handleProfilePictureUpdate}
                    size="large"
                    editable={true}
                    showSaveButton={true}
                    autoSave={false}
                    userInitials={(
                      user?.database_user?.full_name ||
                      user?.user_metadata?.full_name ||
                      user?.database_user?.first_name ||
                      user?.username ||
                      user?.email?.split('@')[0] ||
                      'User'
                    ).charAt(0).toUpperCase()}
                    userName={
                      user?.database_user?.full_name ||
                      user?.user_metadata?.full_name ||
                      user?.database_user?.first_name ||
                      user?.username ||
                      user?.email?.split('@')[0] ||
                      'User'
                    }
                  />
                  <div>
                    <p className="text-white font-medium mb-2">Upload Guidelines:</p>
                    <ul className="text-gray-300 text-sm space-y-1">
                      <li>• Use a clear, professional photo</li>
                      <li>• Maximum file size: 5MB</li>
                      <li>• Supported formats: JPG, PNG, GIF</li>
                      <li>• Square images work best</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="bg-gray-700 rounded-lg p-4">
                <div className="flex justify-between items-center mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-white">👤 Profile Settings</h3>
                    <p className="text-gray-300 text-sm">{isProfileEditing ? 'Edit your personal information' : 'Update your personal information'}</p>
                  </div>
                  {settingsMessage && (
                    <div className={`text-sm px-3 py-2 rounded-lg ${
                      settingsMessage.type === 'success'
                        ? 'bg-green-900/30 text-green-400 border border-green-600'
                        : 'bg-red-900/30 text-red-400 border border-red-600'
                    }`}>
                      {settingsMessage.text}
                    </div>
                  )}
                </div>

                {mustCompleteSettings ? (
                  <p className="text-sm text-amber-200 bg-amber-900/30 border border-amber-600/40 rounded-lg px-3 py-2 mb-4">
                    {PROFILE_DETAILS_MISSING_NOTE}
                  </p>
                ) : null}

                {/* Inline Editable Profile Information */}
                <div className="space-y-3">
                  {!isProfileEditing ? (
                    <>
                      {/* Display Mode */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-1">Name</label>
                          <div className="bg-gray-600 rounded-lg px-3 py-2 text-white">
                            {splitStoredName(user?.database_user).name || 'Not set'}
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-1">Surname</label>
                          <div className="bg-gray-600 rounded-lg px-3 py-2 text-white">
                            {splitStoredName(user?.database_user).surname || 'Not set'}
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-1">Email</label>
                          <div className="bg-gray-600 rounded-lg px-3 py-2 text-white">
                            {user?.database_user?.email || user?.email || 'Not set'}
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-1">Phone Number</label>
                          <div className="bg-gray-600 rounded-lg px-3 py-2 text-white">
                            {user?.database_user?.phone || 'Not set'}
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-1">Country</label>
                          <div className="bg-gray-600 rounded-lg px-3 py-2 text-white">
                            {(() => {
                              const countryCode = user?.database_user?.country_of_residence;
                              return countryCode ? getCountryName(countryCode) : 'Not set';
                            })()}
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-1">Username</label>
                          <div className="bg-gray-600 rounded-lg px-3 py-2 text-white">
                            {user?.database_user?.username || 'Not set'}
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-1">Account Type</label>
                          <div className="bg-gray-600 rounded-lg px-3 py-2 text-white">
                            Email Account
                          </div>
                        </div>
                      </div>

                      {/* Profile Description Section */}
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">Profile Description</label>
                        <div className="bg-gray-600 rounded-lg px-3 py-2 text-white min-h-[60px] prose prose-invert max-w-none" style={{ fontSize: '14px', lineHeight: '1.6' }}>
                          {user?.database_user?.profile_description ? (
                            <div dangerouslySetInnerHTML={createSafeHtml(user.database_user.profile_description)} />
                          ) : (
                            'No description set'
                          )}
                        </div>
                      </div>

                      <div className="pt-2 space-y-3">
                        {selfServiceProfileEditOpen(user?.database_user) ? (
                          <button
                            onClick={loadProfileData}
                            className="bg-yellow-600 hover:bg-yellow-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
                          >
                            {missingProfileDetailFields(user?.database_user).length > 0 ? 'Update missing details' : 'Edit Profile'}
                          </button>
                        ) : null}
                        <p className="text-sm text-amber-200 bg-amber-900/30 border border-amber-600/40 rounded-lg px-3 py-2">
                          {missingProfileDetailFields(user?.database_user).length > 0
                            ? PROFILE_DETAILS_MISSING_NOTE
                            : selfServiceProfileEditOpen(user?.database_user)
                            ? `You can edit these details once after you register. After you save, this button is removed. The username cannot be changed here. ${PROFILE_DETAILS_ADMIN_NOTE}`
                            : `These details, including the username, can no longer be edited here. ${PROFILE_DETAILS_ADMIN_NOTE}`}
                        </p>
                      </div>
                    </>
                  ) : (
                    <>
                      {/* Edit Mode - Inline Editable Fields */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">
                            Name *
                          </label>
                          <div className="relative">
                            <input
                              type="text"
                              required
                              value={profileEditorData.first_name}
                              disabled={!profileEditAcceptsAllFields(user?.database_user) && !missingProfileDetailFields(user?.database_user).includes('name')}
                              onChange={(e) => setProfileEditorData(prev => ({ ...prev, first_name: e.target.value, full_name: `${e.target.value} ${prev.last_name}`.trim() }))}
                              className={`w-full bg-gray-600 border rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 disabled:opacity-60 ${
                                profileValidation.full_name.status === 'valid' ? 'border-green-500 focus:ring-green-500' :
                                profileValidation.full_name.status === 'invalid' ? 'border-red-500 focus:ring-red-500' :
                                'border-gray-500 focus:ring-yellow-500'
                              }`}
                              placeholder="Enter your name"
                            />
                            {profileValidation.full_name.status === 'valid' && (
                              <span className="absolute right-3 top-2.5 text-green-400">✅</span>
                            )}
                            {profileValidation.full_name.status === 'invalid' && (
                              <span className="absolute right-3 top-2.5 text-red-400">❌</span>
                            )}
                          </div>
                          {profileValidation.full_name.message && (
                            <p className={`mt-1 text-sm ${
                              profileValidation.full_name.status === 'valid' ? 'text-green-400' :
                              profileValidation.full_name.status === 'invalid' ? 'text-red-400' :
                              'text-yellow-400'
                            }`}>
                              {profileValidation.full_name.message}
                            </p>
                          )}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">Surname *</label>
                          <input
                            type="text"
                            required
                            value={profileEditorData.last_name}
                            disabled={!profileEditAcceptsAllFields(user?.database_user) && !missingProfileDetailFields(user?.database_user).includes('surname')}
                            onChange={(e) => setProfileEditorData(prev => ({ ...prev, last_name: e.target.value, full_name: `${prev.first_name} ${e.target.value}`.trim() }))}
                            className="w-full bg-gray-600 border border-gray-500 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-yellow-500 disabled:opacity-60"
                            placeholder="Enter your surname"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">Email *</label>
                          {/* SECURITY: Email cannot be directly edited - requires magic link verification */}
                          <div className="relative">
                            <input
                              type="email"
                              readOnly
                              disabled
                              value={profileEditorData.email}
                              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-gray-300 cursor-not-allowed"
                              title="Email changes require verification"
                            />
                            <span className="absolute right-3 top-2.5 text-white">🔒</span>
                          </div>
                          {/* v7.4.x — Email changes locked: admin-only */}
                          <p className="text-xs text-gray-500 mt-2">🔒 Email changes are managed by your administrator.</p>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">Country *</label>
                          <SearchableCountrySelector
                            value={profileEditorData.country_of_residence || ''}
                            disabled={!profileEditAcceptsAllFields(user?.database_user) && !missingProfileDetailFields(user?.database_user).includes('country')}
                            onChange={(code: string, _country: CountryOption) => setProfileEditorData(prev => ({ ...prev, country_of_residence: code }))}
                            placeholder="Choose your country..."
                            error={profileValidation.country_of_residence.status === 'invalid' ? profileValidation.country_of_residence.message : undefined}
                            priorityCountries={['ZAF', 'NAM', 'SWZ', 'BWA', 'GBR', 'USA']}
                            enableGeolocation={true}
                            showGeolocationSuggestion={true}
                          />
                          {profileValidation.country_of_residence.status === 'valid' && profileValidation.country_of_residence.message && (
                            <p className="mt-1 text-sm text-green-400">
                              {profileValidation.country_of_residence.message}
                            </p>
                          )}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">
                            Phone Number * {profileEditorData.country_of_residence && <span className="text-sm text-white">({getPhoneCodeForCountry(profileEditorData.country_of_residence)})</span>}
                          </label>
                          <div className="relative">
                            <input
                              type="tel"
                              value={profileEditorData.phone}
                              disabled={!profileEditAcceptsAllFields(user?.database_user) && !missingProfileDetailFields(user?.database_user).includes('phone')}
                              onChange={(e) => {
                                const value = e.target.value;
                                if (profileEditorData.country_of_residence && value && !value.startsWith('+')) {
                                  const formatted = formatPhoneWithCountryCode(value, profileEditorData.country_of_residence);
                                  setProfileEditorData(prev => ({ ...prev, phone: formatted }));
                                } else {
                                  setProfileEditorData(prev => ({ ...prev, phone: value }));
                                }
                              }}
                              className={`w-full bg-gray-600 border rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 ${
                                profileValidation.phone.status === 'valid' ? 'border-green-500 focus:ring-green-500' :
                                profileValidation.phone.status === 'invalid' ? 'border-red-500 focus:ring-red-500' :
                                'border-gray-500 focus:ring-yellow-500'
                              }`}
                              placeholder={profileEditorData.country_of_residence ? `${getPhoneCodeForCountry(profileEditorData.country_of_residence)} 12 345 6789` : 'Select country first'}
                            />
                            {profileValidation.phone.status === 'valid' && (
                              <span className="absolute right-3 top-2.5 text-green-400">✅</span>
                            )}
                            {profileValidation.phone.status === 'invalid' && (
                              <span className="absolute right-3 top-2.5 text-red-400">❌</span>
                            )}
                          </div>
                          {profileValidation.phone.message && (
                            <p className={`mt-1 text-sm ${
                              profileValidation.phone.status === 'valid' ? 'text-green-400' :
                              profileValidation.phone.status === 'invalid' ? 'text-red-400' :
                              'text-yellow-400'
                            }`}>
                              {profileValidation.phone.message}
                            </p>
                          )}
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Profile Description (WYSIWYG Editor)
                          <span className="ml-2 text-sm text-green-400 bg-green-900/30 px-2 py-0.5 rounded">Optional</span>
                        </label>
                        <ProfileDescriptionEditor
                          value={profileEditorData.profile_description}
                          onChange={(value) => setProfileEditorData(prev => ({ ...prev, profile_description: value }))}
                        />
                        <p className="text-sm text-white mt-2">
                          <span className="text-green-400">ℹ️ This field is optional.</span> Format your description with bold, italic, lists, and more. This will be displayed on your affiliate landing page. If left empty, a default description will be used.
                        </p>
                      </div>

                      <div className="flex gap-3 pt-4">
                        {mustCompleteSettings ? null : (
                        <button
                          type="button"
                          onClick={() => setIsProfileEditing(false)}
                          className="flex-1 bg-gray-600 hover:bg-gray-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
                          disabled={settingsLoading}
                        >
                          Cancel
                        </button>
                        )}
                        <button
                          type="button"
                          onClick={handleProfileUpdate}
                          className="flex-1 bg-yellow-600 hover:bg-yellow-700 text-white font-medium py-2 px-4 rounded-lg transition-colors disabled:opacity-50"
                          disabled={settingsLoading}
                        >
                          {settingsLoading ? '💾 Saving...' : '💾 Save'}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>

              <div className="bg-gray-700 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-white mb-2">🔒 Security</h3>
                <p className="text-gray-300 text-sm mb-4">Password and security preferences</p>

                <div className="space-y-4">
                  {/* Password Change */}
                  <div className="border-b border-gray-600 pb-4">
                    <h4 className="text-white font-medium mb-2">Change Password</h4>
                    <p className="text-white text-sm mb-3">Update your account password</p>
                    <button
                      onClick={() => setShowPasswordChangeModal(true)}
                      className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
                    >
                      Change Password
                    </button>
                  </div>

                  {/* 2FA Email PIN */}
                  <div className="border-b border-gray-600 pb-4">
                    <h4 className="text-white font-medium mb-2">Two-Factor Authentication (2FA)</h4>
                    <p className="text-white text-sm mb-3">
                      Email PIN verification for withdrawals and sensitive operations
                    </p>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-white text-sm">Email PIN Verification</p>
                        <p className="text-white text-sm">
                          {user?.database_user?.two_factor_enabled ?
                            'Enabled - PIN sent to your email for withdrawals' :
                            'Disabled - Enable for enhanced security'}
                        </p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={user?.database_user?.two_factor_enabled || false}
                          onChange={(e) => handle2FAToggle(e.target.checked)}
                          disabled={settingsLoading}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-yellow-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-yellow-600"></div>
                      </label>
                    </div>
                  </div>

                  {/* Authenticator App (TOTP) — v4.3.91 */}
                  {extractNumericUserId(user) > 0 && user?.database_user?.email && (
                    <TotpMfaPanel
                      userId={extractNumericUserId(user)}
                      email={user.database_user.email}
                    />
                  )}

                  {/* REMOVED: Account Security Status section - Email verification is unnecessary as emails are verified during registration */}
                </div>
              </div>

              {/* Language Preferences */}
              <div className="bg-gray-700 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-white mb-2">🌍 Language</h3>
                <p className="text-gray-300 text-sm mb-4">Choose your preferred language. This is the language used for your updates.</p>

                <div className="max-w-md">
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Language / Langue
                  </label>
                  <select
                    value={selectedLanguage}
                    onChange={(e) => {
                      const newLang = e.target.value;
                      setSelectedLanguage(newLang);
                      setLanguageChanged(newLang !== savedLanguage);
                    }}
                    disabled={languageSaving}
                    className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <option value="">Choose a language</option>
                    {languageOptions.map((lang) => (
                      <option key={lang.code} value={lang.code}>{lang.name}</option>
                    ))}
                  </select>

                  {languageChanged && (
                    <div className="mt-4 flex items-center gap-3">
                      <button
                        onClick={handleSaveLanguage}
                        disabled={languageSaving}
                        className="bg-yellow-600 hover:bg-yellow-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-medium py-2 px-6 rounded-lg transition-colors flex items-center gap-2"
                      >
                        {languageSaving ? (
                          <>
                            <span className="inline-block animate-spin">⏳</span>
                            Saving...
                          </>
                        ) : (
                          <>
                            💾 Save Language
                          </>
                        )}
                      </button>
                      <button
                        onClick={() => {
                          setSelectedLanguage(savedLanguage);
                          setLanguageChanged(false);
                        }}
                        disabled={languageSaving}
                        className="bg-gray-600 hover:bg-gray-500 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium py-2 px-4 rounded-lg transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  )}

                  <p className="text-white text-sm mt-2">
                    {languageChanged
                      ? '⚠️ Click "Save Language" to apply your changes'
                      : 'Select a language and click Save to apply changes'}
                  </p>
                </div>
              </div>

              <div className="bg-gray-700 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-white mb-2">🔔 Notifications</h3>
                <p className="text-gray-300 text-sm mb-4">Notification preferences</p>

                <div className="space-y-4">
                  {/* Email Notifications */}
                  <div className="border-b border-gray-600 pb-4">
                    <h4 className="text-white font-medium mb-3">Email Notifications</h4>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-white text-sm">Payment Confirmations</p>
                          <p className="text-white text-sm">Share purchases and commission payments</p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={notificationPreferences.payment_confirmations}
                            onChange={(e) => handleNotificationPreferenceChange('payment_confirmations', e.target.checked)}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-yellow-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-yellow-600"></div>
                        </label>
                      </div>

                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-white text-sm">Security Alerts</p>
                          <p className="text-white text-sm">Login attempts and security changes</p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={notificationPreferences.security_alerts}
                            onChange={(e) => handleNotificationPreferenceChange('security_alerts', e.target.checked)}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-yellow-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-yellow-600"></div>
                        </label>
                      </div>

                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-white text-sm">Commission Updates</p>
                          <p className="text-white text-sm">New referrals and commission earnings</p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={notificationPreferences.commission_updates}
                            onChange={(e) => handleNotificationPreferenceChange('commission_updates', e.target.checked)}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-yellow-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-yellow-600"></div>
                        </label>
                      </div>

                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-white text-sm">Company Updates</p>
                          <p className="text-white text-sm">Mining operations and company news</p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={notificationPreferences.marketing_emails}
                            onChange={(e) => handleNotificationPreferenceChange('marketing_emails', e.target.checked)}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-yellow-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-yellow-600"></div>
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Dashboard Notifications */}
                  <div className="border-b border-gray-600 pb-4">
                    <h4 className="text-white font-medium mb-3">Dashboard Notifications</h4>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-white text-sm">In-App Notifications</p>
                          <p className="text-white text-sm">Show notifications in dashboard</p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={notificationPreferences.in_app_notifications}
                            onChange={(e) => handleNotificationPreferenceChange('in_app_notifications', e.target.checked)}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-yellow-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-yellow-600"></div>
                        </label>
                      </div>

                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-white text-sm">Sound Notifications</p>
                          <p className="text-white text-sm">Play sound for important alerts</p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={notificationPreferences.sound_notifications}
                            onChange={(e) => handleNotificationPreferenceChange('sound_notifications', e.target.checked)}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-yellow-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-yellow-600"></div>
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Notification Frequency */}
                  <div>
                    <h4 className="text-white font-medium mb-3">Notification Frequency</h4>
                    <div className="space-y-2">
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="notification_frequency"
                          value="immediate"
                          checked={notificationPreferences.notification_frequency === 'immediate'}
                          onChange={(e) => handleNotificationPreferenceChange('notification_frequency', e.target.value)}
                          className="w-4 h-4 text-yellow-600 bg-gray-600 border-gray-500 focus:ring-yellow-500"
                        />
                        <span className="ml-2 text-white text-sm">Immediate</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="notification_frequency"
                          value="daily"
                          checked={notificationPreferences.notification_frequency === 'daily'}
                          onChange={(e) => handleNotificationPreferenceChange('notification_frequency', e.target.value)}
                          className="w-4 h-4 text-yellow-600 bg-gray-600 border-gray-500 focus:ring-yellow-500"
                        />
                        <span className="ml-2 text-white text-sm">Daily Summary</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="notification_frequency"
                          value="weekly"
                          checked={notificationPreferences.notification_frequency === 'weekly'}
                          onChange={(e) => handleNotificationPreferenceChange('notification_frequency', e.target.value)}
                          className="w-4 h-4 text-yellow-600 bg-gray-600 border-gray-500 focus:ring-yellow-500"
                        />
                        <span className="ml-2 text-white text-sm">Weekly Summary</span>
                      </label>
                    </div>
                  </div>

                  <div className="pt-2">
                    <button
                      onClick={handleSaveNotificationPreferences}
                      disabled={notificationPreferencesLoading}
                      className="bg-yellow-600 hover:bg-yellow-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-medium py-2 px-4 rounded-lg transition-colors flex items-center space-x-2"
                    >
                      {notificationPreferencesLoading && (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      )}
                      <span>
                        {notificationPreferencesLoading ? 'Saving...' : 'Save Notification Preferences'}
                      </span>
                    </button>

                    {/* Success/Error Message */}
                    {notificationPreferencesMessage && (
                      <div className={`mt-3 p-3 rounded-lg ${
                        notificationPreferencesMessage.type === 'success'
                          ? 'bg-green-600 text-white'
                          : 'bg-red-600 text-white'
                      }`}>
                        {notificationPreferencesMessage.text}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Clear Cache Section */}
              <div className="mt-6 pt-6 border-t border-gray-600">
                <h3 className="text-lg font-semibold text-white mb-4">🔄 Data & Cache</h3>
                <div className="bg-gray-700 rounded-lg p-4">
                  <p className="text-gray-300 text-sm mb-4">
                    If you're experiencing data display issues or seeing outdated information,
                    clearing the cache will fetch fresh data from the server.
                  </p>
                  <button
                    onClick={async () => {
                      if (confirm('This will clear all cached data and refresh your session. Continue?')) {
                        console.log('🧹 User requested cache clear...');
                        // Clear all localStorage user data
                        localStorage.removeItem('aureus_user');
                        localStorage.removeItem('aureus_test_user');
                        localStorage.removeItem('aureus_session');
                        localStorage.removeItem('aureus_email_user');
                        localStorage.removeItem('aureus_auth_timestamp');
                        // Clear any Supabase cache
                        Object.keys(localStorage).forEach(key => {
                          if (key.startsWith('supabase.auth.') || key.startsWith('sb-')) {
                            localStorage.removeItem(key);
                          }
                        });
                        console.log('✅ Cache cleared, reloading page...');
                        // Force reload the page to get fresh data
                        window.location.reload();
                      }
                    }}
                    className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors flex items-center space-x-2"
                  >
                    <span>🔄 Clear Cache & Refresh Data</span>
                  </button>
                  <p className="text-gray-500 text-sm mt-2">
                    Note: You may need to log in again after clearing the cache.
                  </p>
                </div>
              </div>
            </div>
          </div>
        ) : activeSection === 'beneficiaries' ? (
          <div className="bg-gray-800 rounded-lg border border-gray-700 p-6">
            <BeneficiaryManager userId={extractNumericId(user?.database_user?.id || user?.id) || 0} />
          </div>
        ) : activeSection === 'invoices' ? (
          <UserInvoices
            userId={extractNumericId(user?.database_user?.id || user?.id) || 0}
            className=""
          />
        ) : activeSection === 'company-accounts' ? (
          <CompanyDashboardSection
            userId={extractNumericId(user?.database_user?.id || user?.id) || 0}
            onRefreshData={async () => {
              // Refresh dashboard data if needed
              console.log('Company data refreshed');
            }}
          />
        ) : activeSection === 'kyc' ? (
          <div className="bg-gray-800 rounded-lg border border-gray-700 p-6">
            <h2 className="text-2xl font-bold text-white mb-4">🔐 KYC Verification</h2>
            <p className="text-white mb-6">
              Complete your Know Your Customer (KYC) verification to unlock full access to your account features,
              including commission withdrawals.
            </p>

            <div className="space-y-6">
              {/* Smart KYC Router - Show appropriate component based on status and field rejections */}
              <SmartKYCRouter
                userId={extractNumericUserId(user)}
                kycStatus={kycStatus}
                kycId={kycId}
                onKYCStatusChange={() => {
                  const userId = extractNumericUserId(user);
                  if (userId) {
                    console.log('🎉 KYC status changed, reloading KYC status and checking profile completeness...');
                    loadKYCStatus(userId);
                    loadKycCompliance(userId);
                    // Check if profile is complete (has both first and last name)
                    setTimeout(() => {
                      const fullName = user?.database_user?.full_name || '';
                      const nameParts = fullName.trim().split(/\s+/);
                      const isProfileComplete = fullName.trim() && nameParts.length >= 2;

                      if (!isProfileComplete) {
                        console.log('⚠️ Profile incomplete - redirecting to settings to complete profile');
                        setActiveSection('settings');
                        // Show message about completing profile
                        setSettingsMessage({
                          type: 'error',
                          text: '📝 Please complete your profile with first and last name to continue'
                        });
                      } else {
                        console.log('✅ Profile complete - redirecting to overview');
                        setActiveSection('overview');
                      }
                    }, 1000);
                  }
                }}
              />
            </div>
          </div>
        ) : activeSection === 'legal-documents' ? (
          <div className="bg-gray-800 rounded-lg border border-gray-700 p-6">
            <h2 className="text-2xl font-bold text-white mb-6">📋 Legal Documents & Profile</h2>

            {/* Enhanced Compliance Status Section */}
            {(() => {
              // Calculate progress correctly - count only the actual document agreements
              const completedCount = [
                agreementStatus.terms_conditions,
                agreementStatus.privacy_policy,
                agreementStatus.disclaimer,
                agreementStatus.share_purchase_agreement
              ].filter(Boolean).length

              const totalCount = 4
              const progressPercentage = Math.round((completedCount / totalCount) * 100)

              return (
                <div className="mb-6">
                  {/* Overall Progress Card */}
                  <div className={`rounded-lg p-6 mb-4 ${
                    mustCompleteLegalDocuments
                      ? 'bg-orange-900/20 border border-orange-500/30'
                      : 'bg-green-900/20 border border-green-500/30'
                  }`}>
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center space-x-3">
                        <div className={`text-2xl ${mustCompleteLegalDocuments ? 'text-orange-400' : 'text-green-400'}`}>
                          {mustCompleteLegalDocuments ? '⚠️' : '✅'}
                        </div>
                        <div>
                          <h3 className={`font-semibold text-lg ${mustCompleteLegalDocuments ? 'text-orange-400' : 'text-green-400'}`}>
                            {mustCompleteLegalDocuments ? 'Legal Document Compliance Required' : 'Legal Documents Complete'}
                          </h3>
                          <p className={`text-sm ${mustCompleteLegalDocuments ? 'text-orange-200' : 'text-green-200'}`}>
                            {mustCompleteLegalDocuments
                              ? 'Complete all legal documents to access your dashboard'
                              : 'All legal documents have been agreed to'
                            }
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className={`text-2xl font-bold ${mustCompleteLegalDocuments ? 'text-orange-400' : 'text-green-400'}`}>
                          {completedCount}/{totalCount}
                        </div>
                        <div className={`text-sm ${mustCompleteLegalDocuments ? 'text-orange-300' : 'text-green-300'}`}>
                          {progressPercentage}% Complete
                        </div>
                      </div>
                    </div>

                    {/* Progress Bar */}
                    <div className="mb-4">
                      <div className="flex justify-between text-sm mb-2">
                        <span className={mustCompleteLegalDocuments ? 'text-orange-300' : 'text-green-300'}>
                          Progress: {completedCount} of {totalCount} documents completed
                        </span>
                        <span className={mustCompleteLegalDocuments ? 'text-orange-300' : 'text-green-300'}>
                          {progressPercentage}%
                        </span>
                      </div>
                      <div className="w-full bg-gray-700 rounded-full h-3">
                        <div
                          className={`h-3 rounded-full transition-all duration-500 ${
                            mustCompleteLegalDocuments ? 'bg-orange-500' : 'bg-green-500'
                          }`}
                          style={{ width: `${progressPercentage}%` }}
                        ></div>
                      </div>
                    </div>

                    {/* Next Steps */}
                    {mustCompleteLegalDocuments && (
                      <div className="bg-orange-800/30 rounded-lg p-3">
                        <h4 className="text-orange-300 font-medium mb-2">Next Steps:</h4>
                        <ul className="text-orange-200 text-sm space-y-1">
                          {!agreementStatus.terms_conditions && <li>• Read and agree to Terms & Conditions</li>}
                          {!agreementStatus.privacy_policy && <li>• Read and agree to Privacy Policy</li>}
                          {!agreementStatus.disclaimer && <li>• Read and agree to Legal Disclaimer</li>}
                          {!agreementStatus.share_purchase_agreement && <li>• Read and agree to Share Purchase Agreement</li>}
                        </ul>

                        {/* REMOVED: Force Dashboard Access button - SECURITY RISK
                            Users MUST complete legal documents before accessing dashboard
                            This is a LEGAL COMPLIANCE REQUIREMENT - no bypasses allowed */}
                      </div>
                    )}
                  </div>
                </div>
              )
            })()}

            <p className="text-white mb-4">
              {mustCompleteLegalDocuments
                ? "Please review and agree to all documents below to access your dashboard."
                : "Access important legal documents and agreements. Your agreement status is tracked for compliance."
              }
            </p>

            {/* Manual Refresh Button */}
            <div className="mb-6">
              <button
                onClick={async () => {
                  console.log('🔄 LEGAL DOCS: Manual refresh button clicked')
                  if (!user?.id) {
                    console.log('❌ LEGAL DOCS: No user ID available for manual refresh')
                    return
                  }

                  // Use database_user.id if available, fallback to user.id
                  const userIdForCheck = user?.database_user?.id || user.id
                  console.log('🔄 LEGAL DOCS: Manual refresh using ID:', userIdForCheck, 'Type:', typeof userIdForCheck)
                  console.log('🔄 LEGAL DOCS: User object for manual refresh:', {
                    user_id: user.id,
                    database_user_id: user?.database_user?.id,
                    email: user?.email || user?.database_user?.email
                  })

                  setIsCheckingCompliance(true)
                  try {
                    const status = await LegalDocumentService.checkAgreementStatus(userIdForCheck)
                    console.log('📋 LEGAL DOCS: Manual refresh result:', status)
                    console.log('📋 LEGAL DOCS: Manual refresh individual status:', {
                      terms_conditions: status.terms_conditions,
                      privacy_policy: status.privacy_policy,
                      disclaimer: status.disclaimer,
                      share_purchase_agreement: status.share_purchase_agreement,
                      all_agreed: status.all_agreed
                    })

                    setAgreementStatus(status)

                    if (!status.all_agreed) {
                      console.log('⚠️ LEGAL DOCS: Manual refresh shows incomplete documents')
                      setMustCompleteLegalDocuments(true)
                      setActiveSection('legal-documents')
                    } else {
                      console.log('✅ LEGAL DOCS: Manual refresh confirms all documents completed')
                      setMustCompleteLegalDocuments(false)
                      setActiveSection('overview')
                      setMessage({
                        type: 'success',
                        text: 'Legal documents status refreshed - all documents completed!'
                      })
                      setTimeout(() => setMessage(null), 3000)
                    }
                  } catch (error) {
                    console.error('❌ LEGAL DOCS: Error during manual refresh:', error)
                    console.error('❌ LEGAL DOCS: Manual refresh error details:', {
                      message: error instanceof Error ? error.message : String(error),
                      stack: error instanceof Error ? error.stack : undefined,
                      user_id: userIdForCheck
                    })
                  } finally {
                    setIsCheckingCompliance(false)
                  }
                }}
                disabled={isCheckingCompliance}
                className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-800 text-white px-4 py-2 rounded-lg text-sm transition-colors flex items-center gap-2"
              >
                {isCheckingCompliance ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Checking Status...
                  </>
                ) : (
                  <>
                    🔄 Refresh Status
                  </>
                )}
              </button>
            </div>



            {/* Enhanced Individual Document Cards */}
            <div className="space-y-4 mb-6">
              {[
                {
                  title: 'Terms & Conditions',
                  description: 'Platform usage terms and share purchase conditions',
                  onClick: () => setShowTermsModal(true),
                  completed: agreementStatus.terms_conditions,
                  icon: (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  )
                },
                {
                  title: 'Privacy Policy',
                  description: 'Data protection and POPIA compliance policy',
                  onClick: () => setShowPrivacyModal(true),
                  completed: agreementStatus.privacy_policy,
                  icon: (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  )
                },
                {
                  title: 'Legal Disclaimer',
                  description: 'Risk disclosure and liability limitations',
                  onClick: () => setShowDisclaimerModal(true),
                  completed: agreementStatus.disclaimer,
                  icon: (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                  )
                },
                {
                  title: 'Share Purchase Agreement',
                  description: 'Comprehensive share purchase agreement with terms and conditions',
                  onClick: () => setShowSharePurchaseAgreementModal(true),
                  completed: agreementStatus.share_purchase_agreement,
                  icon: (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                  )
                }
              ].map((doc, index) => (
                <div key={index} className={`rounded-lg p-5 border transition-all duration-200 hover:shadow-lg ${
                  doc.completed
                    ? 'bg-green-900/10 border-green-500/30 hover:border-green-500/50'
                    : 'bg-gray-800/50 border-gray-600 hover:border-blue-500/50'
                }`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-start space-x-4">
                      <div className={`p-3 rounded-lg ${
                        doc.completed ? 'bg-green-500/20 text-green-400' : 'bg-gray-700 text-white'
                      }`}>
                        {doc.icon}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <h3 className="text-white font-semibold text-lg">{doc.title}</h3>
                          {doc.completed && (
                            <div className="flex items-center space-x-1 bg-green-500/20 text-green-300 px-2 py-1 rounded-full text-sm">
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                              <span>Agreed</span>
                            </div>
                          )}
                        </div>
                        <p className="text-gray-300 text-sm mb-3">{doc.description}</p>
                        {!doc.completed && (
                          <div className="flex items-center space-x-2 text-orange-400 text-sm">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span>Action Required: Please review and agree to continue</span>
                          </div>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={doc.onClick}
                      className={`px-6 py-2 rounded-lg font-medium transition-all duration-200 ${
                        doc.completed
                          ? 'bg-green-600/20 text-green-300 border border-green-500/30 hover:bg-green-600/30'
                          : 'bg-blue-600 hover:bg-blue-700 text-white shadow-lg hover:shadow-xl'
                      }`}
                    >
                      {doc.completed ? 'Review' : 'View & Agree'}
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Official Company Documents Section */}
            <div className="mt-8">
              <h3 className="text-xl font-bold text-white mb-4 flex items-center">
                🏢 Official Company Documents
              </h3>
              <p className="text-white mb-6">
                View our official company registration, tax compliance, and banking documents for transparency and verification.
              </p>

              <div className="space-y-3">
                {/* CIPC Certificate */}
                <div className="flex items-center justify-between bg-gray-700 rounded-lg p-4">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <h4 className="text-white font-medium">📜 CIPC Certificate</h4>
                      <span className="bg-green-500 text-white text-sm px-2 py-1 rounded">Verified</span>
                    </div>
                    <p className="text-white text-sm">Official company registration certificate from the Companies and Intellectual Property Commission (CIPC)</p>
                  </div>
                  <button
                    onClick={() => window.open('https://fgubaqoftdeefcakejwu.supabase.co/storage/v1/object/public/assets/cipc.pdf', '_blank')}
                    className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded text-sm transition-colors flex items-center space-x-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <span>View PDF</span>
                  </button>
                </div>

                {/* SARS Tax Certificate */}
                <div className="flex items-center justify-between bg-gray-700 rounded-lg p-4">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <h4 className="text-white font-medium">💰 SARS Tax Certificate</h4>
                      <span className="bg-green-500 text-white text-sm px-2 py-1 rounded">Verified</span>
                    </div>
                    <p className="text-white text-sm">South African Revenue Service (SARS) tax compliance certificate confirming good standing</p>
                  </div>
                  <button
                    onClick={() => window.open('https://fgubaqoftdeefcakejwu.supabase.co/storage/v1/object/public/assets/sars.pdf', '_blank')}
                    className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded text-sm transition-colors flex items-center space-x-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <span>View PDF</span>
                  </button>
                </div>

                {/* FNB Bank Proof */}
                <div className="flex items-center justify-between bg-gray-700 rounded-lg p-4">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <h4 className="text-white font-medium">🏦 FNB Bank Proof</h4>
                      <span className="bg-green-500 text-white text-sm px-2 py-1 rounded">Verified</span>
                    </div>
                    <p className="text-white text-sm">First National Bank (FNB) proof of account document confirming official company banking</p>
                  </div>
                  <button
                    onClick={() => window.open('https://fgubaqoftdeefcakejwu.supabase.co/storage/v1/object/public/assets/fnb.pdf', '_blank')}
                    className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded text-sm transition-colors flex items-center space-x-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <span>View PDF</span>
                  </button>
                </div>
              </div>

              {/* Document Verification Notice */}
              <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-4 mt-6">
                <h4 className="text-blue-400 font-semibold mb-2">🔍 Document Verification</h4>
                <p className="text-gray-300 text-sm">
                  All official company documents are verified and up-to-date. These documents demonstrate our legal compliance,
                  tax good standing, and legitimate banking relationships. You can verify these documents independently with
                  the respective authorities if needed.
                </p>
              </div>
            </div>
          </div>
        ) : activeSection === 'gold-diggers' ? (
          <GoldDiggersDashboard
            user={user}
            onBack={() => handleSectionChange('overview')}
          />
        ) : activeSection === 'vein-accord' ? (
          isGlobalTopAffiliatePoolTester(user?.database_user?.username || user?.username)
            ? <ProductionVeinAccord />
            : <p className="text-gray-300">The Global Top Affiliate Pool is not open for this account.</p>
        ) : activeSection === 'zimbabwe-awaits' ? (
          <ZimbabweAwaitsPage
            onPurchaseShares={() => {
              if (mustCompleteLegalDocuments || mustCompleteSettings || mustAddSponsor) {
                handleSectionChange('purchase-shares')
                return
              }
              window.history.pushState({}, '', '/dashboard?section=purchase-shares')
              handleSectionChange('purchase-shares')
              setShowPurchaseFlow(true)
            }}
            onRallyTeam={onSwitchDashboard ? () => {
              window.history.pushState({}, '', '/dashboard?section=referrals')
              onSwitchDashboard('affiliate')
            } : undefined}
            currentUsername={user?.database_user?.username || user?.username}
          />
        ) : activeSection === 'acres' ? (
          // v4.7.78: ACRES Rewards is restricted to user 106 during pre-rollout
          // testing. Defense-in-depth client gate; the server-side ACRES RPCs
          // are also bound to auth.uid() via RLS. Non-106 users fall through to
          // the "section coming soon" fallback below.
          extractNumericId(user?.database_user?.id || user?.id) === 106 ? (
            <div className="bg-gray-800 rounded-lg border border-gray-700 p-6">
              <AcresUserDashboard user={user} />
            </div>
          ) : (
            <div className="bg-gray-800 rounded-lg border border-gray-700 p-6">
              <h2 className="text-2xl font-bold text-white mb-4">ACRES Rewards</h2>
              <p className="text-white">This section is coming soon. Please check back later.</p>
            </div>
          )
        ) : activeSection === 'share-audit' ? (
          <ShareAuditPage
            currentUserId={extractNumericId(user?.database_user?.id || user?.id) || 0}
            onBack={() => handleSectionChange('overview')}
          />
        ) : (
          // Fallback for any unhandled sections
          <div className="bg-gray-800 rounded-lg border border-gray-700 p-6">
            <h2 className="text-2xl font-bold text-white mb-4">
              {activeSection.charAt(0).toUpperCase() + activeSection.slice(1).replace('-', ' ')}
            </h2>
            <p className="text-white">
              This section is coming soon. Please check back later.
            </p>
          </div>
        )}

        {/* Version Footer */}
        <div style={{
          marginTop: '40px',
          padding: '16px',
          borderTop: '1px solid #374151',
          textAlign: 'center'
        }}>
          <p style={{
            color: '#ffffff',
            fontSize: '13px',
            margin: 0
          }}>
            Aureus Africa {getFullVersion()} • {new Date().getFullYear()} Aureus Alliance Holdings (Pty) Ltd
          </p>
          {/* Debug refresh button for testing (only show for user ID 106) */}
          {user?.database_user?.id === 106 && (
            <button
              onClick={handleRefreshUserData}
              style={{
                marginTop: '8px',
                padding: '4px 8px',
                fontSize: '13px',
                backgroundColor: '#374151',
                color: '#ffffff',
                border: '1px solid #4B5563',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
              title="Refresh user data from database (clears cache)"
            >
              🔄 Refresh User Data
            </button>
          )}
        </div>
      </div>

      </div>

      {/* Share Purchase Flow Modal */}
      {showPurchaseFlow && (
        <SharePurchaseFlow
          user={user}
          resumePayment={resumePaymentData}
          birthdayPackage={birthdayPackage}
          productionCapitalPackage={productionCapitalPackage}
          onClose={() => {
            setShowPurchaseFlow(false)
            setResumePaymentData(undefined) // Clear resume data when modal closes
            setBirthdayPackage(undefined) // Clear birthday selection on close
            setProductionCapitalPackage(undefined)
          }}
          onPurchaseComplete={(isFirstPurchase) => {
            // Close purchase flow
            setShowPurchaseFlow(false);
            setResumePaymentData(undefined);

            // Only redirect to KYC if it's not already completed
            // kycStatus is available in this component's scope
            if (isFirstPurchase && kycStatus !== 'approved') {
              // KYC not completed - redirect to KYC page
              setMessage({
                type: 'success',
                text: '🎉 Purchase successful! Please complete KYC verification to unlock all features including referral commissions and withdrawals.'
              });
              setTimeout(() => setMessage(null), 8000);

              // Redirect to KYC section after a short delay
              setTimeout(() => {
                setActiveSection('kyc');
              }, 2000);
            } else {
              // KYC already completed or not first purchase - stay on overview
              setMessage({
                type: 'success',
                text: '🎉 Purchase successful! Your shares have been added to your portfolio.'
              });
              setTimeout(() => setMessage(null), 5000);
            }
          }}
        />
      )}

      {/* Payment Result Modal (Yoco callback) */}
      {showPaymentResult && (
        <div
          className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={() => setShowPaymentResult(null)}
        >
          <div
            className="bg-gray-900 rounded-2xl p-8 max-w-md w-full border-2 text-center"
            style={{
              borderColor: showPaymentResult.status === 'success' ? '#10b981' :
                           showPaymentResult.status === 'cancelled' ? '#f59e0b' : '#ef4444'
            }}
            onClick={e => e.stopPropagation()}
          >
            {/* Icon */}
            <div className="text-6xl mb-6">
              {showPaymentResult.status === 'success' ? '✅' :
               showPaymentResult.status === 'cancelled' ? '⚠️' :
               showPaymentResult.status === 'pending' ? '⏳' : '❌'}
            </div>

            {/* Title */}
            <h2 className="text-2xl font-bold mb-4" style={{
              color: showPaymentResult.status === 'success' ? '#10b981' :
                     showPaymentResult.status === 'cancelled' ? '#f59e0b' :
                     showPaymentResult.status === 'pending' ? '#3b82f6' : '#ef4444'
            }}>
              {showPaymentResult.status === 'success' ? '🎉 Payment Successful!' :
               showPaymentResult.status === 'cancelled' ? 'Payment Cancelled' :
               showPaymentResult.status === 'pending' ? 'Payment Pending' :
               showPaymentResult.status === 'notfound' ? 'Payment Not Found' : 'Payment Failed'}
            </h2>

            {/* Success Details */}
            {showPaymentResult.status === 'success' && (
              <div className="bg-gray-800 rounded-xl p-4 mb-6 text-left">
                <div className="space-y-3">
                  {showPaymentResult.shares && (
                    <div className="flex justify-between">
                      <span className="text-white">Shares Purchased:</span>
                      <span className="text-white font-bold">{parseFloat(showPaymentResult.shares).toFixed(2)}</span>
                    </div>
                  )}
                  {showPaymentResult.amount && (
                    <div className="flex justify-between">
                      <span className="text-white">Amount Paid:</span>
                      <span className="text-green-400 font-bold">${parseFloat(showPaymentResult.amount).toFixed(2)} USD</span>
                    </div>
                  )}
                  {showPaymentResult.reference && (
                    <div className="flex justify-between">
                      <span className="text-white">Reference:</span>
                      <span className="text-white font-mono text-sm">{showPaymentResult.reference}</span>
                    </div>
                  )}
                  {showPaymentResult.method && (
                    <div className="flex justify-between">
                      <span className="text-white">Payment Method:</span>
                      <span className="text-white capitalize">{showPaymentResult.method}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Messages */}
            <p className="text-white mb-6">
              {showPaymentResult.status === 'success'
                ? 'Your shares have been added to your portfolio. Thank you for your share purchase!'
                : showPaymentResult.status === 'cancelled'
                ? 'Your payment was cancelled. No charges have been made to your account.'
                : showPaymentResult.status === 'pending'
                ? 'Your payment is being processed. It may take a few minutes to complete.'
                : showPaymentResult.status === 'notfound'
                ? 'We could not find your payment record. Please contact support if you were charged.'
                : `Payment could not be processed. ${showPaymentResult.reason || 'Please try again or use a different payment method.'}`}
            </p>

            {/* Actions */}
            <div className="flex gap-3">
              <button
                onClick={() => setShowPaymentResult(null)}
                className="flex-1 bg-gray-700 hover:bg-gray-600 text-white px-6 py-3 rounded-lg font-medium transition-colors"
              >
                Close
              </button>
              {showPaymentResult.status === 'success' && (
                <button
                  onClick={() => {
                    setShowPaymentResult(null)
                    setActiveSection('portfolio')
                  }}
                  className="flex-1 bg-green-600 hover:bg-green-500 text-white px-6 py-3 rounded-lg font-medium transition-colors"
                >
                  View Portfolio
                </button>
              )}
              {(showPaymentResult.status === 'error' || showPaymentResult.status === 'failed') && (
                <button
                  onClick={() => {
                    setShowPaymentResult(null)
                    setShowPurchaseFlow(true)
                  }}
                  className="flex-1 bg-blue-600 hover:bg-blue-500 text-white px-6 py-3 rounded-lg font-medium transition-colors"
                >
                  Try Again
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Shares Modal temporarily disabled to fix build error. */}
      {null}

      {/* Profile Editor Modal - REMOVED - Now using inline editing in Profile Settings section */}

      {/* Password Change Modal */}
      {showPasswordChangeModal && user?.database_user && extractNumericUserId(user) > 0 && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="max-w-md w-full">
            <SecurePasswordChangeForm
              userId={extractNumericUserId(user)}
              userEmail={user.database_user.email || user.email}
              currentUsername={user.database_user.username || ''}
              onPasswordChanged={() => {
                setSettingsMessage({
                  type: 'success',
                  text: 'Password changed successfully!'
                });
                setTimeout(() => setSettingsMessage(null), 3000);
              }}
              onClose={() => setShowPasswordChangeModal(false)}
            />
          </div>
        </div>
      )}

      {/* Email Verification PIN Modal */}
      {showEmailVerificationPinModal && user?.database_user?.email && extractNumericUserId(user) > 0 && (
        <PinVerificationModal
          isOpen={showEmailVerificationPinModal}
          onClose={() => setShowEmailVerificationPinModal(false)}
          onVerified={handleEmailVerificationPinSuccess}
          userId={extractNumericUserId(user)}
          email={user.database_user.email}
          purpose="profile_update"
          title="Verify Your Email Address"
          description="Enter the 6-digit code sent to your email to verify your account."
        />
      )}

      {/* Legal Documents Modals */}
      {showTermsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
          <div className="bg-gray-800 rounded-lg max-w-4xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-700 flex-shrink-0">
              <h2 className="text-lg sm:text-xl font-bold text-white">Terms & Conditions</h2>
              <button
                onClick={() => setShowTermsModal(false)}
                className="text-gray-400 hover:text-white transition-colors flex-shrink-0"
              >
                <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-4 sm:p-6 overflow-y-auto flex-1">
              <TermsAndConditions />
            </div>
            {!agreementStatus.terms_conditions && (
              <div className="border-t border-gray-700 p-4 sm:p-6 bg-gray-900 flex-shrink-0">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <label htmlFor="agree-terms" className="flex items-start sm:items-center space-x-3 cursor-pointer flex-1">
                    <input
                      type="checkbox"
                      id="agree-terms"
                      className="w-4 h-4 mt-0.5 sm:mt-0 flex-shrink-0 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-300">
                      I have read and agree to the Terms & Conditions
                    </span>
                  </label>
                  <button
                    onClick={() => {
                      const checkbox = document.getElementById('agree-terms') as HTMLInputElement
                      if (checkbox?.checked) {
                        handleDocumentAgreement('terms_conditions', () => setShowTermsModal(false))
                      } else {
                        alert('Please check the agreement checkbox first.')
                      }
                    }}
                    disabled={agreementLoading}
                    className="w-full sm:w-auto bg-green-600 hover:bg-green-700 disabled:bg-gray-600 text-white px-6 py-3 sm:py-2 rounded text-sm font-medium transition-colors"
                  >
                    {agreementLoading ? 'Recording...' : 'Agree & Close'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {showPrivacyModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
          <div className="bg-gray-800 rounded-lg max-w-4xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-700 flex-shrink-0">
              <h2 className="text-lg sm:text-xl font-bold text-white">Privacy Policy</h2>
              <button
                onClick={() => setShowPrivacyModal(false)}
                className="text-gray-400 hover:text-white transition-colors flex-shrink-0"
              >
                <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-4 sm:p-6 overflow-y-auto flex-1">
              <ComprehensivePrivacyPolicy />
            </div>
            {!agreementStatus.privacy_policy && (
              <div className="border-t border-gray-700 p-4 sm:p-6 bg-gray-900 flex-shrink-0">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <label htmlFor="agree-privacy" className="flex items-start sm:items-center space-x-3 cursor-pointer flex-1">
                    <input
                      type="checkbox"
                      id="agree-privacy"
                      className="w-4 h-4 mt-0.5 sm:mt-0 flex-shrink-0 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-300">
                      I have read and agree to the Privacy Policy
                    </span>
                  </label>
                  <button
                    onClick={() => {
                      const checkbox = document.getElementById('agree-privacy') as HTMLInputElement
                      if (checkbox?.checked) {
                        handleDocumentAgreement('privacy_policy', () => setShowPrivacyModal(false))
                      } else {
                        alert('Please check the agreement checkbox first.')
                      }
                    }}
                    disabled={agreementLoading}
                    className="w-full sm:w-auto bg-green-600 hover:bg-green-700 disabled:bg-gray-600 text-white px-6 py-3 sm:py-2 rounded text-sm font-medium transition-colors"
                  >
                    {agreementLoading ? 'Recording...' : 'Agree & Close'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {showDisclaimerModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
          <div className="bg-gray-800 rounded-lg max-w-4xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-700 flex-shrink-0">
              <h2 className="text-lg sm:text-xl font-bold text-white">Legal Disclaimer</h2>
              <button
                onClick={() => setShowDisclaimerModal(false)}
                className="text-gray-400 hover:text-white transition-colors flex-shrink-0"
              >
                <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-4 sm:p-6 overflow-y-auto flex-1">
              <ComprehensiveDisclaimer />
            </div>
            {!agreementStatus.disclaimer && (
              <div className="border-t border-gray-700 p-4 sm:p-6 bg-gray-900 flex-shrink-0">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <label htmlFor="agree-disclaimer" className="flex items-start sm:items-center space-x-3 cursor-pointer flex-1">
                    <input
                      type="checkbox"
                      id="agree-disclaimer"
                      className="w-4 h-4 mt-0.5 sm:mt-0 flex-shrink-0 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-300">
                      I have read and acknowledge the Legal Disclaimer
                    </span>
                  </label>
                  <button
                    onClick={() => {
                      const checkbox = document.getElementById('agree-disclaimer') as HTMLInputElement
                      if (checkbox?.checked) {
                        handleDocumentAgreement('disclaimer', () => setShowDisclaimerModal(false))
                      } else {
                        alert('Please check the acknowledgment checkbox first.')
                      }
                    }}
                    disabled={agreementLoading}
                    className="w-full sm:w-auto bg-green-600 hover:bg-green-700 disabled:bg-gray-600 text-white px-6 py-3 sm:py-2 rounded text-sm font-medium transition-colors"
                  >
                    {agreementLoading ? 'Recording...' : 'Acknowledge & Close'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Share Purchase Agreement Modal */}
      {showSharePurchaseAgreementModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
          <div className="bg-gray-800 rounded-lg max-w-4xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-700 flex-shrink-0">
              <h2 className="text-lg sm:text-xl font-bold text-white">Share Purchase Agreement</h2>
              <button
                onClick={() => setShowSharePurchaseAgreementModal(false)}
                className="text-gray-400 hover:text-white transition-colors flex-shrink-0"
              >
                <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-4 sm:p-6 overflow-y-auto flex-1">
              <SharePurchaseAgreement />
            </div>
            {!agreementStatus.share_purchase_agreement && (
              <div className="border-t border-gray-700 p-4 sm:p-6 bg-gray-900 flex-shrink-0">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <label htmlFor="agree-share-purchase" className="flex items-start sm:items-center space-x-3 cursor-pointer flex-1">
                    <input
                      type="checkbox"
                      id="agree-share-purchase"
                      className="w-4 h-4 mt-0.5 sm:mt-0 flex-shrink-0 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-300">
                      I have read and agree to the Share Purchase Agreement
                    </span>
                  </label>
                  <button
                    onClick={() => {
                      const checkbox = document.getElementById('agree-share-purchase') as HTMLInputElement
                      if (checkbox?.checked) {
                        handleDocumentAgreement('share_purchase_agreement', () => setShowSharePurchaseAgreementModal(false))
                      } else {
                        alert('Please check the agreement checkbox first.')
                      }
                    }}
                    disabled={agreementLoading}
                    className="w-full sm:w-auto bg-green-600 hover:bg-green-700 disabled:bg-gray-600 text-white px-6 py-3 sm:py-2 rounded text-sm font-medium transition-colors"
                  >
                    {agreementLoading ? 'Recording...' : 'Agree & Close'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {showShareAgreementModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b border-gray-700">
              <h2 className="text-xl font-bold text-white">Share Purchase Agreement Template</h2>
              <button
                onClick={() => setShowShareAgreementModal(false)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              <div className="text-white">
                <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-4 mb-6">
                  <h3 className="text-blue-400 font-bold mb-2">📋 Share Purchase Agreement Template</h3>
                  <p className="text-sm text-gray-300">
                    This is a template of the standard share purchase agreement. Actual agreements are generated
                    automatically upon share purchase and include specific details such as share quantities,
                    prices, and certificate numbers.
                  </p>
                </div>

                <div className="space-y-4 text-gray-300">
                  <h3 className="text-lg font-semibold text-white">Share Purchase Agreement</h3>
                  <p>
                    This Share Purchase Agreement ("Agreement") is entered into between Aureus Alliance Holdings (Pty) Ltd
                    ("Company") and the purchaser ("Shareholder") for the acquisition of no par value shares in the Company.
                  </p>

                  <h4 className="text-white font-semibold">1. Company Details</h4>
                  <p>
                    <strong>Company Name:</strong> Aureus Alliance Holdings (Pty) Ltd<br/>
                    <strong>Registration Number:</strong> 2025/368711/07<br/>
                    <strong>Registered Address:</strong> 1848 Mees Avenue, Randpark Ridge, Randburg, Gauteng, 2169
                  </p>

                  <h4 className="text-white font-semibold">2. Share Details</h4>
                  <p>
                    The shares being purchased are no par value shares in Aureus Alliance Holdings (Pty) Ltd,
                    a company incorporated under the laws of South Africa. Each share represents direct equity
                    ownership in the Company's gold mining operations.
                  </p>

                  <h4 className="text-white font-semibold">3. Purchase Terms</h4>
                  <p>
                    Shares are sold at the current phase price as determined by the Company's 5-year expansion plan.
                    Payment must be completed in full before share allocation. The Company reserves the right to
                    reject any purchase at its sole discretion.
                  </p>

                  <h4 className="text-white font-semibold">4. Certificate Processing</h4>
                  <div className="bg-yellow-900/20 border border-yellow-500/30 rounded-lg p-3">
                    <p className="text-sm">
                      <strong>Share certificates will be available 10-14 business days after purchase completion,</strong>
                      provided KYC verification has been completed and approved. Certificates are issued by CIPC
                      (Companies and Intellectual Property Commission).
                    </p>
                  </div>

                  <h4 className="text-white font-semibold">5. Rights and Obligations</h4>
                  <p>
                    Shareholders have voting rights in accordance with the Company's Memorandum of Incorporation.
                    Shareholders must comply with all applicable laws and Company policies.
                  </p>

                  <h4 className="text-white font-semibold">6. Risk Acknowledgment</h4>
                  <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-3">
                    <p className="text-sm">
                      <strong>Share purchases involve significant risk.</strong> Mining operations are subject to
                      operational, environmental, regulatory, and market risks. Past performance does not guarantee
                      future results. Shareholders may lose some or all of the amount they paid for their shares.
                    </p>
                  </div>

                  <h4 className="text-white font-semibold">7. Governing Law</h4>
                  <p>
                    This Agreement is governed by South African law and subject to the jurisdiction of
                    South African courts.
                  </p>

                  <p className="text-sm text-white mt-6 border-t border-gray-600 pt-4">
                    <strong>Note:</strong> This is a template for informational purposes. Actual share purchase
                    agreements are generated with specific transaction details upon completion of purchase.
                    For legal questions, contact legal@aureus.africa.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 2FA Email-PIN Verification Modal */}
      {show2FAPinModal && user?.database_user?.email && extractNumericUserId(user) > 0 && (
        <PinVerificationModal
          isOpen={show2FAPinModal}
          onClose={handle2FAPinModalClose}
          onVerified={handle2FAPinVerified as any}
          userId={extractNumericUserId(user)}
          email={user.database_user.email}
          purpose="security_change"
          title="2FA Security Verification"
          description={`Enter the 6-digit code sent to your email to ${pending2FAState ? 'enable' : 'disable'} Two-Factor Authentication.`}
        />
      )}

      {/* Live Shareholder Activity Feed - Hidden on mobile devices */}
      <div className="hidden lg:block">
        <LiveActivityFeed />
      </div>

      </div>
    </MasterAdminViewWrapper>
  )
};



export default UserDashboard
